import React, { useState, useEffect, useRef } from "react";
import { load } from "@cashfreepayments/cashfree-js";
import {
  Filter,
  Ship,
  Calendar,
  Package,
  DollarSign,
  Clock,
  ChevronDown,
  ChevronUp,
  Check,
  ArrowLeft,
  ArrowRight,
  Info,
  Weight,
  X,
  ShieldCheck,
  FileText,
  Files,
} from "lucide-react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
// NOTE: payment_failure (QueriesSlice' `paymentfailed`) is deliberately NOT
// imported — that endpoint is no longer called from anywhere in the app.
import {
  getorderid,
  bookingStartOcean,
} from "../../../store/Queries/QueriesSlice";
import { kybstatus, kycdocstatus } from "../../../store/User/KycSlice";
import { isKybApproved } from "../../../utils/kyb";
import { isQueryLimitReached } from "../../../utils/queryLimit";
import VerifyEmailModal from "../../../components/common/VerifyEmailModal";
import { authuser } from "../../../store/Authentication/AuthSlice";
import { useDispatch, useSelector } from "react-redux";
import { message, Modal } from "antd";
import {
  Tooltip,
  Box,
  Stack,
  Paper,
  Typography,
  Button,
  Chip,
  IconButton,
  Collapse,
  Popover,
  Skeleton,
  Divider,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Select,
  MenuItem,
  RadioGroup,
  Radio,
  FormControlLabel,
  Slider,
  TextField,
} from "@mui/material";
import { payment_verification } from "../../../store/BookingIntermediaterypage/airlineairportslice";
import { updatebookinghistorytabvalue } from "../../../store/Common/CommonSlice";
import {
  createOceanBooking,
  updateOceanBooking,
  createKamOceanBooking,
  setOceanEditQuery,
  clearOceanSearchForm,
  searchratestate,
  searchOceanRate,
} from "../../../store/SearchRates/SearchRatesSlice";
import { get_single_search_query } from "../../../store/Admin/Queries/QuerySlice";
import { newqueries } from "../../../store/Newsearchform/Newqueries";
import { updatequeryid } from "../../../store/Newsearchform/Newsearchform";
import {
  buildOceanResultsState,
  buildOceanSearchQuery,
} from "../../../utils/openQueryResults";
import BookingSuccessModal from "../../../components/common/PaymentSection/BookingSuccessModal";
import axios from "axios";
import { httpinstance } from "../../../axios/api";
import {
  containerLineWeight,
  totalContainerWeight,
} from "../../../utils/oceanContainerWeight";

// Builds { [currencyCode]: inrRate } from the admin/getexchangerates list —
// the single source of truth for every currency conversion in this component.
// A charge's USD/INR figures must always come from this table, never from
// whatever conversion a source API (Freightify, manual quote) already
// attached to it — those use their own internal rates, which drift from ours
// and produce a second, conflicting INR figure for the same charge.
function buildRatesByCurrency(exchangeRateList) {
  const ratesByCurrency = {};
  (exchangeRateList || []).forEach((r) => {
    if (r?.currency) ratesByCurrency[r.currency] = Number(r.inrRate) || 0;
  });
  return ratesByCurrency;
}

// Converts a native-currency amount to USD using the REAL admin-configured
// rate for that specific currency — assuming any non-USD currency was INR
// and dividing by the USD rate directly would treat a EUR charge as if it
// were INR (e.g. "200 EUR" becoming ~$2 instead of ~$182). Convert to INR
// first via that currency's own rate (skip if it's already INR), then to USD.
function convertAmountToUsd(amount, currency, ratesByCurrency) {
  const n = Number(amount) || 0;
  const code = currency || "USD";
  if (code === "USD") return n;
  const usdRate = ratesByCurrency.USD || 0;
  if (usdRate <= 0) return n;
  if (code === "INR") return n / usdRate;
  const rate = ratesByCurrency[code];
  return rate > 0 ? (n * rate) / usdRate : n / usdRate;
}

// Ocean queries with no Freightify data fall back to a manual KAM quote
// (manualQuoteData.oceanResponse.availabilityResponseSOs). This adapts that
// shape into the same { offers, schedules } structure Freightify returns so
// it flows through the existing rendering/filtering/booking pipeline as-is.
// `exchangeRateList` (the raw admin/getexchangerates array, same one this
// component fetches into `exchangeRateList` state) converts the manual
// quote's native-currency amounts into a real amountUsd, same as Freightify's
// own charges already carry — this used to just copy the raw INR number into
// amountUsd (and never set `amount` at all), so the Booking Summary modal's
// "Amount" column showed "-" and "USD Amount" showed the INR figure mislabeled.
function buildManualQuoteOffersShape(availabilityResponseSOs, savedQuery, exchangeRateList) {
  const offers = [];
  const schedules = {};
  const ratesByCurrency = buildRatesByCurrency(exchangeRateList);
  const toUsd = (amount, currency) => convertAmountToUsd(amount, currency, ratesByCurrency);

  (availabilityResponseSOs || []).forEach((so, idx) => {
    const scheduleId = `manual-${idx}`;
    const routing0 = so?.routing?.[0] || {};
    const originCode = routing0.originPort || "";
    const destCode = routing0.destinationPort || "";
    const rd = so?.rateDetails || {};
    // Quote-level fallback only — an individual charge line (e.g. a
    // container rate or a chargesBreakDown item) can be entered in a
    // different currency than the rest of the quote (see BuySellChargesTable's
    // per-row Currency column), so its own `currency` always wins when present.
    const quoteCurrency = rd.currencyCode || "USD";

    let charges = [];
    // rd.charges (when present) is the backend's own already-resolved line
    // items — each carries its real rateCurrency, but its amountUsd (if any)
    // was converted server-side with whatever rate the backend had, not our
    // admin exchange-rate table. Always recompute amountUsd via `toUsd` here
    // so every charge's USD/INR figure traces back to the same rate table,
    // instead of mixing server-converted and admin-converted amounts.
    if (Array.isArray(rd.charges) && rd.charges.length > 0) {
      charges = rd.charges
        .filter((c) => c.rateType === "SELL")
        .map((c) => ({
          rateType: "SELL",
          rateTypeCode: c.rateTypeCode,
          containerType: c.containerType || "General",
          description: c.description || c.aggregatedChargeCode || c.chargeCode,
          qty: c.qty,
          rate: c.rate,
          rateCurrency: c.rateCurrency,
          amount: c.amount,
          amountUsd: toUsd(c.amount, c.rateCurrency),
        }));
    } else {
      (rd.containerRateBreakup || []).forEach((cr) => {
        const rowCurrency = cr.currency || quoteCurrency;
        charges.push({
          rateType: "SELL",
          rateTypeCode: "FREIGHT",
          containerType: cr.containerType,
          description: "Ocean Freight",
          qty: cr.quantity,
          rate: cr.rate,
          rateCurrency: rowCurrency,
          amount: cr.amount,
          amountUsd: toUsd(cr.amount, rowCurrency),
        });
      });
      // No containerRateBreakup fallback here on purpose — rd.freightCharges /
      // discountedtotalAmount / totalAmount are the quote's OVERALL total (every
      // charge combined), not a freight-specific figure. Freight is now just a
      // normal line item in chargesBreakDown (the KAM enters "Ocean Freight" as
      // a regular Buy/Sell charge, same as any other), so synthesizing a
      // separate FREIGHT entry from the whole-quote total double-counted it —
      // FREIGHT Total ended up equal to ORIGIN Total, doubling the Grand Total.
      (rd.chargesBreakDown || []).forEach((c) => {
        if (c.category === "Main") return; // already covered by the freight rows above
        const type = (c.chargeType || "").toLowerCase();
        const legType = type.includes("origin")
          ? "ORIGIN"
          : type.includes("destination")
          ? "DESTINATION"
          : null;
        if (!legType) return;
        const chargeAmount = c.Totalamount ?? c.totalAmount ?? c.chargeAmount ?? 0;
        const rowCurrency = c.chargeCurrency || quoteCurrency;
        charges.push({
          rateType: "SELL",
          rateTypeCode: legType,
          containerType: "General",
          description: c.name || c.chargeDescription || c.chargeType,
          rate: c.ratePerkilo,
          rateCurrency: rowCurrency,
          amount: chargeAmount,
          amountUsd: toUsd(chargeAmount, rowCurrency),
        });
      });
    }

    offers.push({
      productOffer: {
        carrierName: so?.carrierName || routing0.carrierName || "Manual Quote",
        carrierScac:
          routing0.carrierCode ||
          (so?.carrierName || "MQ").replace(/\s+/g, "").slice(0, 4).toUpperCase(),
        originPort: originCode,
        destinationPort: destCode,
      },
      productPrice: {
        cargoType: savedQuery?.commonditytype || "FAK",
        // Marks this offer as a KAM manual quote (vs a Freightify rate).
        // Only a manual quote carries a quote expiry, so the booking
        // modal shows its "Rate valid till" chip for these offers only.
        isManualQuote: true,
        // Rate expiry for the "Rate valid till" chip. A KAM quote can put
        // it on the rate itself or on each container rate row; the query's
        // own quote expiry is the fallback at render time.
        validTo:
          rd.validityDate ||
          rd.validity ||
          so?.validity ||
          (rd.containerRateBreakup || []).find((cr) => cr?.validity)?.validity ||
          "",
        charges,
        routeScheduleIds: [scheduleId],
        transitTimeInDays: so?.transitDays,
        remarks: "",
      },
    });

    schedules[scheduleId] = {
      fromLocation: { unLocCode: originCode, departure: so?.departureDateAndTime },
      toLocation: {
        unLocCode: destCode,
        portName: "",
        arrival: so?.arrivalDateAndTime,
      },
      sailingDate: so?.departureDateAndTime,
      transitTime: so?.transitDays ? `${so.transitDays}d` : "",
      scheduleDetails: [
        {
          fromLocation: { unLocCode: originCode, departure: so?.departureDateAndTime },
          toLocation: { unLocCode: destCode, portName: "", arrival: so?.arrivalDateAndTime },
          transport: {
            vessel: { name: routing0.vesselName || "" },
            voyageNumber: routing0.voyageNumber || "",
          },
        },
      ],
    };
  });

  return { offers, schedules };
}

// ── search_query_data: stored query document + the search we JUST ran ────────
// A re-quote (requestType 2) posts the new search to all-ocean-rates, but the
// `savedQuery` that comes back is the STORED query document, which still
// carries the values from the ORIGINAL booking (container mix, cargo-ready
// date, incoterm, commodity...). Persisting that echo verbatim as
// `search_query_data` on update-ocean-booking would snapshot the updated
// booking against the OLD search — e.g. 1 x 20GP / 13000 kg when the user just
// re-searched 2 x 20GP / 7000 kg.
//
// `state.data` on this page IS the exact payload that was submitted to
// all-ocean-rates (ShipmentRateForm's findflights navigates here with it), so
// overlay it onto the stored document, translated into the document's own
// nested shape (originShipment -> originPort, flat containerDetails[] ->
// containerDetails.containerDetails, commonditytype ->
// goodsDetails.commodity_description). Everything the backend owns (_id,
// userId, status, KAMid, carrierScac, usd_amount, timestamps, ...) is left
// untouched — only the user-searchable fields are refreshed.
const DATE_PARTS = /^(\d{1,2})[-/](\d{1,2})[-/](\d{4})$/;
// Submitted dates are DD-MM-YYYY; the stored document uses DD/MM/YYYY for
// goodsDetails.cargoreadydate and an ISO string for the top-level one.
const toStoredDate = (d) => {
  const m = DATE_PARTS.exec(String(d || "").trim());
  return m ? `${m[1].padStart(2, "0")}/${m[2].padStart(2, "0")}/${m[3]}` : "";
};
const toIsoDate = (d) => {
  const m = DATE_PARTS.exec(String(d || "").trim());
  return m
    ? `${m[3]}-${m[2].padStart(2, "0")}-${m[1].padStart(2, "0")}T00:00:00.000Z`
    : null;
};

function mergeSubmittedSearch(stored, submitted) {
  // Direct/legacy navigation with no submitted query in router state — nothing
  // fresher to merge, so keep the stored document exactly as the backend sent it.
  if (!submitted || typeof submitted !== "object") return stored || {};

  const base = stored || {};
  const os = submitted.originShipment || {};
  const ds = submitted.destinationShipment || {};
  const containers = Array.isArray(submitted.containerDetails)
    ? submitted.containerDetails
    : base.containerDetails?.containerDetails || [];
  const cargoreadydate = toStoredDate(submitted.goodsDetails?.cargoreadydate);
  const isoCargoReadyDate = toIsoDate(submitted.goodsDetails?.cargoreadydate);

  return {
    ...base,
    cargoType: submitted.cargoType || base.cargoType,
    containerLoad: submitted.containerLoad || base.containerLoad,
    originType: submitted.originType || base.originType,
    destinationType: submitted.destinationType || base.destinationType,
    originPort: {
      ...(base.originPort || {}),
      originPort: os.originShipment ?? base.originPort?.originPort ?? "",
      originLocation: os.originLocation ?? "",
      originPickup: os.originPickup ?? false,
      originLocationCountry: os.originLocationCountry ?? "",
    },
    destinationPort: {
      ...(base.destinationPort || {}),
      destinationPort:
        ds.destinationShipment ?? base.destinationPort?.destinationPort ?? "",
      destinationLocation: ds.destinationLocation ?? "",
      destinationPickup: ds.destinationPickup ?? false,
      destinationLocationCountry: ds.destinationLocationCountry ?? "",
    },
    goodsDetails: {
      ...(base.goodsDetails || {}),
      ...(cargoreadydate ? { cargoreadydate } : {}),
      commodity_description:
        submitted.commonditytype || base.goodsDetails?.commodity_description || "",
      stuffing: submitted.stuffing ?? base.goodsDetails?.stuffing ?? "",
    },
    paymentTerms: {
      ...(base.paymentTerms || {}),
      paymentType: submitted.paymentTerms?.paymentType ?? "",
      incoTerms: submitted.paymentTerms?.incoTerms ?? "",
    },
    containerDetails: {
      ...(base.containerDetails || {}),
      containerDetails: containers,
    },
    ...(isoCargoReadyDate ? { cargoreadydate: isoCargoReadyDate } : {}),
    // A blank/absent count on both the submitted query and the stored document
    // means one master B/L, never "none". Counts ride as strings, so compare
    // numerically — a stored "0" is truthy and would survive into the booking
    // snapshot as a zero count.
    Noofbl: [submitted.Noofbl, base.Noofbl].map(Number).find((n) => n > 0) ?? 1,
    currency: submitted.currency || base.currency,
    platform: Array.isArray(submitted.platform) ? submitted.platform : base.platform,
    online: Array.isArray(submitted.online) ? submitted.online : base.online,
  };
}

// ── Booking Summary modal: design tokens + presentational primitives ────────
// Purely visual (colours, spacing, typography). Every number/label the modal
// shows still comes from the same state and offer keys as before.
const BM = {
  ink: "#101828",
  text: "#344054",
  body: "#475467",
  muted: "#667085",
  faint: "#98A2B3",
  line: "#E7EBF0",
  hair: "#EDF0F4",
  hair2: "#F2F4F7",
  rule: "#E3E8EE",
  panel: "#F7F9FB",
  head: "#FAFBFC",
  amber: "#B26A00",
  amberBg: "#FFF4E0",
  amberBd: "#FBE3B8",
  green: "#17B26A",
  greenTx: "#067647",
  greenBg: "#ECFDF3",
  greenBd: "#ABEFC6",
  indigo: "#3538CD",
  indigoBg: "#EFF4FF",
  // The project's amber accent, as used by the chips and the Confirm button.
  accent: "#F5A623",
  accentDeep: "#E08E0B",
};
const BM_MONO = "ui-monospace, SFMono-Regular, Menlo, 'Courier New', monospace";
// For glyphs the mono stack doesn't carry (₹ above all) — falling back per-glyph
// renders them at a different size than the digits beside them.
const BM_SANS =
  "system-ui, -apple-system, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif";

const BmEyebrow = ({ children, color = BM.faint, size = 10.5, ls = ".08em", sx }) => (
  <Typography
    component="span"
    sx={{
      fontSize: size,
      fontWeight: 700,
      letterSpacing: ls,
      textTransform: "uppercase",
      color,
      ...sx,
    }}
  >
    {children}
  </Typography>
);

const BmCard = ({ children, sx }) => (
  <Box
    sx={{
      background: "#FFFFFF",
      border: `1px solid ${BM.line}`,
      borderRadius: "14px",
      ...sx,
    }}
  >
    {children}
  </Box>
);

const BmTag = ({ children, bg, bd, color, sx }) => (
  <Box
    component="span"
    sx={{
      fontSize: 11,
      fontWeight: 700,
      letterSpacing: ".1em",
      textTransform: "uppercase",
      color,
      background: bg,
      border: bd ? `1px solid ${bd}` : "none",
      px: "9px",
      py: "4px",
      borderRadius: "6px",
      whiteSpace: "nowrap",
      ...sx,
    }}
  >
    {children}
  </Box>
);

const BmSeg = ({ active, onClick, children, size = "md", sx, ...rest }) => (
  <Box
    component="button"
    type="button"
    onClick={onClick}
    {...rest}
    sx={{
      px: size === "md" ? "16px" : "15px",
      py: size === "md" ? "8px" : "7px",
      borderRadius: size === "md" ? "8px" : "7px",
      border: "none",
      cursor: "pointer",
      fontFamily: "inherit",
      fontSize: size === "md" ? 13 : 12.5,
      whiteSpace: "nowrap",
      fontWeight: active ? 700 : 600,
      color: active ? BM.ink : BM.muted,
      background: active ? "#FFFFFF" : "transparent",
      boxShadow: active
        ? size === "md"
          ? "0 1px 3px rgba(16,24,40,.12)"
          : "0 1px 2px rgba(16,24,40,.1)"
        : "none",
      transition: "background .15s, color .15s",
      ...sx,
    }}
  >
    {children}
  </Box>
);

const BmIconBadge = ({ bg, size = 28, radius = 8, children }) => (
  <Box
    sx={{
      width: size,
      height: size,
      borderRadius: `${radius}px`,
      background: bg,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      flexShrink: 0,
    }}
  >
    {children}
  </Box>
);

// Renders the small HTML fragments that come back with an offer (a carrier's
// remarks/inclusions through Freightify, or the rich text a KAM types into a
// manual quote) WITHOUT dangerouslySetInnerHTML: the markup is parsed into an
// inert document and rebuilt as React elements, so only the formatting tags
// listed here can ever reach the page — a <script>, an <img onerror>, an
// onclick or a javascript: link simply has nothing to map to.
const BM_TEXT_TAGS = {
  b: "b",
  strong: "strong",
  i: "i",
  em: "em",
  u: "u",
  p: "p",
  div: "div",
  span: "span",
  ul: "ul",
  ol: "ol",
  li: "li",
  small: "small",
  h4: "h4",
  h5: "h5",
  h6: "h6",
  br: "br",
};
// Elements whose text must go with them; everything else that is not in
// BM_TEXT_TAGS is unwrapped, keeping its text.
const BM_DROP_TAGS = new Set([
  "script",
  "style",
  "iframe",
  "object",
  "embed",
  "img",
  "svg",
  "math",
  "form",
  "input",
  "button",
  "select",
  "textarea",
  "link",
  "meta",
  "base",
  "template",
  "noscript",
  "video",
  "audio",
  "source",
  "canvas",
  "title",
]);

const bmNodesToReact = (nodes, keyPrefix) => {
  const out = [];
  Array.from(nodes || []).forEach((node, i) => {
    const key = `${keyPrefix}-${i}`;
    // Text: inserted as a string, never as markup.
    if (node.nodeType === 3) {
      if (node.nodeValue) out.push(node.nodeValue);
      return;
    }
    if (node.nodeType !== 1) return; // comments and the rest are dropped
    const tag = (node.tagName || "").toLowerCase();
    if (BM_DROP_TAGS.has(tag)) return;
    const children = bmNodesToReact(node.childNodes, key);
    const mapped = BM_TEXT_TAGS[tag];
    if (!mapped) {
      // Unknown tag (font, a, table, ...): keep the text, drop the element.
      out.push(...children);
      return;
    }
    // No attributes are carried over — no style, no event handlers, no href.
    out.push(
      mapped === "br"
        ? React.createElement("br", { key })
        : React.createElement(mapped, { key }, children.length ? children : null)
    );
  });
  return out;
};

const BmRichText = ({ html }) => {
  const nodes = React.useMemo(() => {
    if (!html || typeof html !== "string") return null;
    if (typeof window === "undefined" || typeof window.DOMParser !== "function") {
      // No parser available: show the text, stripped of its tags, rather than
      // nothing at all.
      return html.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
    }
    try {
      // "text/html" parsing is inert — no script runs and no resource loads.
      const doc = new window.DOMParser().parseFromString(html, "text/html");
      return bmNodesToReact(doc.body?.childNodes, "rt");
    } catch (e) {
      return "";
    }
  }, [html]);
  return nodes ? <>{nodes}</> : null;
};

// ── Remarks & inclusions: HTML blob → structured groups ─────────────────────
// The offer's `remarks` / `inclusionRemarks` arrive as markup the rate source
// built — "<strong>Ocean Freight</strong>:" followed by a <ul> of lines, or a
// comma-separated run of text. These split that into { heading, items } so the
// panel can lay the content out itself (amber fee pills for the lines that
// carry an amount, plain bullets for the rest, green pills for inclusions)
// instead of dumping the source's own markup on screen.
//
// A line reads as a fee when it ends in an amount with a currency code, either
// order: "SPOTON Cancellation Fee = 50.00 USD" or "... = USD 50.00".
const BM_FEE_RE =
  /^(.*?)\s*[=:]\s*(?:([A-Z]{3})\s*)?(\d[\d,]*(?:\.\d+)?)\s*([A-Z]{3})?\s*$/;

const bmParseRemarkItem = (text) => {
  const line = String(text || "").replace(/\s+/g, " ").trim();
  if (!line) return null;
  const m = line.match(BM_FEE_RE);
  const currency = m && (m[2] || m[4]);
  if (m && currency && m[1]) {
    return { label: m[1].trim(), amount: m[3], currency };
  }
  return { label: line };
};

const bmParseRemarkGroups = (html) => {
  if (!html || typeof html !== "string") return [];
  if (typeof window === "undefined" || typeof window.DOMParser !== "function") {
    return [];
  }
  let body;
  try {
    // "text/html" parsing is inert — no script runs and no resource loads.
    body = new window.DOMParser().parseFromString(html, "text/html").body;
  } catch (e) {
    return [];
  }
  if (!body) return [];

  const groups = [];
  let current = null;
  const open = (heading) => {
    current = { heading: heading || "", items: [] };
    groups.push(current);
    return current;
  };
  const addText = (text) => {
    const raw = String(text || "").replace(/^[\s:;·\-–—]+/, "");
    if (!raw.trim()) return;
    // Only an inclusions-style run is comma-separated; a line carrying an
    // amount is one item, so its thousands separator must not split it.
    // Even then, split only on a comma followed by a letter.
    const parts = /[=]/.test(raw) ? [raw] : raw.split(/,(?=\s*[A-Za-z])/);
    parts.forEach((part) => {
      const item = bmParseRemarkItem(part);
      if (item) (current || open("")).items.push(item);
    });
  };

  const walk = (nodes) => {
    Array.prototype.forEach.call(nodes || [], (node) => {
      if (node.nodeType === 3) return addText(node.textContent);
      if (node.nodeType !== 1) return;
      const tag = String(node.tagName || "").toLowerCase();
      if (tag === "strong" || tag === "b" || /^h[1-6]$/.test(tag)) {
        return void open(String(node.textContent || "").replace(/[:\s]+$/, ""));
      }
      if (tag === "li") return addText(node.textContent);
      if (tag === "br" || tag === "script" || tag === "style") return;
      walk(node.childNodes);
    });
  };
  walk(body.childNodes);

  return groups.filter((g) => g.items.length > 0);
};

// One remark line: an amber dot and the text.
const BmRemarkLine = ({ children }) => (
  <Stack direction="row" alignItems="flex-start" gap={1.125}>
    <Box
      sx={{
        width: 5,
        height: 5,
        borderRadius: "50%",
        background: BM.accent,
        mt: "7px",
        flexShrink: 0,
      }}
    />
    <Typography sx={{ fontSize: 13, color: BM.body, lineHeight: 1.55 }}>
      {children}
    </Typography>
  </Stack>
);

// Remarks panel body — plain lines as bullets, priced lines as amber pills
// under their own "Conditional fees" eyebrow.
const BmRemarkGroups = ({ groups, containerLabel }) => (
  <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
    {groups.map((group, gi) => {
      const lines = group.items.filter((i) => !i.currency);
      const fees = group.items.filter((i) => i.currency);
      return (
        <Box
          key={`${group.heading}-${gi}`}
          sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}
        >
          {groups.length > 1 && group.heading && (
            <BmEyebrow size={11} ls=".07em" color={BM.amber}>
              {group.heading}
            </BmEyebrow>
          )}
          {lines.length > 0 && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              {lines.map((line, i) => (
                <BmRemarkLine key={i}>{line.label}</BmRemarkLine>
              ))}
            </Box>
          )}
          {fees.length > 0 && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              {lines.length > 0 && (
                <Box sx={{ borderTop: `1px dashed ${BM.rule}`, mt: 0.5 }} />
              )}
              <BmEyebrow size={10.5} ls=".08em">
                Conditional fees{containerLabel ? ` · ${containerLabel}` : ""}
              </BmEyebrow>
              {fees.map((fee, i) => (
                <Stack
                  key={i}
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  gap={1.5}
                  sx={{
                    px: "12px",
                    py: "10px",
                    borderRadius: "10px",
                    background: BM.amberBg,
                    border: `1px solid ${BM.amberBd}`,
                  }}
                >
                  <Typography
                    sx={{ fontSize: 13, fontWeight: 600, color: BM.text }}
                  >
                    {fee.label}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: 12.5,
                      fontWeight: 700,
                      color: BM.accentDeep,
                      fontFamily: BM_MONO,
                      whiteSpace: "nowrap",
                    }}
                  >
                    {fee.currency} {fee.amount}
                  </Typography>
                </Stack>
              ))}
            </Box>
          )}
        </Box>
      );
    })}
  </Box>
);

// Inclusions panel body — one green pill per item, plus the note that these
// are already priced into the freight line.
const BmInclusionGroups = ({ groups }) => (
  <Box sx={{ display: "flex", flexDirection: "column", gap: 1.75 }}>
    {groups.map((group, gi) => (
      <Box
        key={`${group.heading}-${gi}`}
        sx={{ display: "flex", flexDirection: "column", gap: 1 }}
      >
        {groups.length > 1 && group.heading && (
          <BmEyebrow size={11} ls=".07em" color={BM.greenTx}>
            {group.heading}
          </BmEyebrow>
        )}
        {group.items.map((item, i) => (
          <Stack
            key={i}
            direction="row"
            alignItems="center"
            gap={1}
            sx={{
              px: "12px",
              py: "10px",
              borderRadius: "10px",
              background: BM.greenBg,
              border: `1px solid ${BM.greenBd}`,
            }}
          >
            <Check size={14} color={BM.greenTx} style={{ flexShrink: 0 }} />
            <Typography sx={{ fontSize: 13, fontWeight: 600, color: BM.text }}>
              {item.label}
              {item.currency ? ` — ${item.currency} ${item.amount}` : ""}
            </Typography>
          </Stack>
        ))}
      </Box>
    ))}
    <Typography sx={{ fontSize: 12, color: BM.faint, lineHeight: 1.55 }}>
      Charges above are already built into the ocean freight line and will not be
      invoiced separately.
    </Typography>
  </Box>
);

const bmTh = {
  fontSize: 10.5,
  fontWeight: 700,
  letterSpacing: ".07em",
  textTransform: "uppercase",
  color: BM.faint,
  background: BM.head,
  borderBottom: `1px solid ${BM.hair}`,
  py: "10px",
  lineHeight: 1.4,
  whiteSpace: "nowrap",
};

// Finds the rate/quote expiry to print in the "Rate valid till" chip. The
// field is named differently by each source and does not always sit at the
// same level — `expiryTime` on a manual-quoted query (same field the Admin
// Queries page shows as "Quote Expiry"), `validTo` on a Freightify offer,
// `queryExpiryDate` / `quoteExpiry` on a saved query — so rather than betting
// on one path, this scans the objects it is handed (and their nested objects,
// two levels deep) for the first of those keys holding a non-empty string.
// Arrays are skipped on purpose: a per-charge `validTo` is not the rate's.
const BM_EXPIRY_KEYS = [
  "expiryTime",
  "expiry_time",
  "expiryDate",
  "quoteExpiry",
  "quoteExpiryDate",
  "queryExpiryDate",
  "validTo",
  "validTill",
  "validityDate",
];
const bmFindExpiry = (roots) => {
  const seen = new Set();
  const walk = (obj, depth) => {
    if (!obj || typeof obj !== "object" || Array.isArray(obj)) return "";
    if (depth > 2 || seen.has(obj)) return "";
    seen.add(obj);
    for (const key of BM_EXPIRY_KEYS) {
      const val = obj[key];
      if (typeof val === "string" && val.trim()) return val;
    }
    for (const key of Object.keys(obj)) {
      const found = walk(obj[key], depth + 1);
      if (found) return found;
    }
    return "";
  };
  for (const root of roots || []) {
    const found = walk(root, 0);
    if (found) return found;
  }
  return "";
};

// Rate-validity chip text. The expiry date reaches us in whatever shape its
// source used: an ISO timestamp (Freightify meta.validTo), or DD/MM/YYYY /
// DD-MM-YYYY (a KAM's quote expiry on the saved query). Returns "" when there
// is no usable date, so the chip is simply not rendered.
const bmFormatValidTill = (raw) => {
  const s = String(raw || "").trim();
  if (!s) return "";
  const dmy = /^(\d{1,2})[-/](\d{1,2})[-/](\d{4})$/.exec(s);
  const d = dmy
    ? new Date(Number(dmy[3]), Number(dmy[2]) - 1, Number(dmy[1]))
    : new Date(s);
  if (isNaN(d.getTime())) return s;
  // Fixed month names rather than toLocaleDateString: en-GB renders
  // September as "Sept", which reads oddly once the chip uppercases it.
  const MONTHS = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];
  return `${String(d.getDate()).padStart(2, "0")} ${
    MONTHS[d.getMonth()]
  } ${d.getFullYear()}`;
};

// Leg key (the persisted rateTypeCode) -> the card title shown above its table.
const BM_LEG_TITLE = {
  ORIGIN: "Origin charges",
  FREIGHT: "Freight charges",
  DESTINATION: "Destination charges",
};

const OceanMainRateCom = () => {
  const { state } = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Opened from the Queries list on a "Quote Available" (Manual Quoted) ocean
  // row, which flags the URL with ?manualQuote=true (see Queries.jsx
  // handleSeachFormdata) — exactly like the air results page. Used only by the
  // "Edit Query" back button, which then returns to the Queries list this
  // search came from instead of the standalone /search-rates form.
  const isFromQueriesManualQuote = searchParams.get("manualQuote") === "true";

  // Deep link from the "Your Freight Quotation is Ready" email:
  // `/search-rates/ocean-rates-result?queryId=<id>`. Every other way into this
  // page is a PUSH — Queries.jsx / ShipmentRateForm dispatch the search and
  // hand over `state.data` in the same breath — so arriving with only a URL
  // leaves the page with nothing to render. When that happens we fetch the
  // saved query and replay exactly what those callers do (see the effect
  // below), instead of teaching the rest of this file a second data source.
  // Compared against the query already loaded (rather than just "is state
  // empty") so following a link for a different quotation can't leave the
  // previous query's rates on screen.
  const deepLinkQueryId = searchParams.get("queryId");
  const needsBootstrap =
    !!deepLinkQueryId && String(state?.data?.queryId || "") !== String(deepLinkQueryId);
  const [bootstrapping, setBootstrapping] = useState(needsBootstrap);

  console.log("state", state);

  const [ratesData, setRatesData] = useState(null);
  const dispatch = useDispatch();

  // In-flight results-page requests. `abortRef` cancels the axios lookups, and
  // is aborted on Edit Query and on unmount so no state lands on an unmounted
  // component and no request keeps running after we leave.
  const abortRef = useRef(null);

  const abortPageRequests = () => {
    if (abortRef.current) {
      try {
        abortRef.current.abort();
      } catch (e) {
        /* already aborted */
      }
      abortRef.current = null;
    }
  };

  // Same KYC gate as the Air rate-results page (Newratelisting.jsx) — the
  // ribbon in PageLayout already surfaces the "complete your KYC" message
  // globally, this just blocks the actual booking action on that same signal.
  // `kybStatus` is the logged-in account's OWN business verification (KYB)
  // status — only "approved" books (src/utils/kyb.js). It's meaningless for a
  // KAM (they have no KYB of their own). KYB isn't required for a KAM at all —
  // neither their own nor the customer's — so a KAM always passes this check.
  const kybStatus = useSelector(kybstatus);
  const loginedUser = useSelector(authuser);
  const dataratelist=useSelector(searchratestate)
  console.log(dataratelist?.oceanRates?.data?.savedQuery?._id,"dataratelist")
  let kycAccepted = isKybApproved(kybStatus);
  if (loginedUser?.userroles === "kam") {
    kycAccepted = true;
  }
  // /booking-history is customer-only (routes.js role list) — a KAM/admin
  // session lands on a blank page there (same issue fixed in
  // OceanCargoDetails.jsx's back button). Send the post-booking success
  // redirect to their own queries list instead, same as
  // OceanCargoDetails.jsx's Bookingintermediatrypage redirectPath.
  const isKamOrAdmin =
    loginedUser?.userroles === "kam" || loginedUser?.userroles === "air_admin";
  // Stricter than isKamOrAdmin: the dedicated KAM ocean-booking endpoints are
  // gated server-side to the "kam" role only (checkKam(["kam"])), so
  // air_admin must keep using the legacy Cashfree + createOceanBooking path.
  const isKamRole = loginedUser?.userroles === "kam";

  // This page can be reached directly from the ocean search form without ever
  // mounting a page that fetches this status, leaving it stuck at the empty
  // initialState (and kycAccepted falsely false) even for verified users.
  useEffect(() => {
    dispatch(kycdocstatus());
  }, [dispatch]);

  // Email deep-link bootstrap (see `needsBootstrap` above). Fetches the saved
  // query, then replays exactly what Queries.jsx does when it opens an ocean
  // query: seed the query owner (KYC gate) + query id, fire the rate search,
  // and `replace` this history entry with the router state the rest of the
  // page reads. Re-entering the SAME route means every existing `state?.data`
  // read below keeps working untouched — nothing else in this file has to know
  // the page was opened from a link.
  const bootstrappedIdRef = useRef(null);

  // Unverified-user search gate (utils/queryLimit), shared with air — the
  // backend counts air and ocean searches together. The other routes here
  // (search form, Queries list) gate before navigating; only this deep link
  // fires its search from this page, so it is gated here. Waits for
  // PageLayout's /user-details call (same check as Newratelisting) so the gate
  // never runs against a missing profile.
  const gateUser = useSelector((s) => s.data?.data?.data?.user);
  const profileReady = useSelector(
    (s) =>
      Boolean(s.data?.data?.data?.user) ||
      (!s.data?.loading && (s.data?.error != null || !Array.isArray(s.data?.data)))
  );
  const [showVerifyModal, setShowVerifyModal] = useState(false);
  const pendingSearchRef = useRef(null);

  useEffect(() => {
    if (!needsBootstrap || !profileReady) return;
    // StrictMode double-invoke / re-render guard: fetch + search once per id.
    if (bootstrappedIdRef.current === deepLinkQueryId) return;
    bootstrappedIdRef.current = deepLinkQueryId;
    setBootstrapping(true);
    let active = true;
    dispatch(get_single_search_query(deepLinkQueryId))
      .then((res) => {
        if (!active) return;
        const full = res?.payload?.data?.nodeData;
        if (!full) {
          // Unknown or inaccessible id — drop the spinner and let the normal
          // empty state show instead of hanging on a skeleton forever.
          setBootstrapping(false);
          message.error(
            "We couldn't open this quotation. Please open it from the Queries page."
          );
          return;
        }
        // The mail only goes out for a quotation that is already prepared, so
        // re-quote as manual-only (see buildOceanSearchQuery): the customer sees
        // the figures they were mailed, not a fresh Freightify run that may have
        // moved since — and the response comes straight from the saved quote
        // instead of waiting on the provider. Sent unconditionally rather than
        // gated on `full.Quote_status === "Manual Quoted"` (this projection does
        // not always carry Quote_status, so gating on it silently never fired);
        // the endpoint only honours the flag when a manual quote actually
        // exists, so a link for a query without one still returns live rates.
        const oceanSearchquery = buildOceanSearchQuery(full, {
          manualQuoteOnly: true,
        });
        const runSearch = () => {
          dispatch(newqueries(full));
          dispatch(updatequeryid(full?._id || deepLinkQueryId));
          dispatch(searchOceanRate(oceanSearchquery));
          // Keep ?queryId= so a browser refresh re-bootstraps instead of landing
          // on a blank page, and set manualQuote=true so "Edit Query" returns to
          // the Queries list — the only sensible destination for a customer who
          // arrived here from an email rather than from the search form.
          navigate(
            `/search-rates/ocean-rates-result?queryId=${encodeURIComponent(
              deepLinkQueryId
            )}&manualQuote=true`,
            { replace: true, state: buildOceanResultsState(oceanSearchquery) }
          );
          setBootstrapping(false);
        };
        return isQueryLimitReached(gateUser).then((blocked) => {
          if (!active) return;
          if (!blocked) return runSearch();
          // Held until the email is verified; the skeleton stays behind the modal.
          pendingSearchRef.current = runSearch;
          setShowVerifyModal(true);
        });
      })
      .catch(() => {
        if (active) setBootstrapping(false);
      });
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [needsBootstrap, deepLinkQueryId, profileReady]);

  // Ocean search result + its own loading flag come from redux — the form
  // navigates here on Search click (before data arrives) and we render a
  // skeleton until `oceanRates` lands.
  const { oceanRates, oceanLoading } = useSelector(searchratestate);

  const [allOffers, setAllOffers] = useState([]);
  const [dateGroups, setDateGroups] = useState({});
  const [currentDateKey, setCurrentDateKey] = useState(null);
  const [filters, setFilters] = useState({
    carrier: "",
    cargoType: "",
    containerType: "",
    maxPrice: "",
    priceRange: null,
  });
  const [filterOptions, setFilterOptions] = useState({
    carriers: [],
    cargoTypes: [],
    containerTypes: [],
  });
  const [expandedRows, setExpandedRows] = useState({});
  const [showModal, setShowModal] = useState(false);
  const [currentBooking, setCurrentBooking] = useState(null);
  const [agreeTerms, setAgreeTerms] = useState(false);
  // Booking Summary modal: which detail tab is open, which sailing is
  // selected in the Schedules tab (null = the first one, which is always
  // selected and cannot be cleared), and which side the Free days tab
  // shows ("Export" = Origin, "Import" = Destination, per dndConditions).
  const [summaryTab, setSummaryTab] = useState("schedule");
  const [selectedScheduleIdx, setSelectedScheduleIdx] = useState(null);
  const [freeDaysDirection, setFreeDaysDirection] = useState("Export");
  const [, setError] = useState("");
  const [stats, setStats] = useState({ filtered: 0, total: 0 });
  const [cashfree, setCashfree] = useState(null);
  const [, setResponseMsg] = useState({});
  const [, setBookingData] = useState({
    totalFreightRate: 0,
  });
  const [showBookingSuccess, setShowBookingSuccess] = useState(false);
  // Which copy the success modal shows — a KAM's direct-create booking has no
  // payment collected and lands on "Confirmation Awaited", so it must not say
  // "Booking Confirmed" the way a paid customer/KAM-update booking does.
  const [bookingSuccessKind, setBookingSuccessKind] = useState("confirmed");
  // Guards against a double-click on "Confirm Booking" firing a second,
  // overlapping confirmBooking() run — without this, a second click before
  // the first attempt's booking_start_ocean/create_order round-trip settles
  // starts its own booking_start_ocean call regardless of how the first
  // attempt's create_order call turns out.
  const [isBookingInFlight, setIsBookingInFlight] = useState(false);

  const [grandTotal, setGrandTotal] = useState(0);
  const [, setBookingRate] = useState("");
  // Per-leg charge rows (same figures as `bookingRate`'s HTML table) that the
  // Booking Summary modal renders as a real MUI table. Display only.
  const [chargeBlocks, setChargeBlocks] = useState([]);
  // Per-leg (ORIGIN/FREIGHT/DESTINATION) SELL totals with markup, in USD, for
  // the offer currently in the booking modal — captured from
  // createChargesBreakdown so confirmBooking can persist the leg-wise split.
  // INR per leg is derived from these at confirm time (usd × live FX rate) to
  // stay consistent with the grand total's INR conversion.
  const [chargeSections, setChargeSections] = useState(null);
  // Taxable/GST/Net split for the offer currently in the booking modal —
  // captured from createChargesBreakdown at the same time as chargeSections
  // so the Amount Summary card can render without recomputing GST.
  const [gstSummary, setGstSummary] = useState(null);
  const [exchangeRateList, setExchangeRateList] = useState([]);
  // INR per 1 USD — same rate confirmBooking uses to convert the payable
  // total. Also used to convert manual-quote charges (always quoted in INR,
  // see buildManualQuoteOffersShape) into a real USD figure instead of just
  // echoing the INR number into the "USD Amount" column.
  const usdInrRate = Number(exchangeRateList?.find((item) => item?.currency === "USD")?.inrRate) || 0;

  console.log("userData", exchangeRateList);

  //   const processFile = (file) => {
  //     if (file.type !== "application/json") {
  //       setError("Please upload a valid JSON file");
  //       return;
  //     }

  //     const reader = new FileReader();
  //     reader.onload = (e) => {
  //       try {
  //         const data = JSON.parse(e.target.result);
  //         setRatesData(data);
  //         prepareFilters(data);
  //         displayRates(data);
  //         setError("");
  //       } catch (err) {
  //         setError("Invalid JSON format: " + err.message);
  //       }
  //     };
  //     reader.readAsText(file);
  //   };

  const prepareFilters = (data) => {
    const carriers = new Set();
    const cargoTypes = new Set();
    const containerTypes = new Set();

    data.offers.forEach((offer) => {
      carriers.add(offer.productOffer.carrierName);
      if (offer.productPrice.cargoType) {
        cargoTypes.add(offer.productPrice.cargoType);
      }
      offer.productPrice.charges.forEach((charge) => {
        if (charge.containerType) {
          containerTypes.add(charge.containerType);
        }
      });
    });

    setFilterOptions({
      carriers: Array.from(carriers),
      cargoTypes: Array.from(cargoTypes),
      containerTypes: Array.from(containerTypes),
    });
  };

  const displayRates = (data) => {
    const offers = [];

    data.offers.forEach((offer) => {
      const scheduleIds = offer.productPrice.routeScheduleIds || [];
      scheduleIds.forEach((scheduleId) => {
        const schedule = data.schedules[scheduleId];
        if (schedule) {
          offers.push({ offer, schedule, scheduleId });
        }
      });
    });

    setAllOffers(offers);
    groupByDate(offers);
    setStats({ filtered: offers.length, total: offers.length });
  };

  const groupByDate = (items) => {
    const groups = {};
    items.forEach((item) => {
      const depStr =
        item.schedule?.fromLocation?.departure || item.schedule?.sailingDate;
      const d = depStr ? new Date(depStr) : null;
      const valid = d && !isNaN(d.getTime());
      // Some offers (e.g. ONEY) come back with no sailing/departure date.
      // Group them under a stable "unknown" key instead of crashing on
      // new Date("").toISOString() — which would blow up the whole listing.
      const dateKey = valid ? d.toISOString().split("T")[0] : "unknown";

      if (!groups[dateKey]) {
        groups[dateKey] = {
          date: valid ? d : null,
          items: [],
        };
      }
      groups[dateKey].items.push(item);
    });

    const sortedDates = Object.keys(groups).sort();
    setDateGroups(groups);
    // Keep the currently-selected date if it still has results after a filter
    // change; otherwise fall back to the first available date (or none).
    setCurrentDateKey((prev) =>
      prev && groups[prev] ? prev : sortedDates[0] ?? null
    );
  };

  const applyFilters = () => {
    let filtered = allOffers.filter((item) => {
      const offer = item.offer;

      if (filters.carrier && offer.productOffer.carrierName !== filters.carrier)
        return false;
      if (
        filters.cargoType &&
        offer.productPrice.cargoType !== filters.cargoType
      )
        return false;
      const t = chargesTotal(offer.productPrice.charges);
      if (filters.maxPrice && t > parseFloat(filters.maxPrice)) return false;
      if (
        filters.priceRange &&
        (t < filters.priceRange[0] || t > filters.priceRange[1])
      )
        return false;
      if (filters.containerType) {
        const hasContainer = offer.productPrice.charges.some(
          (c) => c.containerType === filters.containerType
        );
        if (!hasContainer) return false;
      }

      return true;
    });

    groupByDate(filtered);
    setStats({ filtered: filtered.length, total: allOffers.length });
  };

  // Reset the sidebar filters back to their initial state and restore the full,
  // unfiltered listing (same grouping/stats as displayRates on first load).
  const resetFilters = () => {
    setFilters({
      carrier: "",
      cargoType: "",
      containerType: "",
      maxPrice: "",
      priceRange: null,
    });
    groupByDate(allOffers);
    setStats({ filtered: allOffers.length, total: allOffers.length });
  };

  const toggleDetails = (index) => {
    setExpandedRows((prev) => ({
      ...prev,
      [index]: !prev[index],
    }));
  };

  const openBookingModal = (item) => {
    if (!kycAccepted) return;
    setCurrentBooking(item);
    const x = createChargesBreakdown(item.offer.productPrice.charges);
    console.log("x", x);
    setBookingRate(x?.html);
    setChargeBlocks(x?.legBlocks || []);
    // grandTotal is the GST-inclusive Net (payable) amount — it flows
    // straight into newUsd, the Cashfree charge, the persisted usd_amount,
    // and the update-flow difference calc, so every one of those becomes
    // GST-inclusive automatically without further changes at those sites.
    setGrandTotal(x?.netAmount);
    setChargeSections(x?.sections || null);
    setGstSummary(
      x
        ? {
            taxableAmount: x.taxableAmount,
            gstAmount: x.gstAmount,
            netAmount: x.netAmount,
            gstBySection: x.gstBySection,
          }
        : null
    );

    setShowModal(true);
    setAgreeTerms(false);
    setSummaryTab("schedule");
    setSelectedScheduleIdx(null);
    setFreeDaysDirection("Export");

    setBookingData((prev) => ({
      ...prev,
      totalFreightRate: Number(x?.netAmount || 0),
    }));
  };

  const closeBookingModal = () => {
    setShowModal(false);
    setCurrentBooking(null);
    setBookingRate(null);
    setChargeBlocks([]);
    setGrandTotal(null);
    setChargeSections(null);
    setGstSummary(null);
    setAgreeTerms(false);
    setSummaryTab("schedule");
    setSelectedScheduleIdx(null);
    setBookingData((prev) => ({
      ...prev,
      totalFreightRate: Number(0),
    }));
  };


  // Esc closes the Booking Summary. The modal is a plain overlay (not a MUI
  // Dialog), so nothing gives it that for free — and a keyboard user needs a
  // way out that is not "find the X".
  useEffect(() => {
    if (!showModal) return undefined;
    const onKeyDown = (e) => {
      if (e.key === "Escape") closeBookingModal();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showModal]);
  // ── Ocean update-flow detection (shared by confirmBooking + booking modal) ──
  // Driven entirely by `savedQuery` from the all-ocean-rates response (router
  // state first, redux fallback). `savedQuery.requestType === 2` means a
  // re-quote of an ALREADY-BOOKED query -> UPDATE flow; anything else is a
  // normal full-payment NEW booking. In the update flow `savedQuery.usd_amount`
  // (same level) is the prior paid USD total, and:
  //   • new total <= previously paid  -> no payment, adjust from prior payment
  //   • new total >  previously paid  -> pay ONLY the difference
  // Compared in USD (grandTotal is USD) to avoid FX drift; a 1-cent tolerance
  // absorbs floating-point noise around equality.
  const savedQuery =
    state?.savedQuery || dataratelist?.oceanRates?.data?.savedQuery || {};
  // What gets persisted as `search_query_data`. `savedQuery` above stays the
  // raw backend echo — the update-flow amounts (usd_amount, carrierScac/Name)
  // are read from it and MUST remain the previously-booked values. Only the
  // snapshot we send back is refreshed with the search the user just ran.
  const searchQuerySnapshot = mergeSubmittedSearch(savedQuery, state?.data);
  const queryId =
    savedQuery?._id ||
    dataratelist?.oceanRates?.data?.savedQuery?._id ||
    null;

  // Where BookingSuccessModal sends the user once the booking lands. EVERY
  // role goes to the booking they just made rather than a list — the same
  // standalone page the Queries eye opens (handleOceanBookingView) and the one
  // the customer's own Bookings list links to (OceanCargoList).
  //
  // Only KAM/admin get `from=queries&cargoTab=2`: those tell the page's back
  // button to return to /QueriesKAM's Ocean tab and keep the top nav lit on
  // Queries (see FROM_MENU_PATHS in layouts/Header.jsx). A customer didn't come
  // from Queries — they came from this search — so they're left off and the
  // back button walks the history stack as it does from any other entry point.
  //
  // No queryId means there's no booking page to open, so each role falls back
  // to its own list. It has to be per-role: /booking-history is customer-only
  // (routes.js), and a KAM/admin session lands on a blank page there.
  const oceanBookingSuccessRedirect = queryId
    ? `/booking-history/ocean-cargo-booking-details?queryId=${queryId}${
        isKamOrAdmin ? "&from=queries&cargoTab=2" : ""
      }`
    : isKamOrAdmin
    ? "/QueriesKAM?cargoTab=2"
    : "/booking-history";
  // Whether this is a booking UPDATE — which makes booking_start_ocean,
  // payment-verification and create/update-ocean-booking send requestType "2".
  // Driven purely by the all-ocean-rates response: an update is one where the
  // response echoes a previously-booked carrier, i.e. BOTH `carrierScac` and
  // `carrierName` on savedQuery are non-empty. Empty carrier fields => normal
  // new booking (requestType "1").
  const isUpdateFlow =
    !!String(savedQuery?.carrierScac ?? "").trim() &&
    !!String(savedQuery?.carrierName ?? "").trim();
  const prevUsd = Number(savedQuery?.usd_amount) || 0;
  const newUsd = Number(grandTotal) || 0;
  const differenceUsd = Math.max(0, newUsd - prevUsd);
  const noExtraPayment = isUpdateFlow && newUsd <= prevUsd + 0.01;
  // Carrier of the ALREADY-BOOKED offer (update flow) — `carrierScac` /
  // `carrierName` come back on savedQuery alongside usd_amount/requestType.
  // Used to warn when the user picks a different shipping line during an update.
  const prevCarrierScac = savedQuery?.carrierScac || "";
  const prevCarrierName = savedQuery?.carrierName || "";

  // "Book Now" click. During an update (requestType 2), warn if the chosen
  // shipping line differs from the one already booked — compare by the stable
  // SCAC code, falling back to the display name. Confirm -> open booking modal.
  const handleBookClick = (item) => {
    const offer = item?.offer?.productOffer || {};
    const selScac = offer?.carrierScac || "";
    const selName = offer?.carrierName || "";
    const changedCarrier =
      prevCarrierScac && selScac
        ? selScac !== prevCarrierScac
        : !!prevCarrierName && !!selName && selName !== prevCarrierName;

    if (isUpdateFlow && changedCarrier) {
      Modal.confirm({
        title: "Change shipping line?",
        centered: true,
        content: `You are changing the shipping line${
          prevCarrierName ? ` from "${prevCarrierName}"` : ""
        }${selName ? ` to "${selName}"` : ""}. Do you want to continue?`,
        okText: "Yes, continue",
        cancelText: "No",
        // Explicit colors: the app's global button CSS blanks antd's default
        // primary background, leaving white text invisible on the OK button.
        okButtonProps: {
          style: {
            backgroundColor: "#F5A623",
            borderColor: "#F5A623",
            color: "#fff",
          },
        },
        onOk: () => openBookingModal(item),
      });
      return;
    }
    openBookingModal(item);
  };

  const confirmBooking = async () => {
    if (isBookingInFlight) return;

    // The offer actually being booked, captured ONCE up-front. `offer`,
    // `schedule` and `scheduleId` are assembled together in displayRates (the
    // schedule is looked up by the offer's OWN routeScheduleIds), so this trio
    // is always internally consistent — the persisted ocean_schedule can never
    // belong to a different offer than ocean_offer.
    const booking = currentBooking;

    // An update MUST NOT overwrite a live booking's offer/schedule/amount with
    // placeholder values. The `?.offer || {}` / `?.scheduleId || ""` /
    // `Number(grandTotal) || 0` fallbacks would otherwise persist an empty
    // offer, an empty schedule id or a 0 total without a word if the modal
    // state or the charge breakdown were ever missing. Abort loudly instead —
    // the user can reopen the offer and retry against fresh data.
    if (!booking?.offer || !booking?.scheduleId || newUsd <= 0) {
      message.error(
        "Selected rate is no longer available. Please reopen the offer and try again."
      );
      return;
    }

    setIsBookingInFlight(true);

    const inrRate = usdInrRate;

    // New INR total = the new USD grand total converted at the USD→INR rate.
    // This is `total_amount` (New INR total) on the booking/update endpoints —
    // distinct from `online_amount` (what's actually charged NOW: the full
    // total for a new booking, or only the difference for a paid update).
    const newTotalInr = Number(newUsd * inrRate);

    // ── Leg-wise ORIGIN/FREIGHT/DESTINATION amounts (USD + INR) ──────────────
    // These mirror the section totals shown in the Booking Summary modal: the
    // markup-applied USD per leg (from createChargesBreakdown, stashed in
    // chargeSections when the modal opened) and its INR equivalent, converted
    // here at the same live rate the grand total uses so the parts always sum
    // to usd_amount / total_amount. round2 keeps them as clean 2-dp money and
    // coerces null/undefined/NaN (missing leg, breakdown not captured, or FX
    // rate not yet loaded → 0) so the payload never carries a bad number.
    const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;
    const originUsd = round2(chargeSections?.ORIGIN?.usd);
    const freightUsd = round2(chargeSections?.FREIGHT?.usd);
    const destinationUsd = round2(chargeSections?.DESTINATION?.usd);

    // GST per leg — read straight from gstSummary.gstBySection, which
    // createChargesBreakdown already computed PER CHARGE (5% on the one
    // Basic Ocean Freight line, 18% on every other charge, including
    // sibling surcharges Freightify groups under the same FREIGHT
    // rateTypeCode). Do NOT recompute here by multiplying a flat rate
    // against the whole leg total — that would incorrectly tax fuel/ISPS/
    // other FREIGHT-leg surcharges at 5% instead of 18%.
    const freightGstUsd = round2(gstSummary?.gstBySection?.FREIGHT?.usd);
    const originGstUsd = round2(gstSummary?.gstBySection?.ORIGIN?.usd);
    const destinationGstUsd = round2(gstSummary?.gstBySection?.DESTINATION?.usd);
    const taxableUsd = round2(gstSummary?.taxableAmount);
    const totalGstUsd = round2(gstSummary?.gstAmount);

    const chargeAmounts = {
      // Rate used for every USD→INR figure in this booking, so the record is
      // self-describing even if admin rates change later.
      usd_inr_rate: round2(inrRate),
      origin_usd_amount: originUsd,
      origin_inr_amount: round2(originUsd * inrRate),
      freight_usd_amount: freightUsd,
      freight_inr_amount: round2(freightUsd * inrRate),
      destination_usd_amount: destinationUsd,
      destination_inr_amount: round2(destinationUsd * inrRate),
      // No fixed "percentage" field for FREIGHT — its GST can be a mix of
      // 5% (the freight charge) and 18% (sibling surcharges), so only the
      // resulting amount is meaningful, not a single rate.
      freight_gst_usd_amount: freightGstUsd,
      freight_gst_inr_amount: round2(freightGstUsd * inrRate),
      origin_gst_percentage: OCEAN_GST_RATE_OTHER * 100,
      origin_gst_usd_amount: originGstUsd,
      origin_gst_inr_amount: round2(originGstUsd * inrRate),
      destination_gst_percentage: OCEAN_GST_RATE_OTHER * 100,
      destination_gst_usd_amount: destinationGstUsd,
      destination_gst_inr_amount: round2(destinationGstUsd * inrRate),
      taxable_usd_amount: taxableUsd,
      taxable_inr_amount: round2(taxableUsd * inrRate),
      gst_usd_amount: totalGstUsd,
      gst_inr_amount: round2(totalGstUsd * inrRate),
    };

    // Leg-wise USD+INR charge amounts that get persisted on the booking.
    console.log("Ocean charges being sent in API:", chargeAmounts);

    // Ocean offer/schedule/query details shared by every persist + staging call
    // (booking_start_ocean, create/update-ocean-booking, kam/ocean-booking) so
    // all of them describe the SAME selection. Read off the `booking` captured
    // above rather than the live `currentBooking` state: the Cashfree checkout
    // in the paid path is async, and closeBookingModal nulls that state — the
    // snapshot keeps the update pinned to the offer the user confirmed.
    // The space the user booked against. Offline (manual-quote) offers carry
    // it on the offer itself; online carrier offers don't — then this is null
    // and nothing is added (spreading null is a no-op), so the booking stores
    // no space and OceanCargoDetails leaves the cell out entirely.
    const bookedSpaceAvailability =
      booking.offer?.space_availability ??
      booking.offer?.productOffer?.space_availability;
    const spaceAvailabilityFields =
      bookedSpaceAvailability !== undefined && bookedSpaceAvailability !== null
        ? { space_availability: bookedSpaceAvailability }
        : null;

    const oceanDetails = {
      ocean_offer: booking.offer,
      ocean_schedule: booking.schedule || {},
      ocean_scheduleId: booking.scheduleId,
      search_query_data: searchQuerySnapshot,
      commodity: state?.commondity,
      stuffing: state?.stuffing,
    };

    // One-line identity of what is about to be persisted, so an update can be
    // eyeballed against the row that was clicked without expanding ocean_offer.
    // `requestType` is derived further down (it only matters once we know a
    // payment is needed) — `isUpdateFlow` already says which one it will be.
    console.log("Ocean booking selection being persisted:", {
      isUpdateFlow,
      carrierScac: booking.offer?.productOffer?.carrierScac,
      carrierName: booking.offer?.productOffer?.carrierName,
      ocean_scheduleId: booking.scheduleId,
      usd_amount: newUsd,
      previously_booked_carrier: prevCarrierScac || null,
      previous_usd_amount: prevUsd,
      containers: searchQuerySnapshot?.containerDetails?.containerDetails,
    });

    if (!cashfree) {
      message.error("Cashfree SDK loading...");
      setIsBookingInFlight(false);
      return;
    }

    // ── Update with NO extra payment (new total <= previously paid) ─────────
    // Nothing to charge — persist the updated offer/schedule against the
    // existing query via ratesearch/update-ocean-booking (payload matches its
    // field contract) and go straight to the success modal.
    if (noExtraPayment) {
      try {
        const updateNoPayPayload = {
          queryId: queryId || null,
          cargoType: "Ocean Cargo",
          usd_amount: newUsd,
          total_amount: newTotalInr,
          previous_usd_amount: prevUsd,
          difference_usd_amount: 0,
          requestType: "2",
          ...chargeAmounts,
          ...oceanDetails,
        };
        console.log(
          "updateOceanBooking (no extra payment) payload:",
          updateNoPayPayload
        );
        const updRes = await dispatch(updateOceanBooking(updateNoPayPayload));
        const payload = updRes?.payload;
        const ok =
          payload?.status === 200 ||
          payload?.status === 201 ||
          payload?.data?.statusCode === 200 ||
          payload?.data?.statusCode === 201;
        if (ok) {
          dispatch(clearOceanSearchForm());
          dispatch(updatebookinghistorytabvalue(2));
          setBookingSuccessKind("confirmed");
          setShowBookingSuccess(true);
        } else {
          message.error(
            payload?.data?.message ||
              "Something went wrong while updating booking"
          );
        }
      } catch (err) {
        console.error("Ocean booking update error:", err);
        message.error("Something went wrong while updating booking");
      }
      setIsBookingInFlight(false);
      closeBookingModal();
      return;
    }

    // ── Amount to actually charge ───────────────────────────────────────────
    // Paid update -> only the increase; new booking -> the full new total.
    // Both in USD, converted to INR (what Cashfree charges).
    const amountUsdToCharge = isUpdateFlow ? differenceUsd : newUsd;
    const bookingAmountValue = Number(amountUsdToCharge * inrRate);

    // No valid amount (missing FX rate, or a non-positive total) — don't open a
    // Cashfree order that can't succeed.
    if (!(bookingAmountValue > 0)) {
      message.error("Unable to determine the payable amount. Please try again.");
      setIsBookingInFlight(false);
      return;
    }

    // requestType follows the flow (see isUpdateFlow doc above): "2" when
    // re-quoting an ALREADY-BOOKED query, "1" for a fresh new booking. This one
    // value drives every downstream call in this sequence — booking_start_ocean,
    // create_order (getorderid), payment_verification and create/update-ocean-
    // booking — so an update stages, charges and persists consistently as
    // requestType "2".
    const requestType = isUpdateFlow ? "2" : "1";

    // Ocean bookings must first be staged server-side via booking_start_ocean —
    // the ocean analogue of the air-only bookingStart used in the air-cargo flow
    // (NewBookingFlowContent.jsx handleProceedToPay). Only once it returns
    // 200/201 do we create the Cashfree order and run the same
    // getorderid → payment_verification → create/update sequence below.
    let startRes;
    try {
      const bookingStartPayload = {
        queryId,
        bookingAmount: bookingAmountValue,
        requestType,
        bookingData: {
          cargoType: "Ocean Cargo",
          queryId: queryId || null,
          usd_amount: newUsd,
          total_amount: bookingAmountValue,
          ...(isUpdateFlow && {
            previous_usd_amount: prevUsd,
            difference_usd_amount: differenceUsd,
          }),
          ...chargeAmounts,
          ...oceanDetails,
          search_query_data: { ...searchQuerySnapshot, requestType },
        },
      };
      console.log("bookingStartOcean payload:", bookingStartPayload);
      startRes = await dispatch(bookingStartOcean(bookingStartPayload));
    } catch (err) {
      console.error("Ocean booking start error:", err);
      message.error("Failed to start booking. Please try again.");
      setIsBookingInFlight(false);
      return;
    }

    // bookingStartOcean swallows errors and returns err.response, so payload can
    // be a 200 axios response, a non-2xx error response, or undefined. Treat
    // HTTP 200/201 OR a 200/201 backend statusCode as success; anything else
    // aborts before we ever create a Cashfree order.
    const startHttp = startRes?.payload?.status;
    const startBackend = startRes?.payload?.data?.statusCode;
    const startedOk =
      startHttp === 200 ||
      startHttp === 201 ||
      startBackend === 200 ||
      startBackend === 201;

    if (!startedOk) {
      message.error(
        startRes?.payload?.data?.message || "Failed to start booking!"
      );
      setIsBookingInFlight(false);
      return;
    }

    // Same getorderid/payment_verification payload shape as the air-cargo flow
    // (NewBookingFlowContent.jsx handleProceedToPay).
    dispatch(
      getorderid({
        queryId,
        bookingAmount: bookingAmountValue,
        requestType,
      })
    )
      .then((res) => {
        const resData = res?.payload?.data;

        if (resData?.statusCode !== 200) {
          message.error(resData?.message || "Failed to create order!");
          setIsBookingInFlight(false);
          return;
        }

        const paymentSessionId = resData?.data?.payment_session_id;
        const internalOrderId = resData?.data?.orderId;

        if (!paymentSessionId) {
          message.error("Failed to get Cashfree Session ID");
          setIsBookingInFlight(false);
          return;
        }

        const checkoutOptions = {
          paymentSessionId: paymentSessionId,
          redirectTarget: "_modal",
        };

        cashfree.checkout(checkoutOptions).then(async (result) => {
          if (result.error) {
            message.error("Payment Failed");
            setIsBookingInFlight(false);
            return;
          }

          try {
            const verifyRes = await dispatch(
              payment_verification({
                orderid: internalOrderId,
                requestType,
                queryId,
              })
            );

            const httpStatus = verifyRes?.payload?.status;
            const backendStatus = verifyRes?.payload?.data?.statusCode;

            if (httpStatus === 200 || backendStatus === 200) {
              setResponseMsg(verifyRes.payload.data);
              message.success("Payment successfully verified!");

              // Persist against the existing query (updateOceanBooking) for an
              // update, or create a brand-new booking otherwise. online_amount
              // is what was actually charged now (the difference for a paid
              // update); usd_amount is the full new total.
              const bookingPayload = {
                cargoType: "Ocean Cargo",
                orderid: internalOrderId || null,
                // online_amount = charged NOW (full new total for a new booking,
                // just the difference for a paid update); total_amount = the full
                // New INR total (conversion of usd_amount).
                online_amount: bookingAmountValue,
                total_amount: newTotalInr,
                payment_type: "Online",
                // A KAM booking here has no real MBL captured yet, so it's
                // treated exactly like a customer's own booking of a
                // manual-quote offer: goes to "Confirmation Awaited" and the
                // KAM later opens it from Queries to assign the MBL number
                // and confirm (OceanCargoDetails' existing kam gate) — same
                // flow as the customer side, not a separate shortcut.
                booking_status: isKamOrAdmin
                  ? "Confirmation Awaited"
                  : isUpdateFlow
                    ? "Booking Updated"
                    : "Booking Confirmed",
                queryId: queryId || null,
                payment_status: "Payment Confirmed",

                bookeditem: {},
                booking_confirm_date: savedQuery?.createdAt || "",

                // ⭐ Ocean Fields ⭐
                usd_amount: newUsd,
                // 1 = new booking (create), 2 = update.
                requestType,
                ...(isUpdateFlow && {
                  previous_usd_amount: prevUsd,
                  difference_usd_amount: differenceUsd,
                }),
                ...chargeAmounts,
                ...oceanDetails,
                ...spaceAvailabilityFields,
              };

              console.log(
                `${isUpdateFlow ? "updateOceanBooking" : "createOceanBooking"} payload:`,
                bookingPayload
              );

              // KAM's fresh booking (not a re-quote) still collects payment via
              // Cashfree exactly like the customer flow above — only the
              // persist call differs, hitting the dedicated kam/ocean-booking
              // endpoint (kam role only, per backend's checkKam(["kam"])) so
              // the record is stamped with bookedBy/KAMid. air_admin keeps
              // using the shared createOceanBooking endpoint.
              const persistAction =
                isKamRole && !isUpdateFlow
                  ? createKamOceanBooking({
                      queryId,
                      bookingAmount: bookingAmountValue,
                      bookingData: {
                        cargoType: "Ocean Cargo",
                        total_amount: newTotalInr,
                        payment_type: "Online",
                        usd_amount: newUsd,
                        booking_confirm_date: savedQuery?.createdAt || "",
                        ...chargeAmounts,
                        ...oceanDetails,
                        ...spaceAvailabilityFields,
                      },
                    })
                  : isUpdateFlow
                    ? updateOceanBooking(bookingPayload)
                    : createOceanBooking(bookingPayload);

              dispatch(persistAction)
                .then((bookRes) => {
                  // create/updateOceanBooking swallow errors and return
                  // err.response, so payload can be: a 200 axios response, a
                  // non-2xx error response, or undefined (network/thunk failure
                  // with no response). Treat HTTP 200/201 OR a 200/201 backend
                  // statusCode as success; everything else is a failure.
                  const payload = bookRes?.payload;
                  const okHttp = payload?.status;
                  const okBackend = payload?.data?.statusCode;
                  const isSuccess =
                    okHttp === 200 ||
                    okHttp === 201 ||
                    okBackend === 200 ||
                    okBackend === 201;

                  if (isSuccess) {
                    // Persisted — wipe the ocean search/query redux so a new
                    // search starts fresh (no Edit-Query prefill or stale
                    // results). The listing behind the success modal reads from
                    // router `state`, not this redux slice, so it's unaffected.
                    dispatch(clearOceanSearchForm());
                    dispatch(updatebookinghistorytabvalue(2));
                    // Mirrors bookingPayload.booking_status above — an
                    // isKamOrAdmin booking (air_admin here; "kam" itself never
                    // reaches this Cashfree path) still lands on "Confirmation
                    // Awaited" pending MBL, even though payment was collected.
                    setBookingSuccessKind(
                      isKamOrAdmin ? "confirmation_awaited" : "confirmed"
                    );
                    setShowBookingSuccess(true);
                  } else {
                    message.error(
                      payload?.data?.message ||
                        "Something went wrong while confirming booking"
                    );
                  }
                })
                .catch((err) => {
                  console.error("Ocean booking error:", err);
                  message.error(
                    "Something went wrong while confirming booking"
                  );
                });
            } else {
              message.error("Verification failed. Please contact support.");
            }
          } catch (error) {
            console.error("Payment verification error:", error);
            message.error("Something went wrong while verifying payment");
          }

          setIsBookingInFlight(false);
          closeBookingModal();
        });
      })
      .catch((err) => {
        console.error("Cashfree order error:", err);
        message.error("Error creating order");
        setIsBookingInFlight(false);
      });
  };

  console.log("currentBooking", currentBooking);
  console.log("savedQuery", state?.savedQuery?._id);
  // console.log(
  //   "currentBooking",
  //   currentBooking.offer.productPrice.totalUSDAmount?.SELL?.toLocaleString()
  // );
  // const confirmBooking = () => {
  //   closeBookingModal();
  // };
  const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return "—";
    return d.toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Some carriers (e.g. HDMU) return a schedule with no arrival / no legs.
  // Fall back to sailing date + transit days so we never show "Invalid Date".
  const effectiveArrival = (schedule, offer) => {
    const arr = schedule?.toLocation?.arrival;
    if (arr && !isNaN(new Date(arr).getTime())) return arr;
    const dep = schedule?.fromLocation?.departure || schedule?.sailingDate;
    const days = Number(offer?.productPrice?.transitTimeInDays);
    if (dep && !isNaN(new Date(dep).getTime()) && days > 0) {
      const d = new Date(dep);
      d.setDate(d.getDate() + days);
      return d.toISOString();
    }
    return "";
  };

  // GST rates: the ONE actual ocean-freight charge is taxed at 5%; every
  // other charge is taxed at 18% — including surcharges (fuel, ISPS, energy
  // transition, ...) that Freightify/manual quotes still group under the
  // same FREIGHT rateTypeCode as the freight charge itself. So the rate
  // depends on the individual charge, not on which leg (ORIGIN/FREIGHT/
  // DESTINATION) it's grouped under.
  const OCEAN_GST_RATE_FREIGHT = 0.05;
  const OCEAN_GST_RATE_OTHER = 0.18;
  // Identifies the single "Basic Ocean Freight" line — matched on the
  // Freightify chargeCode/aggregatedChargeCode, or the "Ocean Freight"
  // description buildManualQuoteOffersShape gives its synthesized freight
  // row — never on rateTypeCode alone, since that's shared with sibling
  // surcharges that must stay at 18%.
  const isOceanFreightMainCharge = (charge) => {
    const code = String(charge?.chargeCode || "").toLowerCase();
    const agg = String(charge?.aggregatedChargeCode || "").toLowerCase();
    const desc = String(charge?.description || "").toLowerCase();
    return (
      code === "basic_ocean_freight" ||
      agg === "basic ocean freight" ||
      desc === "ocean freight" ||
      desc === "basic ocean freight"
    );
  };
  const oceanGstRateForCharge = (charge) =>
    isOceanFreightMainCharge(charge) ? OCEAN_GST_RATE_FREIGHT : OCEAN_GST_RATE_OTHER;

  function createChargesBreakdown(charges) {
    let sumOverallTotal = 0;
    // GST accumulated per charge (see oceanGstRateForCharge above), not per
    // leg — taxableAmount ends up equal to sumOverallTotal (kept as a
    // separate variable for clarity at the return site), gstAmount is the
    // sum of every charge's own GST, netAmount is what the customer actually
    // pays (Taxable + GST). gstBySection is still keyed by leg so the
    // Booking Summary modal and the persisted booking record can show each
    // leg's own GST total, even though a leg's GST can now be a mix of
    // rates (e.g. FREIGHT = 5% on the freight charge + 18% on its sibling
    // surcharges).
    let totalGstAmount = 0;
    const gstBySection = {
      ORIGIN: { usd: 0 },
      FREIGHT: { usd: 0 },
      DESTINATION: { usd: 0 },
    };
    const groupedCharges = {
      SELL: { ORIGIN: {}, FREIGHT: {}, DESTINATION: {} },
    };

    const sectionTotals = {
      ORIGIN: { usd: 0, currencies: {} },
      FREIGHT: { usd: 0, currencies: {} },
      DESTINATION: { usd: 0, currencies: {} },
    };

    const sectionHasAllUSD = {
      ORIGIN: true,
      FREIGHT: true,
      DESTINATION: true,
    };

    // Per-leg SELL totals WITH markup applied, in both USD and its INR
    // equivalent (USD × the live USD→INR rate). These are the exact figures
    // rendered in the modal's section-total rows; captured here so the same
    // numbers can be persisted on the booking instead of being recomputed.
    // A leg the offer doesn't quote keeps its {usd:0, inr:0} default.
    const sectionBreakdown = {
      ORIGIN: { usd: 0, inr: 0 },
      FREIGHT: { usd: 0, inr: 0 },
      DESTINATION: { usd: 0, inr: 0 },
    };

    // The very same per-charge figures the `html` table below prints, kept as
    // plain data so the Booking Summary modal can lay them out itself instead
    // of injecting a pre-rendered HTML string. Display only — `html`, `total`,
    // `sections` and `gstBySection` (what the booking payload reads) unchanged.
    const legBlocks = [];

    charges.forEach((charge) => {
      if (charge.rateType !== "SELL") return;

      const legType = charge.rateTypeCode;
      const container = charge.containerType || "General";

      if (!groupedCharges.SELL[legType]) return;
      if (!groupedCharges.SELL[legType][container]) {
        groupedCharges.SELL[legType][container] = [];
      }
      groupedCharges.SELL[legType][container].push(charge);

      if (charge.amountUsd != null && charge.amountUsd !== undefined) {
        sectionTotals[legType].usd += charge.amountUsd;
      } else {
        sectionHasAllUSD[legType] = false;
        const currency = charge.rateCurrency || "UNKNOWN";
        const amount = charge.amount || 0;
        if (!sectionTotals[legType].currencies[currency]) {
          sectionTotals[legType].currencies[currency] = 0;
        }
        sectionTotals[legType].currencies[currency] += amount;
      }
    });

    let html = "";

    ["ORIGIN", "FREIGHT", "DESTINATION"].forEach((legType) => {
      const containers = groupedCharges.SELL[legType];
      if (Object.keys(containers).length === 0) return;

      html += `<style>
        * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
        }

        body {
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
            background: #f5f7fa;
            padding: 20px;
            font-size: 13px;
        }

        .container {
            max-width: 1600px;
            margin: 0 auto;
        }

        .header {
            background: white;
            padding: 20px;
            border-radius: 8px;
            box-shadow: 0 2px 8px rgba(0,0,0,0.08);
            margin-bottom: 20px;
        }

        .header h1 {
            font-size: 24px;
            margin-bottom: 15px;
            color: #2d3748;
        }

        .upload-area {
            border: 2px dashed #cbd5e0;
            border-radius: 8px;
            padding: 30px;
            text-align: center;
            background: #f7fafc;
            cursor: pointer;
            transition: all 0.3s;
        }

        .upload-area:hover {
            border-color: #667eea;
            background: #edf2f7;
        }

        #fileInput {
            display: none;
        }

        .filters {
            background: white;
            padding: 15px;
            border-radius: 8px;
            box-shadow: 0 2px 8px rgba(0,0,0,0.08);
            margin-bottom: 20px;
            display: none;
        }

        .filter-row {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
            gap: 12px;
            align-items: end;
        }

        .filter-group {
            display: flex;
            flex-direction: column;
        }

        .filter-group label {
            font-size: 11px;
            font-weight: 600;
            color: #718096;
            margin-bottom: 5px;
            text-transform: uppercase;
            letter-spacing: 0.5px;
        }

        .filter-group input,
        .filter-group select {
            padding: 8px 10px;
            border: 1px solid #e2e8f0;
            border-radius: 6px;
            font-size: 13px;
            background: white;
        }

        .filter-group input:focus,
        .filter-group select:focus {
            outline: none;
            border-color: #667eea;
        }

        .btn-filter {
            padding: 8px 16px;
            background: #667eea;
            color: white;
            border: none;
            border-radius: 6px;
            cursor: pointer;
            font-weight: 600;
            font-size: 13px;
        }

        .btn-filter:hover {
            background: #5568d3;
        }

        .results {
            display: none;
        }

        .date-selector {
            background: white;
            padding: 12px;
            border-radius: 8px;
            box-shadow: 0 2px 8px rgba(0,0,0,0.08);
            margin-bottom: 20px;
            overflow-x: auto;
        }

        .date-tabs {
            display: flex;
            gap: 8px;
            min-width: min-content;
        }

        .date-tab {
            flex-shrink: 0;
            background: #f8fafc;
            border: 1px solid #e2e8f0;
            border-radius: 6px;
            padding: 8px 14px;
            cursor: pointer;
            transition: all 0.2s;
            min-width: 140px;
        }

        .date-tab:hover {
            background: #edf2f7;
            border-color: #cbd5e0;
        }

        .date-tab.active {
            background: #667eea;
            border-color: #667eea;
            color: white;
        }

        .date-tab-date {
            font-size: 12px;
            font-weight: 600;
            margin-bottom: 2px;
        }

        .date-tab-info {
            font-size: 10px;
            opacity: 0.75;
        }

        .rates-container {
            display: none;
        }

        .rates-container.active {
            display: block;
        }

        .rate-row {
            background: white;
            border-radius: 8px;
            margin-bottom: 12px;
            box-shadow: 0 2px 6px rgba(0,0,0,0.06);
            overflow: hidden;
            border: 1px solid #e2e8f0;
        }

        .rate-row:hover {
            box-shadow: 0 4px 12px rgba(0,0,0,0.1);
        }

        .row-header {
            display: grid;
            grid-template-columns: 2fr 1fr 1fr auto auto;
            gap: 15px;
            padding: 12px 15px;
            background: #f8fafc;
            border-bottom: 1px solid #e2e8f0;
            align-items: center;
        }

        .route-info {
            display: flex;
            flex-direction: column;
            gap: 4px;
        }

        .port-route {
            display: flex;
            align-items: center;
            gap: 10px;
        }

        .port-code {
            font-weight: 700;
            font-size: 15px;
            color: #2d3748;
        }

        .port-name {
            font-size: 11px;
            color: #718096;
        }

        .schedule-dates {
            font-size: 11px;
            color: #718096;
            display: flex;
            gap: 12px;
        }

        .schedule-dates strong {
            color: #4a5568;
        }

        .arrow {
            color: #667eea;
            font-weight: 700;
        }

        .carrier-info {
            display: flex;
            align-items: center;
            gap: 10px;
        }

        .carrier-badge {
            background: #667eea;
            color: white;
            padding: 3px 8px;
            border-radius: 4px;
            font-weight: 600;
            font-size: 11px;
        }

        .cargo-badge {
            padding: 3px 8px;
            border-radius: 4px;
            font-size: 11px;
            font-weight: 600;
            background: #c6f6d5;
            color: #22543d;
        }

        .cargo-badge.hazardous {
            background: #fed7d7;
            color: #c53030;
        }

        .price-summary {
            text-align: right;
        }

        .price-label {
            font-size: 10px;
            color: #718096;
            text-transform: uppercase;
            letter-spacing: 0.5px;
        }

        .price-value {
            font-size: 18px;
            font-weight: 700;
            color: #2d3748;
        }

        .btn-book {
            padding: 8px 16px;
            background: #48bb78;
            color: white;
            border: none;
            border-radius: 6px;
            cursor: pointer;
            font-weight: 600;
            font-size: 13px;
            margin-left: 8px;
        }

        .btn-book:hover {
            background: #38a169;
        }

        .expand-btn {
            padding: 6px 12px;
            background: #edf2f7;
            border: none;
            border-radius: 6px;
            cursor: pointer;
            font-size: 12px;
            font-weight: 600;
            color: #4a5568;
        }

        .expand-btn:hover {
            background: #e2e8f0;
        }

        .row-details {
            display: none;
            padding: 15px;
            background: #fafbfc;
        }

        .row-details.expanded {
            display: block;
        }

        .details-grid {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 15px;
        }

        .detail-section {
            background: white;
            padding: 12px;
            border-radius: 6px;
            border: 1px solid #e2e8f0;
        }

        .section-title {
            font-weight: 700;
            font-size: 12px;
            color: #2d3748;
            margin-bottom: 10px;
            text-transform: uppercase;
            letter-spacing: 0.5px;
            padding-bottom: 8px;
            border-bottom: 2px solid #e2e8f0;
        }

        .schedule-timeline {
            position: relative;
            padding-left: 25px;
        }

        .schedule-stop {
            position: relative;
            padding: 8px 0;
        }

        .schedule-stop::before {
            content: '';
            position: absolute;
            left: -25px;
            top: 0;
            width: 10px;
            height: 10px;
            background: #667eea;
            border: 2px solid white;
            border-radius: 50%;
            box-shadow: 0 0 0 2px #667eea;
        }

        .schedule-stop::after {
            content: '';
            position: absolute;
            left: -21px;
            top: 10px;
            width: 2px;
            height: calc(100% - 10px);
            background: #cbd5e0;
        }

        .schedule-stop:last-child::after {
            display: none;
        }

        .schedule-stop:last-child::before {
            background: #48bb78;
            box-shadow: 0 0 0 2px #48bb78;
        }

        .stop-header {
            font-weight: 600;
            color: #2d3748;
            margin-bottom: 4px;
            font-size: 13px;
        }

        .stop-details {
            font-size: 11px;
            color: #718096;
            line-height: 1.4;
        }

        .charges-table {
            width: 100%;
            font-size: 11px;
        }

        .charges-table th {
            text-align: left;
            padding: 6px 8px;
            background: #f8fafc;
            font-weight: 600;
            color: #4a5568;
            font-size: 10px;
            text-transform: uppercase;
            letter-spacing: 0.5px;
        }

        .charges-table td {
            padding: 6px 8px;
            border-top: 1px solid #e2e8f0;
            color: #2d3748;
        }

        .leg-type-header:hover {
            filter: brightness(1.05);
        }

        .leg-type-header {
            background: linear-gradient(to bottom right, #fbbf24, #d97706);
            color: white;
            padding: 6px 10px;
            font-weight: 600;
            margin: 10px 0 6px 0;
            border-radius: 8px;
            font-size: 11px;
            transition: filter 0.15s;
        }

        .section-total {
            background: #f8fafc;
            padding: 8px;
            margin-top: 8px;
            border-radius: 4px;
            font-weight: 700;
            font-size: 12px;
            color: #2d3748;
            text-align: right;
            border: 1px solid #e2e8f0;
        }

        .meta-info {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
            gap: 10px;
            margin-top: 10px;
        }

        .meta-item {
            font-size: 11px;
        }

        .meta-label {
            color: #718096;
            display: block;
            margin-bottom: 2px;
        }

        .meta-value {
            color: #2d3748;
            font-weight: 600;
        }

        .error-message {
            background: #fed7d7;
            color: #c53030;
            padding: 12px;
            border-radius: 6px;
            margin-top: 15px;
            display: none;
        }

        .stats-bar {
            display: flex;
            gap: 20px;
            padding: 10px 0;
            font-size: 12px;
            color: #718096;
        }

        .stat-item {
            font-weight: 600;
        }

        .stat-value {
            color: #2d3748;
            margin-left: 5px;
        }

        /* Booking Modal Styles */
        .modal-overlay {
            display: none;
            position: fixed;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background: rgba(0, 0, 0, 0.5);
            z-index: 1000;
            align-items: center;
            justify-content: center;
        }

        .modal-overlay.active {
            display: flex;
        }

        .modal-content {
            background: white;
            border-radius: 12px;
            width: 90%;
            max-width: 900px;
            max-height: 90vh;
            overflow: hidden;
            display: flex;
            flex-direction: column;
            box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
        }

        .modal-header {
            padding: 20px 24px;
            border-bottom: 2px solid #e2e8f0;
            background: #f8fafc;
        }

        .modal-header h2 {
            font-size: 20px;
            color: #2d3748;
            margin-bottom: 8px;
        }

        .modal-header-info {
            font-size: 12px;
            color: #718096;
        }

        .modal-body {
            padding: 24px;
            overflow-y: auto;
            flex: 1;
        }

        .charge-summary-section {
            margin-bottom: 24px;
        }

        .section-header {
            background: #667eea;
            color: white;
            padding: 10px 12px;
            border-radius: 6px;
            font-weight: 700;
            font-size: 13px;
            margin-bottom: 12px;
            text-transform: uppercase;
            letter-spacing: 0.5px;
        }

        .summary-table {
            width: 100%;
            border-collapse: collapse;
            margin-bottom: 12px;
        }

        .summary-table th {
            text-align: left;
            padding: 8px 10px;
            background: #f8fafc;
            font-weight: 600;
            color: #4a5568;
            font-size: 11px;
            text-transform: uppercase;
            border-bottom: 2px solid #e2e8f0;
        }

        .summary-table td {
            padding: 8px 10px;
            border-bottom: 1px solid #e2e8f0;
            font-size: 12px;
            color: #2d3748;
        }

        .summary-table tr:last-child td {
            border-bottom: none;
        }

        .section-total-row {
            background: #f8fafc;
            font-weight: 700;
            border-top: 2px solid #e2e8f0;
        }

        .section-total-row td {
            padding: 10px;
            font-size: 13px;
        }

        .grand-total {
            background: #667eea;
            color: white;
            padding: 16px;
            border-radius: 8px;
            margin-top: 16px;
            display: flex;
            justify-content: space-between;
            align-items: center;
        }

        .grand-total-label {
            font-size: 16px;
            font-weight: 700;
            text-transform: uppercase;
        }

        .grand-total-amount {
            font-size: 24px;
            font-weight: 700;
        }

        .remarks-section {
            margin-top: 24px;
            padding: 16px;
            background: #fff5f5;
            border: 1px solid #feb2b2;
            border-radius: 8px;
        }

        .remarks-title {
            font-weight: 700;
            font-size: 13px;
            color: #c53030;
            margin-bottom: 12px;
            text-transform: uppercase;
        }

        .remarks-content {
            font-size: 12px;
            color: #2d3748;
            line-height: 1.6;
            white-space: pre-line;
        }

        .agreement-checkbox {
            margin-top: 16px;
            display: flex;
            align-items: center;
            gap: 8px;
        }

        .agreement-checkbox input[type="checkbox"] {
            width: 18px;
            height: 18px;
            cursor: pointer;
        }

        .agreement-checkbox label {
            font-size: 13px;
            color: #2d3748;
            cursor: pointer;
            font-weight: 600;
        }

        .modal-footer {
            padding: 16px 24px;
            border-top: 2px solid #e2e8f0;
            background: #f8fafc;
            display: flex;
            justify-content: flex-end;
            gap: 12px;
        }

        .btn-modal {
            padding: 10px 20px;
            border: none;
            border-radius: 6px;
            cursor: pointer;
            font-weight: 600;
            font-size: 13px;
            transition: all 0.2s;
        }

        .btn-cancel {
            background: #e2e8f0;
            color: #4a5568;
        }

        .btn-cancel:hover {
            background: #cbd5e0;
        }

        .btn-confirm-book {
            background: #48bb78;
            color: white;
        }

        .btn-confirm-book:hover:not(:disabled) {
            background: #38a169;
        }

        .btn-confirm-book:disabled {
            background: #cbd5e0;
            cursor: not-allowed;
            opacity: 0.6;
        }
    </style><div class="leg-type-header">${legType}</div>`;
      html +=
        '<table class="charges-table"><thead><tr><th>Description</th><th>Container</th><th>Qty</th><th>Rate</th><th>Currency</th><th>Amount</th><th>USD Amount</th><th>INR Amount</th></tr></thead><tbody>';

      // NOTE: the customer-category markup used to be applied here, per leg,
      // from customerCategory.ocean_*_markup. It now lands server-side in
      // rateSearchController.allOceanrates (services/markupServices/
      // ocean-markup-service.js), which floors it against the configured
      // minimum markup and marks up the Basic Ocean Freight line before the
      // rates ever reach the browser. Every amount below is therefore already
      // the sell price — re-applying a percentage here would double it.

      // This leg's own GST total — a sum of each of its charges' GST at
      // THAT CHARGE's own rate, not the leg's total × one rate, since a
      // FREIGHT leg mixes the 5% freight charge with 18% sibling surcharges.
      let legGstUsd = 0;
      const legRowsData = [];

      Object.entries(containers).forEach(([container, chargeList]) => {
        chargeList.forEach((charge, chargeIdx) => {
          if (charge?.amountUsd != null) {
            legGstUsd += charge.amountUsd * oceanGstRateForCharge(charge);
          }

          const rowUsd = charge?.amountUsd != null ? charge.amountUsd : null;
          legRowsData.push({
            key: `${legType}-${container}-${chargeIdx}`,
            description: charge.description,
            chargeCode: charge.chargeCode || charge.aggregatedChargeCode || "",
            container,
            qty: charge.qty,
            rate: charge?.rate != null ? charge.rate : null,
            rateCurrency: charge.rateCurrency,
            amount: charge?.amount != null ? charge.amount : null,
            usdAmount: rowUsd,
            inrAmount: rowUsd != null ? rowUsd * usdInrRate : null,
          });

          html += `
                            <tr>
                               <td style="width: 300px; max-width: 300px; word-wrap: break-word;">${charge.description}</td>
                                <td>${container}</td>
                                <td>${charge.qty || "-"}</td>
                                <td>${
                                  charge?.rate != null
                                    ? Math.round(charge.rate).toLocaleString()
                                    : "-"
                                }</td>
                                <td>${charge.rateCurrency}</td>
                                <td style="font-weight: 600;">${
                                  charge?.amount != null
                                    ? Math.round(charge.amount).toLocaleString()
                                    : "-"
                                }</td>
                                <td style="font-weight: 700; color: #2d3748;">${
                                  charge?.amountUsd != null
                                    ? Math.round(charge.amountUsd).toLocaleString()
                                    : "-"
                                }</td>
                                <td style="font-weight: 700; color: #2d3748;">${
                                  charge?.amountUsd != null
                                    ? Math.round(
                                        charge.amountUsd * usdInrRate
                                      ).toLocaleString()
                                    : "-"
                                }</td>
                            </tr>
                        `;
        });
      });

      html += "</tbody></table>";

      // Already the marked-up sell total: sectionTotals is summed from the
      // charges the API returned, which the server has already marked up.
      const totalWithMarkup = Number(sectionTotals[legType]?.usd) || 0;
      const totalWithMarkupInr = totalWithMarkup * usdInrRate;

      // Record this leg's USD + INR total so it can be persisted (same figures
      // shown in the section-total row below). INR here reflects the rate at
      // render time; confirmBooking re-derives INR from `usd` × the live rate.
      if (sectionBreakdown[legType]) {
        sectionBreakdown[legType] = {
          usd: totalWithMarkup,
          inr: totalWithMarkupInr,
        };
      }

      // --- Convert to display format ---
      const displayTotalWithMarkup = Math.round(totalWithMarkup).toLocaleString();
      const displayTotalWithMarkupInr = Math.round(totalWithMarkupInr).toLocaleString();

      // --- Build your original non-markup display (multi-currency) ---

      sumOverallTotal += totalWithMarkup;

      // --- GST on this leg — sum of each of its charges' own GST, already
      // accumulated per-charge above (legGstUsd), not this leg's total × one
      // flat rate. For ORIGIN/DESTINATION every charge is taxed at 18%; for
      // FREIGHT it's a mix of the 5% freight charge and 18% on its sibling
      // surcharges. Not rendered per leg in the HTML below — kept clean, one
      // combined GST explanation lives in the Amount Summary card instead
      // (see the Booking Modal JSX) so GST isn't repeated 4 times over. ---
      totalGstAmount += legGstUsd;
      gstBySection[legType] = { usd: legGstUsd };

      legBlocks.push({
        legType,
        rows: legRowsData,
        totalUsd: totalWithMarkup,
        totalInr: totalWithMarkupInr,
      });

      console.log("displayTotalWithMarkup", displayTotalWithMarkup);

      // --- Final HTML output with markup applied ---
      html += `
  <div class="section-total">
    ${legType} Total: USD ${displayTotalWithMarkup} (INR ${displayTotalWithMarkupInr})
  </div>
`;
    });
    console.log("sumOverallTotal", sumOverallTotal);

    // setGrandTotal(sumOverallTotal);

    const taxableAmount = sumOverallTotal;
    const netAmount = taxableAmount + totalGstAmount;

    return {
      html,
      legBlocks,
      total: taxableAmount,
      taxableAmount,
      gstAmount: totalGstAmount,
      netAmount,
      totalInr: taxableAmount * usdInrRate,
      sections: sectionBreakdown,
      gstBySection,
    };
  }

  useEffect(() => {
    // Wait for the in-flight search (skeleton shows meanwhile).
    if (oceanLoading) return;
    // Same, for the email deep-link's query fetch — it runs BEFORE the search
    // is dispatched, so without this the `!state?.data` branch below would
    // flag "No data received" for a perfectly valid link.
    if (bootstrapping) return;

    // Prefer the redux result (navigated on Search click); fall back to router
    // state.freightifyData for direct/legacy navigations.
    const freightify =
      (oceanRates?.status === 200 && oceanRates?.data?.freightifyData) ||
      state?.freightifyData ||
      null;

    // Freightify empty/unavailable → fall back to a manual KAM quote, if any.
    const manualQuoteSOs =
      (oceanRates?.status === 200 &&
        oceanRates?.data?.manualQuoteData?.oceanResponse?.availabilityResponseSOs) ||
      state?.manualQuoteData?.oceanResponse?.availabilityResponseSOs ||
      null;

    // A query can have both a real Freightify result AND a KAM manual quote
    // (e.g. Freightify came back after the manual quote was already made, or
    // vice versa) — same as the air flow, where a manual quote shows up as
    // just another row (rates_from: "manualQoute") alongside system rates
    // instead of replacing them. Merge both into one { offers, schedules }
    // set rather than letting Freightify silently hide the manual quote.
    const manualShape =
      Array.isArray(manualQuoteSOs) && manualQuoteSOs.length > 0
        ? buildManualQuoteOffersShape(
            manualQuoteSOs,
            (oceanRates?.status === 200 && oceanRates?.data?.savedQuery) || state?.savedQuery || null,
            exchangeRateList
          )
        : null;

    // ...EXCEPT on a manual-quote-only view (?manualQuote=true — the Queries
    // "Quote Available" row and the quotation email's deep link), where the
    // whole point is to show the customer the quote that was prepared for
    // them. The air flow gets this for free by subscribing to MQ_res alone;
    // ocean's single response carries both, so drop Freightify here. The
    // `manualQuoteOnly` payload flag means the backend can stop producing it
    // altogether, but this stands on its own if the flag isn't honoured (or
    // wasn't sent, e.g. the fetched document carried no Quote_status).
    // Guarded on manualShape existing so a link for a query with no manual
    // quote still falls through to the Freightify rates instead of an empty
    // page.
    const combined =
      manualShape && isFromQueriesManualQuote
        ? manualShape
        : freightify && manualShape
        ? {
            offers: [...(freightify.offers || []), ...manualShape.offers],
            schedules: { ...(freightify.schedules || {}), ...manualShape.schedules },
          }
        : freightify || manualShape;

    if (combined) {
      // Recompute every charge's amountUsd from its own native amount +
      // currency via OUR admin exchange-rate table, overriding whatever USD
      // figure the source (Freightify's own conversion, or a manual quote's
      // server-resolved amount) already attached. Freightify converts with
      // its own internal rate, which drifts from ours — round-tripping that
      // mismatched amountUsd back to INR for display produced a second,
      // conflicting INR figure for the same INR-native charge (e.g. a
      // 42,000 INR charge showing as INR 42,265.81 elsewhere on the same
      // row). Recomputing here makes every USD/INR figure trace back to one
      // rate table, so the round trip is exact.
      const ratesByCurrency = buildRatesByCurrency(exchangeRateList);

      // Qty exactly as the customer searched it, keyed by container type —
      // the breakdown's Qty column shows the searched mix (e.g. 2 x 20GP),
      // not a charge's own calculation quantity (a per-BL charge comes back
      // as 1, a weight-based one as a weight).
      const searchedQuery =
        (oceanRates?.status === 200 && oceanRates?.data?.savedQuery) ||
        state?.savedQuery ||
        null;
      const searchedContainers = Array.isArray(state?.data?.containerDetails)
        ? state.data.containerDetails
        : searchedQuery?.containerDetails?.containerDetails || [];
      const searchedQtyByContainer = {};
      searchedContainers.forEach((cc) => {
        const code = String(cc?.type || cc?.containerType || "").toUpperCase();
        const q = Number(cc?.quantity ?? cc?.qty);
        if (code && q > 0) searchedQtyByContainer[code] = q;
      });

      // A charge that carries the search-currency pair states its TOTAL in the
      // currency the search was run in (charge.searchCurrency) — that figure IS
      // the Amount column, and Rate is it split across the searched qty. Every
      // downstream INR/GST figure then falls out of amountUsd below, converted
      // through the same admin rate table.
      const applySearchCurrency = (charge) => {
        const searchTotal = Number(charge?.searchCurrencyAmount);
        // Left as-is when the search named no qty for this container type, so
        // a charge that legitimately has none (a manual quote's origin/
        // destination lines) still renders "-" rather than a made-up 1.
        const qty =
          searchedQtyByContainer[
            String(charge?.containerType || "").toUpperCase()
          ] || charge?.qty;
        if (
          charge?.searchCurrencyAmount == null ||
          charge.searchCurrencyAmount === "" ||
          !Number.isFinite(searchTotal)
        ) {
          return { ...charge, qty };
        }
        const divQty = Number(qty) > 0 ? Number(qty) : 1;
        return {
          ...charge,
          qty,
          rate: searchTotal / divQty,
          rateCurrency:
            charge.searchCurrency || charge.rateCurrency || "USD",
          amount: searchTotal,
        };
      };

      const normalized = {
        ...combined,
        offers: (combined.offers || []).map((offer) => ({
          ...offer,
          productPrice: {
            ...offer.productPrice,
            charges: (offer.productPrice?.charges || []).map((raw) => {
              const charge = applySearchCurrency(raw);
              return {
                ...charge,
                amountUsd: convertAmountToUsd(
                  charge.amount,
                  charge.rateCurrency,
                  ratesByCurrency
                ),
              };
            }),
          },
        })),
      };
      setRatesData(normalized);
      prepareFilters(normalized);
      displayRates(normalized);
      setError("");
    } else if (oceanRates) {
      setError(
        oceanRates?.data?.message ||
          "No rates found for this search. Please adjust your query and try again."
      );
    } else if (!state?.data) {
      setError("No data received");
    }
    // usdInrRate: exchange rates load in a separate effect/request and can
    // resolve after this one already ran with rate 0 — re-run once it lands
    // so manual-quote charges get re-converted with the real rate instead of
    // being stuck showing the raw INR figure.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [oceanRates, oceanLoading, state, usdInrRate, bootstrapping, isFromQueriesManualQuote]);

  useEffect(() => {
    // Cashfree SDK load isn't cancellable; guard against a late setState if we
    // unmount before it resolves.
    let active = true;
    load({ mode: "sandbox" }).then((instance) => {
      if (active) setCashfree(instance);
    });
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    // One AbortController for both results-page lookups. Aborted from the
    // cleanup (unmount / route change) and explicitly by Edit Query, so a
    // request that's still running when the user leaves is cancelled and never
    // updates state afterwards.
    const controller = new AbortController();
    abortRef.current = controller;
    const { signal } = controller;

    // The customer-category markup used to be fetched here to price the rates
    // client-side. The server now applies it before the rates are returned, so
    // the browser no longer needs (or receives) the markup percentages.

    // Exchange rates — cookie-authenticated via the shared httpinstance
    // (Task #99: no bearer token is readable client-side to attach
    // manually anymore), abortable via the AbortController signal.
    (async () => {
      try {
        const result = await httpinstance.get("admin/getexchangerates", { signal });
        if (!signal.aborted && result.status === 200) {
          setExchangeRateList(result.data?.data);
        }
      } catch (error) {
        // Cancelled request — no-op. axios throws CanceledError on abort.
        if (
          signal.aborted ||
          (axios.isCancel && axios.isCancel(error)) ||
          error?.name === "CanceledError" ||
          error?.name === "AbortError"
        ) {
          return;
        }
        console.log(error);
      }
    })();

    return () => {
      abortPageRequests();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  // ── Query-bar values from the persisted search query (state.data) ──
  // Port codes come from the parenthesised UN/LOCODE in the shipment string
  // ("Mundra - (INMUN1)"), falling back to the offer port code when offers
  // are present. Country is derived from the first two chars of the code.
  const q = state?.data || {};
  const parenCode = (s) => {
    const m = /\(([^)]+)\)/.exec(s || "");
    return (m ? m[1] : s || "").trim();
  };
  const cleanCode = (s) => parenCode(s).replace(/\d+$/, "");
  const qbFirst = allOffers[0];
  const qbOrigin =
    qbFirst?.offer?.productOffer?.originPort ||
    cleanCode(q?.originShipment?.originShipment);
  const qbDest =
    qbFirst?.offer?.productOffer?.destinationPort ||
    cleanCode(q?.destinationShipment?.destinationShipment);
  const qbCountry = (code) => {
    const cc = (code || "").slice(0, 2).toUpperCase();
    try {
      return new Intl.DisplayNames(["en"], { type: "region" }).of(cc) || cc;
    } catch {
      return cc;
    }
  };
  // Country flag from the port's UN/LOCODE (first 2 chars = ISO country code).
  const flagUrl = (code) =>
    `https://flagcdn.com/w40/${(code || "").slice(0, 2).toLowerCase()}.png`;
  const qbCargo =
    qbFirst?.offer?.productPrice?.cargoType ||
    q?.commonditytype ||
    state?.commondity ||
    "";
  const qbDate = q?.goodsDetails?.cargoreadydate
    ? q.goodsDetails.cargoreadydate.replace(/-/g, "/")
    : "";
  const qbContainerArr = Array.isArray(q?.containerDetails)
    ? q.containerDetails
    : Array.isArray(q?.containerDetails?.containerDetails)
    ? q.containerDetails.containerDetails
    : [];
  // Total weight across all containers (each kgs is already the line total;
  // legacy rows are per container × qty), shown as its own pill.
  const qbTotalWeight = totalContainerWeight(qbContainerArr);

  // ── MUI listing helpers ──
  // Structured SELL breakdown (per leg + container) for the "i" charge
  // popover. Its grand total equals createChargesBreakdown().total.
  //
  // The markup is no longer applied here: it is already baked into the amounts
  // by the server (see the note in createChargesBreakdown above), so these
  // figures are displayed as received. The *Disp fields are kept so the
  // popover's render code stays unchanged.
  const computeSellBreakdown = (charges = []) => {
    const sections = { FREIGHT: {}, ORIGIN: {}, DESTINATION: {} };
    const totals = { FREIGHT: 0, ORIGIN: 0, DESTINATION: 0 };
    charges.forEach((c) => {
      if (c.rateType !== "SELL") return;
      const legType = c.rateTypeCode;
      if (!sections[legType]) return;
      const container = c.containerType || "General";
      const marked = {
        ...c,
        rateDisp: c.rate != null ? c.rate : null,
        amountUsdDisp: c.amountUsd != null ? c.amountUsd : 0,
      };
      if (!sections[legType][container]) sections[legType][container] = [];
      sections[legType][container].push(marked);
      totals[legType] += marked.amountUsdDisp || 0;
    });
    return {
      sections,
      totals,
      grand: totals.FREIGHT + totals.ORIGIN + totals.DESTINATION,
    };
  };
  const chargesTotal = (charges) => computeSellBreakdown(charges).grand;
  const fmtUsd = (n) => Math.round(Number(n) || 0).toLocaleString();
  const carrierColor = (scac) =>
    ({
      EGLV: "#1F9254",
      ONEY: "#C6007E",
      HLCU: "#C1440E",
      COSU: "#C8102E",
      OOLU: "#003DA5",
      HDMU: "#003876",
    }[scac] || "#4B5563");
  const formatDateShort = (dateKey) => {
    const d = new Date(dateKey + "T00:00:00");
    if (isNaN(d.getTime())) return "FLEXIBLE DATE";
    return d
      .toLocaleDateString("en-US", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      })
      .toUpperCase();
  };

  // Charge-breakdown popover ("i" button, hover-triggered) + date-strip scroll.
  const [chargeAnchor, setChargeAnchor] = useState(null);
  const [chargeOffer, setChargeOffer] = useState(null);
  // Small close delay so the pointer can travel from the "i" button into the
  // popover without it flickering shut — the popover cancels this timer on its
  // own mouse-enter and re-arms it on mouse-leave. Opened on hover (no click).
  const chargeCloseTimer = useRef(null);
  const cancelCloseCharges = () => {
    if (chargeCloseTimer.current) {
      clearTimeout(chargeCloseTimer.current);
      chargeCloseTimer.current = null;
    }
  };
  const openCharges = (e, offer) => {
    cancelCloseCharges();
    setChargeAnchor(e.currentTarget);
    setChargeOffer(offer);
  };
  const closeCharges = () => {
    cancelCloseCharges();
    setChargeAnchor(null);
    setChargeOffer(null);
  };
  const scheduleCloseCharges = () => {
    cancelCloseCharges();
    chargeCloseTimer.current = setTimeout(closeCharges, 120);
  };
  const dateStripRef = useRef(null);
  const scrollDates = (dir) =>
    dateStripRef.current?.scrollBy({ left: dir * 300, behavior: "smooth" });

  const AMBER = "#F5A623";
  const AMBER_DARK = "#E08E0B";

  // Sidebar filter styling + dynamic total-rate range (from displayed totals).
  const sidebarLabel = { fontSize: 13, fontWeight: 700, mb: 0.75 };
  const sidebarField = {
    "& .MuiOutlinedInput-root": { borderRadius: 2 },
    "& .MuiSelect-select, & .MuiInputBase-input": { fontSize: 13 },
  };
  const amberRadio = { color: "#CBD5E1", "&.Mui-checked": { color: AMBER } };
  const priceVals = allOffers.map((it) =>
    chargesTotal(it.offer.productPrice.charges)
  );
  const rateMin = priceVals.length ? Math.floor(Math.min(...priceVals)) : 0;
  const rateMaxRaw = priceVals.length ? Math.ceil(Math.max(...priceVals)) : 0;
  const rateMax = rateMaxRaw > rateMin ? rateMaxRaw : rateMin + 1;
  const priceRangeVal = filters.priceRange || [rateMin, rateMax];
  const hasRates = !!ratesData && allOffers.length > 0;

  // Only show the date-strip arrows when the cards actually overflow the width.
  const [dateOverflow, setDateOverflow] = useState(false);
  useEffect(() => {
    const check = () => {
      const el = dateStripRef.current;
      if (el) setDateOverflow(el.scrollWidth > el.clientWidth + 1);
    };
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, [dateGroups, currentDateKey, oceanLoading, hasRates]);

  // Column header (shown both while loading and once results are in), aligned
  // to the rate-row grid.
  const columnHeader = (
    <Box
      sx={{
        display: { xs: "none", md: "grid" },
        gridTemplateColumns:
          "28px minmax(120px, 1.9fr) minmax(118px, 1.5fr) minmax(132px, 1.1fr) minmax(132px, 1.1fr) minmax(40px, 0.45fr) minmax(94px, 0.65fr) minmax(124px, 1.1fr)",
        gap: 1.5,
        alignItems: "center",
        minWidth: "1050px",
        pl: "20px",
        pr: 2,
        py: 1.5,
        bgcolor: "#fff",
        borderBottom: "1px solid #E5E7EB",
      }}
    >
      <span />
      {[
        "Shipping Line",
        "Route",
        "Departure",
        "Arrival",
        "Transit",
        "Cargo",
      ].map((h) => (
        <Typography
          key={h}
          sx={{
            fontSize: 10.5,
            fontWeight: 700,
            color: "#8B93A1",
            textTransform: "uppercase",
            letterSpacing: ".04em",
            ...(h === "Cargo" ? { pl: "21px" } : {}),
          }}
        >
          {h}
        </Typography>
      ))}
      <Typography
        sx={{
          fontSize: 10.5,
          fontWeight: 700,
          color: "#8B93A1",
          textTransform: "uppercase",
          letterSpacing: ".04em",
          textAlign: "right",
        }}
      >
        Total Rate
      </Typography>
    </Box>
  );

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      <div>
        <Box sx={{ display: "flex", gap: 2, alignItems: "flex-start" }}>
          {(oceanLoading || bootstrapping || hasRates) && (
            <Paper
              elevation={0}
              sx={{
                width: 250,
                flexShrink: 0,
                borderRadius: 2.5,
                border: "1px solid #E5E7EB",
                bgcolor: "#fff",
                position: "sticky",
                top: 16,
                alignSelf: "flex-start",
                display: "flex",
                flexDirection: "column",
                // Same panel layout as the Air filters (see .filterFormRoot /
                // .filterFormScroll / .filterFormFooter in customStyle.css):
                // the field list scrolls internally while Reset/Apply Filters
                // stay pinned as a footer that never scrolls out of view.
                maxHeight: "calc(100vh - 180px)",
                overflow: "hidden",
              }}
            >
              <Box sx={{ flex: "1 1 auto", minHeight: 0, overflowY: "auto", p: 2.25 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                <Box
                  sx={{
                    width: 26,
                    height: 26,
                    borderRadius: 1.5,
                    bgcolor: "#FFF4E0",
                    color: AMBER_DARK,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Filter size={14} />
                </Box>
                <Typography sx={{ fontSize: 14.5, fontWeight: 800 }}>
                  Filters
                </Typography>
              </Box>

              <Typography sx={sidebarLabel}>Shipping Line</Typography>
              <Select
                fullWidth
                size="small"
                displayEmpty
                value={filters.carrier}
                onChange={(e) =>
                  setFilters({ ...filters, carrier: e.target.value })
                }
                sx={sidebarField}
              >
                <MenuItem value="">All Shipping Lines</MenuItem>
                {filterOptions.carriers.map((c) => (
                  <MenuItem key={c} value={c}>
                    {c}
                  </MenuItem>
                ))}
              </Select>
              <Divider sx={{ my: 2 }} />

              <Typography sx={sidebarLabel}>Cargo Type</Typography>
              <RadioGroup
                value={filters.cargoType}
                onChange={(e) =>
                  setFilters({ ...filters, cargoType: e.target.value })
                }
              >
                <FormControlLabel
                  value=""
                  control={<Radio size="small" sx={amberRadio} />}
                  label={
                    <span style={{ fontSize: 13.5, fontWeight: 600 }}>All</span>
                  }
                />
                {filterOptions.cargoTypes.map((c) => (
                  <FormControlLabel
                    key={c}
                    value={c}
                    control={<Radio size="small" sx={amberRadio} />}
                    label={
                      <span style={{ fontSize: 13.5, fontWeight: 600 }}>
                        {c}
                      </span>
                    }
                  />
                ))}
              </RadioGroup>
              <Divider sx={{ my: 2 }} />

              <Typography sx={sidebarLabel}>Container Type</Typography>
              <Select
                fullWidth
                size="small"
                displayEmpty
                value={filters.containerType}
                onChange={(e) =>
                  setFilters({ ...filters, containerType: e.target.value })
                }
                sx={sidebarField}
              >
                <MenuItem value="">All Containers</MenuItem>
                {filterOptions.containerTypes.map((c) => (
                  <MenuItem key={c} value={c}>
                    {c}
                  </MenuItem>
                ))}
              </Select>
              <Divider sx={{ my: 2 }} />

              <Typography sx={sidebarLabel}>Max Price (USD)</Typography>
              <TextField
                fullWidth
                size="small"
                type="number"
                placeholder="e.g., 250000"
                value={filters.maxPrice}
                onChange={(e) =>
                  setFilters({ ...filters, maxPrice: e.target.value })
                }
                sx={sidebarField}
              />
              <Divider sx={{ my: 2 }} />

              <Typography sx={sidebarLabel}>Total Rate Range</Typography>
              {oceanLoading || !priceVals.length ? (
                <Skeleton variant="rounded" height={26} />
              ) : (
                <Box sx={{ px: 0.75 }}>
                  <Slider
                    size="small"
                    min={rateMin}
                    max={rateMax}
                    value={priceRangeVal}
                    onChange={(e, v) =>
                      setFilters({ ...filters, priceRange: v })
                    }
                    valueLabelDisplay="auto"
                    valueLabelFormat={(v) => `$${Math.round(v).toLocaleString()}`}
                    disableSwap
                    sx={{ color: AMBER }}
                  />
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      fontSize: 11,
                      fontWeight: 700,
                      color: "#4B5563",
                    }}
                  >
                    <span>
                      {`$${Math.round(priceRangeVal[0]).toLocaleString()}`}
                    </span>
                    <span>
                      {`$${Math.round(priceRangeVal[1]).toLocaleString()}`}
                    </span>
                  </Box>
                </Box>
              )}
              </Box>

              {/* Footer — flex-shrink:0 so it never scrolls with the field
                  list above (same as Air's .filterFormFooter): Reset and
                  Apply Filters side by side, plus the filtered/total count. */}
              <Box
                sx={{
                  flexShrink: 0,
                  display: "flex",
                  gap: 1,
                  p: 1.5,
                  borderTop: "1px solid #E5E7EB",
                  bgcolor: "#fff",
                }}
              >
                <Button
                  fullWidth
                  size="small"
                  variant="outlined"
                  disableElevation
                  onClick={resetFilters}
                  sx={{
                    textTransform: "none",
                    fontWeight: 700,
                    fontSize: 12.5,
                    // nowrap keeps "Apply Filters" on one line so both buttons
                    // stay a single compact row in the narrow filter panel.
                    whiteSpace: "nowrap",
                    py: 0.5,
                    minHeight: 30,
                    borderRadius: 1.5,
                    color: AMBER_DARK,
                    borderColor: "#E5E7EB",
                    "&:hover": {
                      borderColor: AMBER,
                      background: "#FFFAF0",
                    },
                  }}
                >
                  Reset
                </Button>

                <Button
                  fullWidth
                  size="small"
                  variant="contained"
                  disableElevation
                  onClick={applyFilters}
                  sx={{
                    textTransform: "none",
                    fontWeight: 700,
                    fontSize: 12.5,
                    whiteSpace: "nowrap",
                    py: 0.5,
                    minHeight: 30,
                    borderRadius: 1.5,
                    background: `linear-gradient(135deg, ${AMBER}, ${AMBER_DARK})`,
                    "&:hover": { background: `linear-gradient(135deg, ${AMBER}, ${AMBER_DARK})` },
                  }}
                >
                  Apply Filters
                </Button>
              </Box>

              <Box
                sx={{
                  flexShrink: 0,
                  display: "flex",
                  flexDirection: "column",
                  gap: 0.5,
                  fontSize: 11.5,
                  color: "#8B93A1",
                  px: 2.25,
                  pb: 1.5,
                }}
              >
                <span>
                  Showing: <b style={{ color: "#1F2430" }}>{stats.filtered}</b>
                </span>
                <span>
                  Total: <b style={{ color: "#1F2430" }}>{stats.total}</b>
                </span>
              </Box>
            </Paper>
          )}
          <Box sx={{ flex: 1, minWidth: 0 }}>
        {/* Query bar (Air-style, sourced from state.data) */}
        <div className="ocean-qbar">
          <style>{`
            .ocean-qbar{ display:flex; align-items:center; flex-wrap:wrap; gap:14px;
              background:#FFFAF0; border:1px solid #FFF4E0; border-radius:10px;
              padding:14px 18px; margin-bottom:16px; }
            .ocean-qbar *{ box-sizing:border-box; }
            .ocean-qbar .oqb-edit{ display:flex; flex-direction:column; align-items:center;
              gap:4px; font-size:10.5px; font-weight:700; color:#E08E0B; background:none;
              border:1.5px solid #D8DCE3; border-radius:10px; padding:8px 12px; cursor:pointer; }
            .ocean-qbar .oqb-port{ display:flex; flex-direction:column; align-items:center; gap:2px; }
            .ocean-qbar .oqb-flag{ width:26px; height:18px; object-fit:cover; border-radius:2px;
              margin-bottom:4px; box-shadow:0 1px 3px rgba(0,0,0,0.12); }
            .ocean-qbar .oqb-code{ font-size:17px; font-weight:700; color:#1F2430; letter-spacing:.02em; }
            .ocean-qbar .oqb-country{ font-size:9.5px; color:#8B93A1; font-weight:700;
              letter-spacing:.03em; text-transform:uppercase; }
            .ocean-qbar .oqb-route{ display:flex; align-items:center; gap:6px; }
            .ocean-qbar .oqb-ship{ width:26px; height:26px; border-radius:50%; background:#F5A623;
              color:#fff; display:flex; align-items:center; justify-content:center; flex-shrink:0; }
            .ocean-qbar .oqb-divider{ width:1px; align-self:stretch; background:#D8DCE3; margin:0 2px; }
            .ocean-qbar .oqb-pills{ display:flex; align-items:center; flex-wrap:wrap; gap:8px; flex:1; }
            .ocean-qbar .oqb-pill{ display:flex; align-items:center; gap:6px; font-size:12px;
              font-weight:600; color:#4B5563; background:#fff; border:1px solid #D8DCE3;
              border-radius:999px; padding:6px 12px; }
            .ocean-qbar .oqb-pill svg{ color:#E08E0B; flex-shrink:0; }
            .ocean-qbar .oqb-pill.oqb-local{ background:#FFF4E0; border-color:#F5A623; color:#E08E0B; }
            /* One chip per container: ship icon + type + its weight, inline. */
            .ocean-qbar .oqb-ctr .oqb-ctr-type{ font-weight:700; color:#1F2430; }
            .ocean-qbar .oqb-ctr .oqb-ctr-wgt{ font-size:11px; font-weight:600;
              color:#8B93A1; text-transform:uppercase; letter-spacing:.02em; }
            /* "+N" more-containers pill (hover shows the rest in a tooltip). */
            .ocean-qbar .oqb-ctr-more{ font-weight:800; color:#E08E0B; cursor:default; }
          `}</style>

          <button
            className="oqb-edit"
            onClick={() => {
              // Cancel any in-flight results-page lookups before leaving.
              abortPageRequests();
              // The queryId to re-quote: prefer the one already stamped on the
              // submitted query (booking-history deep link / earlier re-quote),
              // else fall back to the id the search response just persisted for
              // THIS query (`savedQuery._id`) — the fresh-search case, where
              // `state.data` has no queryId yet.
              const reQuoteId =
                state?.data?.queryId ||
                dataratelist?.oceanRates?.data?.savedQuery?._id ||
                "";
              // Persist the previous query to redux so the search form prefills.
              // Shape matches what NewSearchRates + ShipmentRateForm read:
              // data.data.nodeData.searchquery (form) and data.nodeData
              // .searchquery.cargoType (tab-selection effect). Stamp queryId +
              // requestType so the re-search sends them in its payload.
              if (state?.data) {
                const searchquery = reQuoteId
                  ? { ...state.data, queryId: reQuoteId, requestType: 2 }
                  : state.data;
                const nodeData = { searchquery };
                dispatch(setOceanEditQuery({ data: { nodeData }, nodeData }));
              }
              // Only booking-history re-quotes (queryId already on state.data
              // with requestType 2) deep-link back via single_booking_data; a
              // fresh search carries its queryId purely through the redux
              // prefill stamped above, avoiding an extra fetch that could fail.
              const deepLinkId =
                state?.data?.queryId &&
                Number(state?.data?.requestType) === 2
                  ? state.data.queryId
                  : "";
              // A KAM or air_admin manages queries from the Queries list, not
              // the standalone search page — send them back there instead of
              // /search-rates, which isn't even in air_admin's allowed-route
              // list (see roleRoutes in utils/constant.jsx) and rendered
              // blank for that role.
              if (isKamOrAdmin) {
                navigate("/QueriesKAM");
                return;
              }
              // Customer / customer-admin manual-quote row opened from the
              // Queries list: back belongs to that list, not the search form —
              // same as the air results page (Newratelisting), whose back
              // button goes to /queries for a manual-quoted search. cargoTab=2
              // lands on the Ocean Cargo tab the row was clicked from.
              if (isFromQueriesManualQuote) {
                navigate("/queries?cargoTab=2");
                return;
              }
              navigate(
                deepLinkId
                  ? `/search-rates?_id=${deepLinkId}`
                  : "/search-rates",
                {
                  // fromOceanResults: NewSearchRates keeps the just-searched
                  // containers (weightType incl.) over the booking snapshot.
                  state: { searchBy: "Ocean Cargo", fromOceanResults: true },
                }
              );
            }}
          >
            <ArrowLeft size={15} />
            Edit Query
          </button>

          <div className="oqb-port">
            {qbOrigin && (
              <img
                className="oqb-flag"
                src={flagUrl(qbOrigin)}
                alt=""
                onError={(e) => (e.target.style.display = "none")}
              />
            )}
            <span className="oqb-code">{qbOrigin}</span>
            <span className="oqb-country">{qbCountry(qbOrigin)}</span>
          </div>

          <div className="oqb-route">
            <span className="oqb-ship">
              <Ship size={14} />
            </span>
          </div>

          <div className="oqb-port">
            {qbDest && (
              <img
                className="oqb-flag"
                src={flagUrl(qbDest)}
                alt=""
                onError={(e) => (e.target.style.display = "none")}
              />
            )}
            <span className="oqb-code">{qbDest}</span>
            <span className="oqb-country">{qbCountry(qbDest)}</span>
          </div>

          <div className="oqb-divider" />

          <div className="oqb-pills">
            {qbDate && (
              <span className="oqb-pill">
                <Calendar size={13} />
                {qbDate}
              </span>
            )}
            {qbCargo && (
              <span className="oqb-pill">
                <Package size={13} />
                {qbCargo}
              </span>
            )}
            {(() => {
              // Normalise each container to { label, weight } once so both the
              // visible columns and the "+N" tooltip render from the same shape.
              const items = qbContainerArr
                .map((c) => {
                  const qty = c?.quantity ?? c?.qty;
                  const type = c?.type || c?.containerType;
                  if (!type) return null;
                  const label = `${qty ? qty + " x " : ""}${type}`;
                  // kgs is the line total (legacy rows: per container × qty).
                  const weight = containerLineWeight(c);
                  return { label, weight };
                })
                .filter(Boolean);
              if (!items.length) return null;

              // Show at most 2 containers in the chip; any extra collapse into a
              // "+N" marker whose tooltip lists the remaining containers.
              const MAX_VISIBLE = 2;
              const visible = items.slice(0, MAX_VISIBLE);
              const hidden = items.slice(MAX_VISIBLE);

              return (
                <>
                  {visible.map((it, i) => (
                    <span className="oqb-pill oqb-ctr" key={i}>
                      <Ship size={13} />
                      <span className="oqb-ctr-type">{it.label}</span>
                      {it.weight > 0 && (
                        <span className="oqb-ctr-wgt">
                          {it.weight.toLocaleString()} KG
                        </span>
                      )}
                    </span>
                  ))}
                  {hidden.length > 0 && (
                    <Tooltip
                      arrow
                      placement="top"
                      title={
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 0.75,
                            py: 0.25,
                          }}
                        >
                          {hidden.map((it, i) => (
                            <Box
                              key={i}
                              sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                gap: 2,
                                fontSize: 12,
                              }}
                            >
                              <span style={{ fontWeight: 700 }}>{it.label}</span>
                              {it.weight > 0 && (
                                <span>{it.weight.toLocaleString()} KG</span>
                              )}
                            </Box>
                          ))}
                        </Box>
                      }
                      componentsProps={{
                        tooltip: {
                          sx: {
                            backgroundColor: "#FEF3E2",
                            color: "#7A4100",
                            border: "1px solid #F5A623",
                            borderRadius: "8px",
                            padding: "8px 12px",
                            maxWidth: 260,
                          },
                        },
                        arrow: { sx: { color: "#F5A623" } },
                      }}
                    >
                      <span className="oqb-pill oqb-ctr-more">
                        +{hidden.length}
                      </span>
                    </Tooltip>
                  )}
                </>
              );
            })()}
            {qbTotalWeight > 0 && (
              <span className="oqb-pill">
                <Weight size={13} />
                {qbTotalWeight.toLocaleString()} KG
              </span>
            )}
            <span className="oqb-pill oqb-local">
              Local Charges
              <ChevronDown size={13} />
            </span>
          </div>
        </div>

        {/* Loading skeleton (search in flight, or the deep link's query fetch
            that precedes it) */}
        {(oceanLoading || bootstrapping) && (
          <Box>
            <Paper
              elevation={0}
              sx={{
                display: "flex",
                gap: 1.25,
                p: 1.75,
                mb: 1.75,
                borderRadius: 2.5,
                border: "1px solid #E5E7EB",
                overflow: "hidden",
              }}
            >
              {Array.from({ length: 6 }).map((_, i) => (
                <Skeleton
                  key={i}
                  variant="rounded"
                  width={130}
                  height={46}
                  sx={{ flexShrink: 0 }}
                />
              ))}
            </Paper>
            <Paper
              elevation={0}
              sx={{
                borderRadius: 2.5,
                border: "1px solid #E5E7EB",
                overflowX: "auto",
              }}
            >
            {columnHeader}
            <Box sx={{ display: "flex", flexDirection: "column", minWidth: "1050px" }}>
              {Array.from({ length: 5 }).map((_, i) => (
                <Paper
                  key={i}
                  elevation={0}
                  square
                  sx={{
                    p: 2,
                    borderLeft: `4px solid ${AMBER}`,
                    borderBottom: i === 4 ? "none" : "1px solid #E5E7EB",
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <Skeleton variant="rounded" width={40} height={40} />
                    <Box sx={{ flex: 1 }}>
                      <Skeleton width="30%" height={16} />
                      <Skeleton width="45%" height={14} />
                    </Box>
                    <Skeleton variant="rounded" width={90} height={22} />
                    <Skeleton variant="rounded" width={110} height={40} />
                  </Box>
                </Paper>
              ))}
            </Box>
            </Paper>
          </Box>
        )}

        {/* No rates — themed ocean-freight empty state */}
        {!oceanLoading && !bootstrapping && !hasRates && (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              textAlign: "center",
              py: 8,
              px: 2,
            }}
          >
            <style>{`
              @keyframes oceanBob { 0%,100%{ transform: translateY(0) rotate(-3deg);} 50%{ transform: translateY(-10px) rotate(3deg);} }
              @keyframes oceanWave { 0%{ transform: translateX(0);} 100%{ transform: translateX(-150px);} }
            `}</style>
            <Box sx={{ position: "relative", width: 170, height: 130, mb: 2 }}>
              <Box
                sx={{
                  position: "absolute",
                  inset: 0,
                  m: "auto",
                  width: 130,
                  height: 130,
                  borderRadius: "50%",
                  background:
                    "radial-gradient(circle, #FFF4E0 0%, #FFFAF0 65%, transparent 100%)",
                }}
              />
              <Box
                sx={{
                  position: "absolute",
                  left: 0,
                  right: 0,
                  top: 6,
                  display: "flex",
                  justifyContent: "center",
                  color: AMBER_DARK,
                  animation: "oceanBob 3s ease-in-out infinite",
                }}
              >
                <Ship size={66} strokeWidth={1.5} />
              </Box>
              <Box
                sx={{
                  position: "absolute",
                  bottom: 8,
                  left: "50%",
                  transform: "translateX(-50%)",
                  width: 150,
                  height: 26,
                  overflow: "hidden",
                }}
              >
                <svg
                  width="300"
                  height="26"
                  viewBox="0 0 300 26"
                  style={{ animation: "oceanWave 2.5s linear infinite" }}
                >
                  <path
                    d="M0 14 Q 15 5, 30 14 T 60 14 T 90 14 T 120 14 T 150 14 T 180 14 T 210 14 T 240 14 T 270 14 T 300 14"
                    fill="none"
                    stroke={AMBER}
                    strokeWidth="3"
                    opacity="0.55"
                  />
                </svg>
              </Box>
            </Box>
            <Typography sx={{ fontSize: 18, fontWeight: 800, color: "#1F2430", mb: 0.5 }}>
              No rates available
            </Typography>
            <Typography
              sx={{ fontSize: 13.5, color: "#8B93A1", maxWidth: 430 }}
            >
              But your KAM has been informed about this query, and they will be
              sharing a quotation very soon. Stay tuned to the notification
              center for the quote.
            </Typography>
          </Box>
        )}

        {/* Rates found count — reflects the current filter; hidden when the
            filter matches nothing (the "no rates for this filter" card shows). */}
        {!oceanLoading && hasRates && stats.filtered > 0 && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.75,
              mb: 1.25,
              color: "#1F9254",
              fontSize: 13,
              fontWeight: 600,
            }}
          >
            <Check size={16} /> {stats.filtered} rates found
          </Box>
        )}

        {/* Date strip (MUI) */}
        {!oceanLoading &&
          ratesData &&
          Object.keys(dateGroups).length > 0 && (
            <Paper
              elevation={0}
              sx={{
                mb: 2,
                borderRadius: 2.5,
                border: "1px solid #E5E7EB",
                bgcolor: "#fff",
                overflow: "hidden",
              }}
            >
              {/* Scrollable cards */}
              <Box
                ref={dateStripRef}
                sx={{
                  display: "flex",
                  gap: 1.25,
                  p: 1.5,
                  overflowX: "auto",
                  "&::-webkit-scrollbar": { height: 9 },
                  "&::-webkit-scrollbar-thumb": {
                    background: "#C7CDD6",
                    borderRadius: 8,
                  },
                  "&::-webkit-scrollbar-thumb:hover": { background: "#AEB6C2" },
                  "&::-webkit-scrollbar-track": { background: "transparent" },
                }}
              >
                {Object.keys(dateGroups)
                  .sort()
                  .map((dateKey) => {
                    const group = dateGroups[dateKey];
                    const minPrice = Math.min(
                      ...group.items.map((it) =>
                        chargesTotal(it.offer.productPrice.charges)
                      )
                    );
                    const selected = currentDateKey === dateKey;
                    return (
                      <Box
                        key={dateKey}
                        onClick={() => setCurrentDateKey(dateKey)}
                        sx={{
                          position: "relative",
                          flexShrink: 0,
                          minWidth: 140,
                          p: 1.25,
                          borderRadius: 2,
                          cursor: "pointer",
                          border: selected
                            ? "1.5px solid #1F9254"
                            : "1.5px solid #E5E7EB",
                          bgcolor: selected ? "#F4FBF7" : "#fff",
                          transition: "all .15s",
                          "&:hover": {
                            borderColor: selected ? "#1F9254" : "#CBD5E1",
                          },
                        }}
                      >
                        {selected && (
                          <Box
                            sx={{
                              position: "absolute",
                              top: 7,
                              right: 8,
                              width: 7,
                              height: 7,
                              borderRadius: "50%",
                              bgcolor: "#1F9254",
                            }}
                          />
                        )}
                        <Typography
                          sx={{
                            fontSize: 12,
                            fontWeight: 700,
                            color: selected ? "#146C3A" : "#4B5563",
                          }}
                        >
                          {formatDateShort(dateKey)}
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: 11,
                            fontWeight: 600,
                            color: selected ? AMBER_DARK : "#8B93A1",
                            mt: 0.4,
                          }}
                        >
                          {group.items.length} options from $
                          {Math.round(minPrice).toLocaleString()}
                        </Typography>
                      </Box>
                    );
                  })}
              </Box>

              {/* Scroll arrows — only when the cards overflow the width */}
              {dateOverflow && (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    px: 1,
                    pb: 0.5,
                  }}
                >
                  <IconButton
                    size="small"
                    onClick={() => scrollDates(-1)}
                    sx={{ color: "#9AA2AD" }}
                  >
                    <ArrowLeft size={18} />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => scrollDates(1)}
                    sx={{ color: "#9AA2AD" }}
                  >
                    <ArrowRight size={18} />
                  </IconButton>
                </Box>
              )}
            </Paper>
          )}

        {/* Filter produced no matches — keep the filters visible and prompt the
            user to change them (the search itself did return rates). */}
        {!oceanLoading &&
          hasRates &&
          Object.keys(dateGroups).length === 0 && (
            <Paper
              elevation={0}
              sx={{
                borderRadius: 2.5,
                border: "1px solid #E5E7EB",
                bgcolor: "#fff",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                textAlign: "center",
                py: 6,
                px: 3,
              }}
            >
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: "50%",
                  bgcolor: "#FFF4E0",
                  color: AMBER_DARK,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  mb: 1.5,
                }}
              >
                <Filter size={22} />
              </Box>
              <Typography
                sx={{
                  fontSize: 15.5,
                  fontWeight: 800,
                  color: "#1F2430",
                  mb: 0.5,
                }}
              >
                No rates present for this filter
              </Typography>
              <Typography
                sx={{ fontSize: 13, color: "#8B93A1", maxWidth: 360 }}
              >
                Change the filter to get the data.
              </Typography>
            </Paper>
          )}

        {/* Connected header + rate list (MUI) */}
        {!oceanLoading &&
          hasRates &&
          currentDateKey &&
          dateGroups[currentDateKey] && (
            <Paper
              elevation={0}
              sx={{
                borderRadius: 2.5,
                border: "1px solid #E5E7EB",
                overflowX: "auto",
              }}
            >
              {columnHeader}
              <Box sx={{ display: "flex", flexDirection: "column" }}>
              {dateGroups[currentDateKey].items.map((item, idx) => {
                const { offer, schedule } = item;
                // Some carriers (e.g. HDMU) return an empty schedule location,
                // so fall back to the offer's origin/destination ports.
                const fromPort =
                  schedule.fromLocation?.unLocCode ||
                  offer.productOffer.originPort;
                const toPort =
                  schedule.toLocation?.unLocCode ||
                  offer.productOffer.destinationPort;
                const carrier = offer.productOffer.carrierName;
                const carrierCode = offer.productOffer.carrierScac;
                const cargoType = offer.productPrice.cargoType || "FAK";
                const isHazard = cargoType === "HAZARDOUS";
                const transit = offer.productPrice.transitTimeInDays
                  ? offer.productPrice.transitTimeInDays + "d"
                  : schedule.transitTime;
                const legs = schedule.scheduleDetails || [];
                const arrival = effectiveArrival(schedule, offer);
                // Fallback single leg when the carrier returns no scheduleDetails
                // so the expanded schedule isn't blank.
                const legRows =
                  legs.length > 0
                    ? legs
                    : [
                        {
                          fromLocation: {
                            unLocCode:
                              schedule.fromLocation?.unLocCode ||
                              offer.productOffer.originPort,
                            departure:
                              schedule.fromLocation?.departure ||
                              schedule.sailingDate,
                          },
                          toLocation: {
                            unLocCode:
                              schedule.toLocation?.unLocCode ||
                              offer.productOffer.destinationPort,
                            portName: schedule.toLocation?.portName || "",
                            arrival,
                          },
                          transport: { vessel: { name: "" }, voyageNumber: "" },
                        },
                      ];
                const viaText =
                  legs.length > 1
                    ? `${legs.length - 1} transshipment via ${
                        legs[0].toLocation.unLocCode
                      }`
                    : "Direct service";
                const total = chargesTotal(offer.productPrice.charges);
                const open = !!expandedRows[idx];
                return (
                  <Paper
                    key={idx}
                    elevation={0}
                    square
                    sx={{
                      borderLeft: `4px solid ${AMBER}`,
                      borderBottom:
                        idx === dateGroups[currentDateKey].items.length - 1
                          ? "none"
                          : "1px solid #E5E7EB",
                      // overflow: "hidden",
                    }}
                  >
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: {
                          xs: "1fr",
                          md: "28px minmax(120px, 1.9fr) minmax(118px, 1.5fr) minmax(132px, 1.1fr) minmax(132px, 1.1fr) minmax(40px, 0.45fr) minmax(94px, 0.65fr) minmax(124px, 1.1fr)",
                        },
                        gap: 1.5,
                        minWidth: "1050px",
                        alignItems: "center",
                        p: 2,
                        bgcolor: "#FFFCF5",
                      }}
                    >
                      <IconButton
                        size="small"
                        onClick={() => toggleDetails(idx)}
                        sx={{ color: "#8B93A1", justifySelf: "start" }}
                      >
                        {open ? (
                          <ChevronUp size={18} />
                        ) : (
                          <ChevronDown size={18} />
                        )}
                      </IconButton>

                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          minWidth: 0,
                        }}
                      >
                        <Box
                          sx={{
                            px: 0.75,
                            py: 0.25,
                            borderRadius: 1.25,
                            bgcolor: carrierColor(carrierCode),
                            color: "#fff",
                            fontSize: 9,
                            fontWeight: 800,
                            flexShrink: 0,
                            alignSelf: "flex-start",
                          }}
                        >
                          {carrierCode}
                        </Box>
                        <Box sx={{ minWidth: 0 }}>
                          <Typography
                            sx={{
                              fontSize: 10.5,
                              fontWeight: 700,
                              lineHeight: 1.25,
                            }}
                          >
                            {carrier}
                          </Typography>
                          <Chip
                            label={cargoType}
                            size="small"
                            sx={{
                              height: 18,
                              mt: 0.4,
                              fontSize: 10,
                              fontWeight: 800,
                              bgcolor: isHazard ? "#FBEAE8" : "#E7F6EC",
                              color: isHazard ? "#C43F34" : "#1F9254",
                            }}
                          />
                        </Box>
                      </Box>

                      <Box sx={{ minWidth: 0 }}>
                        <Typography sx={{ fontSize: 12.5, fontWeight: 700 }}>
                          {fromPort}{" "}
                          <span style={{ color: AMBER_DARK }}>→</span> {toPort}
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: 9.5,
                            color: "#8B93A1",
                            mt: 0.3,
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                        >
                          {viaText}
                        </Typography>
                      </Box>

                      <Box sx={{ minWidth: 0 }}>
                        <Typography sx={{ fontSize: 11, fontWeight: 600, whiteSpace: "nowrap" }}>
                          {formatDate(
                            schedule.fromLocation.departure ||
                              schedule.sailingDate
                          )}
                        </Typography>
                      </Box>

                      <Box sx={{ minWidth: 0 }}>
                        <Typography sx={{ fontSize: 11, fontWeight: 600, whiteSpace: "nowrap" }}>
                          {formatDate(arrival)}
                        </Typography>
                      </Box>

                      <Box>
                        <Typography
                          sx={{ fontSize: 11.5, fontWeight: 700, color: AMBER_DARK }}
                        >
                          {transit}
                        </Typography>
                      </Box>

                      <Box sx={{ minWidth: 0, pl: "21px" }}>
                        <Typography
                          sx={{
                            fontSize: 11.5,
                            fontWeight: 700,
                            letterSpacing: ".02em",
                            color: isHazard ? "#C43F34" : "#4B5563",
                          }}
                          noWrap
                        >
                          {cargoType}
                        </Typography>
                      </Box>

                      <Box sx={{ textAlign: "right" }}>
                        <Typography sx={{ fontSize: 14, fontWeight: 800 }}>
                          ${Math.round(total).toLocaleString()}
                        </Typography>
                        <Typography sx={{ fontSize: 10, color: "#8B93A1" }}>
                          ₹{Math.round(total * usdInrRate).toLocaleString()}
                        </Typography>
                        <Typography
                          sx={{ fontSize: 9.5, color: "#8B93A1", mb: 0.75 }}
                        >
                          Total rate
                        </Typography>
                        <Box
                          sx={{
                            display: "flex",
                            gap: 1.5,
                            justifyContent: "flex-end",
                            alignItems: "center",
                          }}
                        >
                          <IconButton
                            size="small"
                            onMouseEnter={(e) => openCharges(e, offer)}
                            onMouseLeave={scheduleCloseCharges}
                            sx={{
                              border: "1px solid #E5E7EB",
                              color: AMBER_DARK,
                              width: 30,
                              height: 30,
                              borderRadius: 2,
                              flexShrink: 0,
                            }}
                          >
                            <Info size={14} />
                          </IconButton>
                          <Tooltip
                            title={
                              !kycAccepted
                                ? "Booking is unavailable until your KYC verification is completed."
                                : ""
                            }
                            arrow
                            placement="top"
                            disableHoverListener={kycAccepted}
                            componentsProps={{
                              tooltip: {
                                sx: {
                                  backgroundColor: "#FEF3E2",
                                  color: "#7A4100",
                                  border: "1px solid #F5A623",
                                  fontSize: "12px",
                                  fontWeight: 500,
                                  borderRadius: "6px",
                                  padding: "6px 12px",
                                  maxWidth: 220,
                                },
                              },
                              arrow: { sx: { color: "#F5A623" } },
                            }}
                          >
                            <span>
                              <Button
                                variant="contained"
                                disableElevation
                                disabled={!kycAccepted}
                                onClick={() => handleBookClick(item)}
                                sx={{
                                  textTransform: "none",
                                  fontWeight: 800,
                                  fontSize: 11,
                                  borderRadius: 2,
                                  px: 1.75,
                                  py: 0.75,
                                  whiteSpace: "nowrap",
                                  minWidth: "auto",
                                  flexShrink: 0,
                                  background: `linear-gradient(135deg, ${AMBER}, ${AMBER_DARK})`,
                                  "&:hover": {
                                    background: `linear-gradient(135deg, ${AMBER}, ${AMBER_DARK})`,
                                    filter: "brightness(1.03)",
                                  },
                                }}
                              >
                                Book Now
                              </Button>
                            </span>
                          </Tooltip>
                        </Box>
                      </Box>
                    </Box>

                    <Collapse in={open} unmountOnExit>
                      <Box
                        sx={{
                          p: 2,
                          borderTop: "1px dashed #E5E7EB",
                          bgcolor: "#fff",
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "flex-start",
                            pb: 2,
                            overflowX: "auto",
                          }}
                        >
                          {legRows.map((d, li) => (
                            <React.Fragment key={li}>
                              <Box
                                sx={{
                                  display: "flex",
                                  flexDirection: "column",
                                  alignItems: "center",
                                  gap: 0.5,
                                  flexShrink: 0,
                                }}
                              >
                                <Box
                                  sx={{
                                    width: 11,
                                    height: 11,
                                    borderRadius: "50%",
                                    bgcolor: li > 0 ? "#fff" : AMBER,
                                    border: `2px solid ${AMBER}`,
                                  }}
                                />
                                <Typography sx={{ fontSize: 12, fontWeight: 700 }}>
                                  {d.fromLocation.unLocCode}
                                </Typography>
                              </Box>
                              <Box
                                sx={{
                                  flex: 1,
                                  minWidth: 70,
                                  display: "flex",
                                  flexDirection: "column",
                                  alignItems: "center",
                                  mx: 0.5,
                                }}
                              >
                                <Box
                                  sx={{
                                    width: "100%",
                                    borderTop: `2px solid ${AMBER}`,
                                    mt: "4.5px",
                                  }}
                                />
                                <Typography
                                  sx={{
                                    fontSize: 10.5,
                                    fontWeight: 700,
                                    color: AMBER_DARK,
                                  }}
                                >
                                  Leg {li + 1}
                                </Typography>
                              </Box>
                            </React.Fragment>
                          ))}
                          {legRows.length > 0 && (
                            <Box
                              sx={{
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                gap: 0.5,
                                flexShrink: 0,
                              }}
                            >
                              <Box
                                sx={{
                                  width: 11,
                                  height: 11,
                                  borderRadius: "50%",
                                  bgcolor: AMBER,
                                }}
                              />
                              <Typography sx={{ fontSize: 12, fontWeight: 700 }}>
                                {legRows[legRows.length - 1].toLocation.unLocCode}
                              </Typography>
                            </Box>
                          )}
                        </Box>

                        <Table size="small">
                          <TableHead>
                            <TableRow
                              sx={{
                                "& th": {
                                  fontSize: 10.5,
                                  fontWeight: 700,
                                  color: "#8B93A1",
                                  textTransform: "uppercase",
                                  bgcolor: "#F4F5F7",
                                  borderBottom: "none",
                                },
                              }}
                            >
                              <TableCell>Shipping Line</TableCell>
                              <TableCell>Route</TableCell>
                              <TableCell>Vessel / Voyage</TableCell>
                              <TableCell>Departure</TableCell>
                              <TableCell>Arrival</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {legRows.map((d, li) => (
                              <React.Fragment key={li}>
                                <TableRow>
                                  <TableCell
                                    sx={{
                                      fontSize: 11,
                                      fontWeight: 700,
                                      whiteSpace: "nowrap",
                                    }}
                                  >
                                    <Box
                                      sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 1,
                                      }}
                                    >
                                      <Box
                                        sx={{
                                          width: 22,
                                          height: 22,
                                          borderRadius: 1,
                                          bgcolor: carrierColor(carrierCode),
                                          color: "#fff",
                                          display: "flex",
                                          alignItems: "center",
                                          justifyContent: "center",
                                          fontSize: 8,
                                          fontWeight: 800,
                                        }}
                                      >
                                        {carrierCode.slice(0, 2)}
                                      </Box>
                                      {carrier}
                                    </Box>
                                  </TableCell>
                                  <TableCell sx={{ fontSize: 11, whiteSpace: "nowrap" }}>
                                    {d.fromLocation.unLocCode} →{" "}
                                    {d.toLocation.unLocCode}
                                  </TableCell>
                                  <TableCell sx={{ fontSize: 11, whiteSpace: "nowrap" }}>
                                    {d.transport?.vessel?.name || "-"} /{" "}
                                    {d.transport?.voyageNumber || "-"}
                                  </TableCell>
                                  <TableCell sx={{ fontSize: 11, whiteSpace: "nowrap" }}>
                                    {formatDate(d.fromLocation.departure)}
                                  </TableCell>
                                  <TableCell sx={{ fontSize: 11, whiteSpace: "nowrap" }}>
                                    {formatDate(d.toLocation.arrival)}
                                  </TableCell>
                                </TableRow>
                                {li < legRows.length - 1 && (
                                  <TableRow>
                                    <TableCell
                                      colSpan={5}
                                      sx={{
                                        textAlign: "center",
                                        fontSize: 12,
                                        fontWeight: 700,
                                        color: AMBER_DARK,
                                        bgcolor: "#FFF4E0",
                                      }}
                                    >
                                      Transshipment at {d.toLocation.unLocCode} (
                                      {d.toLocation.portName})
                                    </TableCell>
                                  </TableRow>
                                )}
                              </React.Fragment>
                            ))}
                          </TableBody>
                        </Table>
                      </Box>
                    </Collapse>
                  </Paper>
                );
              })}
            </Box>
            </Paper>
          )}

          </Box>
        </Box>
        {/* Charge breakdown popover ("i" button) */}
        <Popover
          open={Boolean(chargeAnchor)}
          anchorEl={chargeAnchor}
          onClose={closeCharges}
          // Hover popover: don't grab focus, and let the pointer pass through the
          // (transparent) backdrop so the page stays interactive. The Paper below
          // re-enables pointer events so it stays open (and scrollable) while
          // hovered; leaving it re-arms the close timer.
          disableAutoFocus
          disableEnforceFocus
          disableRestoreFocus
          sx={{ pointerEvents: "none" }}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
          PaperProps={{
            onMouseEnter: cancelCloseCharges,
            onMouseLeave: scheduleCloseCharges,
            sx: {
              pointerEvents: "auto",
              width: 360,
              // Never wider than the viewport on small screens.
              maxWidth: "calc(100vw - 32px)",
              // Cap to the viewport height; the body below scrolls so every
              // charge row is reachable no matter how many an offer has.
              maxHeight: "85vh",
              display: "flex",
              flexDirection: "column",
              borderRadius: 3,
              overflow: "hidden",
              boxShadow: "0 20px 50px rgba(31,36,48,0.3)",
            },
          }}
        >
          {chargeOffer &&
            (() => {
              const { sections, totals, grand } = computeSellBreakdown(
                chargeOffer.productPrice.charges
              );
              const cfg = {
                FREIGHT: { tag: "#2E6FF2", bg: "#EAF1FF", border: "#3c4e6e", title: "Freight Charges" },
                ORIGIN: { tag: "#1F9254", bg: "#E7F6EC", border: "#324e3e", title: "Origin Charges" },
                DESTINATION: { tag: "#1F9254", bg: "#E7F6EC", border: "#303b34", title: "Destination Charges" },
              };
              return (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    flex: 1,
                    minHeight: 0,
                      overflowY: "auto",

                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      // Header stays pinned while the body scrolls beneath it.
                      flexShrink: 0,
                      px: 2.25,
                      py: 1.75,
                      background: `linear-gradient(135deg, ${AMBER}, ${AMBER_DARK})`,
                      color: "#fff",
                      
                    }}
                  >
                    <Typography sx={{ fontSize: 13.5, fontWeight: 700 }}>
                      Charge Breakdown
                    </Typography>
                    <Typography sx={{ fontSize: 15, fontWeight: 800 }}>
                      ${fmtUsd(grand)}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      p: 2,
                      display: "flex",
                      flexDirection: "column",
                      gap: 1.75,
                      // Fill the remaining card height and scroll internally so
                      // all charge rows are shown instead of being clipped.
                      flex: 1,
                      minHeight: 0,
                      overflowY: "auto",
                      // Cross-browser thin, always-visible scrollbar so the
                      // overflowing charge rows are reachable (Firefox).
                      scrollbarWidth: "thin",
                      scrollbarColor: "#C7CDD6 transparent",
                      "&::-webkit-scrollbar": { width: 8 },
                      "&::-webkit-scrollbar-thumb": {
                        background: "#C7CDD6",
                        borderRadius: 8,
                      },
                      "&::-webkit-scrollbar-thumb:hover": { background: "#AEB6C2" },
                      "&::-webkit-scrollbar-track": { background: "transparent" },
                    }}
                  >
                    {["FREIGHT", "ORIGIN", "DESTINATION"].map((leg) => {
                      const containers = sections[leg];
                      if (!Object.keys(containers).length) return null;
                      const c = cfg[leg];
                      return (
                        <Box
                          key={leg}
                          sx={{
                            border: `1.5px solid ${c.tag}`,
                            borderRadius: 2,
                            overflow: "auto",
                            scrollbarWidth: "thin",
                      scrollbarColor: "#C7CDD6 transparent",
                          }}
                        >
                          {/* Amber "Required" heading bar (air-breakdown style):
                              tinted leg-colour header, amber "Required" chip and
                              leg-coloured title + leg total, over a white body. */}
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                              gap: 1,
                              px: 1.5,
                              py: 1,
                              bgcolor: c.bg,
                            }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                                minWidth: 0,
                              }}
                            >
                              <Box
                                sx={{
                                  bgcolor: "#EFA603",
                                  borderRadius: "4px",
                                  px: "6px",
                                  py: "1px",
                                  flexShrink: 0,
                                }}
                              >
                                <Typography
                                  sx={{ fontSize: 10, color: "#fff", fontWeight: 700 }}
                                >
                                  Required
                                </Typography>
                              </Box>
                              <Typography
                                noWrap
                                sx={{ fontSize: 14, fontWeight: 700, color: c.tag }}
                              >
                                {c.title}
                              </Typography>
                            </Box>
                            <Typography
                              sx={{
                                fontSize: 14,
                                fontWeight: 700,
                                flexShrink: 0,
                                color: c.tag,
                              }}
                            >
                              ${fmtUsd(totals[leg])}
                            </Typography>
                          </Box>
                          <Box sx={{ px: 1.5, py: 1, bgcolor: "#fff" }}>
                          {Object.entries(containers).map(([container, list]) =>
                            list.map((ch, i) => {
                              const rateStr =
                                ch.rateDisp != null
                                  ? `${fmtUsd(ch.rateDisp)} ${ch.rateCurrency}`
                                  : "";
                              return (
                                <Box
                                  key={container + i}
                                  sx={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    gap: 1,
                                    py: 0.5,
                                  }}
                                >
                                  <Box sx={{ minWidth: 0 }}>
                                    <Typography
                                      sx={{ fontSize: 12, color: "#4B5563" }}
                                    >
                                      {ch.description}
                                    </Typography>
                                    <Typography
                                      sx={{ fontSize: 10, color: "#8B93A1" }}
                                    >
                                      {container !== "General"
                                        ? `${container} ×${ch.qty || 1} @ ${rateStr}`
                                        : rateStr}
                                    </Typography>
                                  </Box>
                                  <Typography
                                    sx={{
                                      fontSize: 12,
                                      fontWeight: 700,
                                      whiteSpace: "nowrap",
                                    }}
                                  >
                                    ${fmtUsd(ch.amountUsdDisp)}
                                  </Typography>
                                </Box>
                              );
                            })
                          )}
                          </Box>
                        </Box>
                      );
                    })}
                  </Box>
                </Box>
              );
            })()}
        </Popover>

        {/* Booking Summary modal */}
        {showModal &&
          currentBooking &&
          (() => {
            // Header / shipment facts, read from the same offer + query keys
            // the listing row and the booking payload already use.
            const bmOffer = currentBooking.offer;
            const bmPrice = bmOffer.productPrice || {};
            const bmSchedule = currentBooking.schedule || {};
            const bmFrom =
              bmSchedule.fromLocation?.unLocCode || bmOffer.productOffer.originPort;
            const bmTo =
              bmSchedule.toLocation?.unLocCode ||
              bmOffer.productOffer.destinationPort;
            const bmCargo = bmPrice.cargoType || "FAK";
            // "Rate valid till": the offer's own expiry when the source
            // sent one, else the quote expiry stored on the query itself
            // (what OceanCargoDetails reads to mark a quote expired).
            // Rate validity is a manual-quote concept here: a KAM sets the
            // quote expiry, and only those offers are marked isManualQuote
            // by buildManualQuoteOffersShape. A Freightify rate gets no
            // chip, even if some expiry-ish field happens to be present.
            const bmIsManualQuote = !!bmPrice.isManualQuote;
            const bmValidTill = bmIsManualQuote
              ? bmFormatValidTill(
                  bmFindExpiry([
                    bmPrice,
                    bmOffer,
                    bmSchedule,
                    savedQuery,
                    oceanRates?.data,
                    state,
                  ])
                )
              : "";
            const bmTransitText = bmPrice.transitTimeInDays
              ? `${bmPrice.transitTimeInDays} days`
              : bmSchedule.transitTime || "";
            // Equipment line — the containers the user actually searched for
            // (same list the query bar renders), falling back to the container
            // types this quote's charges are priced against.
            const bmEquipment = (() => {
              const fromQuery = qbContainerArr
                .map((c) => {
                  const type = c?.type || c?.containerType;
                  if (!type) return null;
                  const qty = Number(c?.quantity ?? c?.qty) || 0;
                  return qty ? `${qty} × ${type}` : type;
                })
                .filter(Boolean);
              if (fromQuery.length) return fromQuery.join(", ");
              return [
                ...new Set(
                  (bmPrice.charges || [])
                    .filter(
                      (c) =>
                        c.rateType === "SELL" &&
                        c.containerType &&
                        c.containerType !== "General"
                    )
                    .map((c) => c.containerType)
                ),
              ].join(", ");
            })();
            const bmIncoterm =
              q?.paymentTerms?.incoTerms || savedQuery?.paymentTerms?.incoTerms || "";
            // Gross weight across every container in the query — the same
            // weightType-aware sum the query bar's own weight pill shows.
            const bmWeightText =
              qbTotalWeight > 0 ? `${qbTotalWeight.toLocaleString()} KG` : "";
            // Master bills the user asked for. `Noofbl` rides flat on the
            // submitted query; savedQuery is the backend copy of the same
            // field, used when this page was opened from a re-quote.
            // Same numeric selection the merge above uses, so a "0" on the
            // submitted query falls through to savedQuery rather than to 1.
            const bmNoOfBl =
              [q?.Noofbl, savedQuery?.Noofbl].map(Number).find((n) => n > 0) ?? 1;
            const bmBlText =
              bmNoOfBl > 0 ? `${bmNoOfBl} MBL${bmNoOfBl > 1 ? "s" : ""}` : "";
            // Header chips — every shipment fact the header carries, on one
            // line beside the route: the carrier and the two facts that used to
            // read as plain text, plus the three the right rail's Shipment card
            // used to hold before it was dropped.
            const bmChips = [
              { icon: Ship, label: bmOffer.productOffer.carrierName },
              { icon: Package, label: bmEquipment },
              { icon: Weight, label: bmWeightText },
              { icon: Clock, label: bmTransitText && `${bmTransitText} transit` },
              { icon: Info, label: bmCargo },
              { icon: FileText, label: bmIncoterm },
              { icon: Files, label: bmBlText },
              { icon: Calendar, label: qbDate },
            ].filter((c) => !!c.label);
            // Every money figure in this modal to 2 decimals — a row's paise
            // are visible instead of being rounded away into a total that then
            // looks off by a rupee.
            const bmNum = (n) =>
              n == null
                ? "—"
                : (Number(n) || 0).toLocaleString(undefined, {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  });
            // A figure with its currency underneath, right-aligned in the cell.
            const bmCellStack = {
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-end",
              gap: "2px",
            };
            const bmCellUnit = {
              fontSize: 10.5,
              fontWeight: 600,
              letterSpacing: ".04em",
              color: BM.faint,
            };
            // What Confirm will actually charge — mirrors confirmBooking's
            // amountUsdToCharge: the full new total for a new booking, only the
            // increase for an update, nothing when the update's new total is at
            // or below what was already paid.
            const bmPayableUsd = isUpdateFlow
              ? noExtraPayment
                ? 0
                : differenceUsd
              : Number(grandTotal) || 0;
            // The rows shown above the net figure, in the order the card
            // stacks them; the update-flow pair only appears on an update.
            const bmPayableRows = [
              {
                label: "Taxable amount",
                usd: Number(gstSummary?.taxableAmount || 0),
              },
              {
                label: "GST",
                usd: Number(gstSummary?.gstAmount || 0),
              },
              // Update flow: this booking's full total vs what was already
              // paid, so the net figure reads as the difference between them.
              ...(isUpdateFlow
                ? [
                    { label: "This booking total", usd: newUsd, divider: true },
                    { label: "Previously paid", usd: prevUsd, minus: true },
                  ]
                : []),
            ];
            // A manual quote carries its terms in `remarks`, and this page has
            // always gated Confirm on the customer ticking the agreement.
            const bmTerms = bmPrice.remarks;
            // The full tab strip, same as the Freightify results page. A
            // manual quote only really has its sailing and its remarks, so
            // the other panels fall back to their own empty states rather
            // than disappearing.
            const bmDndConditions = bmPrice.dndConditions || [];
            const bmInclusions = bmPrice.inclusionRemarks;
            const bmTabs = [
              { key: "schedule", label: "Schedules" },
              { key: "freedays", label: "Free days" },
              { key: "remarks", label: "Remarks & inclusions" },
              { key: "terms", label: "Terms & conditions" },
            ];
            const bmActiveTab = bmTabs.some((t) => t.key === summaryTab)
              ? summaryTab
              : bmTabs[0]?.key;
            const bmConfirmBlocked = isBookingInFlight || (bmTerms && !agreeTerms);
            const bmEmptyText = {
              fontSize: 13,
              color: BM.muted,
              fontStyle: "italic",
            };

            return (
              <Box
                onClick={closeBookingModal}
                sx={{
                  position: "fixed",
                  inset: 0,
                  zIndex: 1300,
                  background: "rgba(16,24,40,.55)",
                  display: "flex",
                  alignItems: "flex-start",
                  justifyContent: "center",
                  px: { xs: 1.5, md: 3 },
                  py: { xs: 2, md: 4 },
                  overflowY: "auto",
                }}
              >
                <Box
                  onClick={(e) => e.stopPropagation()}
                  role="dialog"
                  aria-modal="true"
                  aria-label={`Booking summary, ${bmFrom} to ${bmTo}, ${bmOffer.productOffer.carrierName}`}
                  sx={{
                    width: "100%",
                    maxWidth: 1120,
                    background: "#FFFFFF",
                    borderRadius: "20px",
                    boxShadow:
                      "0 32px 80px -24px rgba(16,24,40,.35), 0 0 0 1px rgba(16,24,40,.06)",
                    overflow: "hidden",
                    display: "flex",
                    flexDirection: "column",
                    maxHeight: "calc(100vh - 64px)",
                  }}
                >
                  {/* header */}
                  <Box
                    sx={{
                      px: { xs: 2.5, md: 4 },
                      pt: "22px",
                      pb: "20px",
                      borderBottom: `1px solid ${BM.hair}`,
                      display: "flex",
                      alignItems: "flex-start",
                      justifyContent: "space-between",
                      gap: 3,
                      flexShrink: 0,
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 1.25,
                        flex: 1,
                        minWidth: 0,
                      }}
                    >
                      <Stack direction="row" alignItems="center" gap={1.25} flexWrap="wrap">
                        <BmTag bg={BM.amberBg} bd={BM.amberBd} color={BM.amber}>
                          Booking Summary
                        </BmTag>
                        {bmValidTill && (
                          <BmTag
                            bg={BM.greenBg}
                            bd={BM.greenBd}
                            color={BM.greenTx}
                          >
                            Rate valid till {bmValidTill}
                          </BmTag>
                        )}
                      </Stack>

                      <Stack direction="row" alignItems="center" gap={1.25} flexWrap="wrap">
                        <Stack direction="row" alignItems="center" gap={1.25}>
                          <Typography
                            sx={{
                              fontSize: 26,
                              fontWeight: 800,
                              color: BM.ink,
                              letterSpacing: "-.02em",
                            }}
                          >
                            {bmFrom}
                          </Typography>
                          <ArrowRight size={24} color={AMBER} />
                          <Typography
                            sx={{
                              fontSize: 26,
                              fontWeight: 800,
                              color: BM.ink,
                              letterSpacing: "-.02em",
                            }}
                          >
                            {bmTo}
                          </Typography>
                        </Stack>
                        <Box sx={{ width: "1px", height: 22, background: BM.rule }} />
                        {bmChips.map(({ icon: ChipIcon, label }) => (
                          <Stack
                            key={label}
                            direction="row"
                            alignItems="center"
                            gap={0.75}
                            sx={{
                              px: "12px",
                              py: "6px",
                              borderRadius: "999px",
                              background: BM.amberBg,
                              border: `1px solid ${AMBER}`,
                              color: AMBER_DARK,
                              fontSize: 12,
                              fontWeight: 700,
                              whiteSpace: "nowrap",
                            }}
                          >
                            <ChipIcon size={13} />
                            {label}
                          </Stack>
                        ))}
                      </Stack>
                    </Box>

                    <IconButton
                      aria-label="Close"
                      onClick={closeBookingModal}
                      sx={{
                        flexShrink: 0,
                        width: 38,
                        height: 38,
                        borderRadius: "10px",
                        border: `1px solid ${BM.rule}`,
                        background: "#FFFFFF",
                        color: BM.muted,
                        "&:hover": { background: "#F4F6F8", color: BM.ink },
                      }}
                    >
                      <X size={17} />
                    </IconButton>
                  </Box>

                  {/* body */}
                  <Box sx={{ flex: 1, overflowY: "auto", background: BM.panel }}>
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: { xs: "1fr", lg: "minmax(0,1fr) 300px" },
                        alignItems: "start",
                        columnGap: "18px",
                        rowGap: "22px",
                        px: { xs: 1.5, md: 2 },
                        pt: "20px",
                        pb: "28px",
                        minWidth: 0,
                      }}
                    >
                      {/* charge breakdown — first column of the grid */}
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          gap: "22px",
                          minWidth: 0,
                        }}
                      >
                        {/* charges — one card per ORIGIN / FREIGHT / DESTINATION leg */}
                        {chargeBlocks.length === 0 && (
                          <BmCard sx={{ px: "20px", py: "18px" }}>
                            <Typography sx={bmEmptyText}>
                              Charge breakdown is not available for this quote.
                            </Typography>
                          </BmCard>
                        )}
                        {chargeBlocks.map((block) => (
                          <BmCard key={block.legType} sx={{ overflow: "hidden" }}>
                            <Stack
                              direction="row"
                              alignItems="center"
                              justifyContent="space-between"
                              gap={1.5}
                              flexWrap="wrap"
                              sx={{
                                px: "18px",
                                py: "14px",
                                borderBottom: `1px solid ${BM.hair}`,
                              }}
                            >
                              <Stack direction="row" alignItems="center" gap={1.25}>
                                <BmIconBadge bg={BM.amberBg}>
                                  {block.legType === "FREIGHT" ? (
                                    <Ship size={16} color={BM.amber} />
                                  ) : (
                                    <Package size={16} color={BM.amber} />
                                  )}
                                </BmIconBadge>
                                <Typography
                                  sx={{ fontSize: 15, fontWeight: 700, color: BM.ink }}
                                >
                                  {BM_LEG_TITLE[block.legType] || block.legType}
                                </Typography>
                                <Box
                                  component="span"
                                  sx={{
                                    fontSize: 12,
                                    fontWeight: 600,
                                    color: BM.muted,
                                    background: BM.hair2,
                                    px: 1,
                                    py: "3px",
                                    borderRadius: "20px",
                                  }}
                                >
                                  {block.rows.length} line items
                                </Box>
                              </Stack>
                              <Stack
                                direction="row"
                                alignItems="center"
                                gap={0.75}
                                sx={{
                                  px: "12px",
                                  py: "6px",
                                  borderRadius: "999px",
                                  background: BM.amberBg,
                                  border: `1px solid ${AMBER}`,
                                  color: AMBER_DARK,
                                  fontSize: 12,
                                  fontWeight: 700,
                                  whiteSpace: "nowrap",
                                }}
                              >
                                <DollarSign size={13} />
                                Exchange rate 1 USD = ₹{usdInrRate.toLocaleString()}
                              </Stack>
                            </Stack>

                            <Box sx={{ overflowX: "auto" }}>
                              <Table sx={{ width: "100%" }} size="small">
                                <TableHead>
                                  <TableRow>
                                    <TableCell sx={{ ...bmTh, pl: "16px", pr: "8px" }}>
                                      Description
                                    </TableCell>
                                    <TableCell sx={{ ...bmTh, px: "8px" }}>Qty</TableCell>
                                    <TableCell align="right" sx={{ ...bmTh, px: "8px" }}>
                                      Rate
                                    </TableCell>
                                    <TableCell align="right" sx={{ ...bmTh, px: "8px" }}>
                                      Amount
                                    </TableCell>
                                    <TableCell align="right" sx={{ ...bmTh, px: "8px" }}>
                                      USD amount
                                    </TableCell>
                                    <TableCell align="right" sx={{ ...bmTh, pl: "8px", pr: "16px" }}>
                                      INR amount
                                    </TableCell>
                                  </TableRow>
                                </TableHead>

                                <TableBody>
                                  {block.rows.map((row) => (
                                    <TableRow
                                      key={row.key}
                                      sx={{
                                        "& td": {
                                          borderBottom: `1px solid ${BM.hair2}`,
                                          fontSize: 13,
                                        },
                                        "&:hover": { background: "#FBFCFD" },
                                      }}
                                    >
                                      <TableCell
                                        sx={{ pl: "16px", pr: "8px", py: "13px", minWidth: 170 }}
                                      >
                                        <Box
                                          sx={{
                                            display: "flex",
                                            flexDirection: "column",
                                            gap: "3px",
                                          }}
                                        >
                                          <Typography
                                            sx={{
                                              fontSize: 13.5,
                                              fontWeight: 600,
                                              color: BM.ink,
                                            }}
                                          >
                                            {row.description}
                                          </Typography>
                                          <Typography
                                            sx={{
                                              fontSize: 11.5,
                                              color: BM.faint,
                                              fontWeight: 500,
                                            }}
                                          >
                                            {row.container}
                                            {row.chargeCode ? ` · ${row.chargeCode}` : ""}
                                          </Typography>
                                        </Box>
                                      </TableCell>
                                      <TableCell
                                        sx={{
                                          px: "8px",
                                          py: "13px",
                                          color: BM.body,
                                          fontWeight: 600,
                                        }}
                                      >
                                        {row.qty || "-"}
                                      </TableCell>
                                      <TableCell
                                        align="right"
                                        sx={{
                                          px: "8px",
                                          py: "13px",
                                          color: BM.body,
                                          fontFamily: BM_MONO,
                                          fontWeight: 500,
                                        }}
                                      >
                                        <Box sx={bmCellStack}>
                                          <Box component="span">{bmNum(row.rate)}</Box>
                                          {/* the currency this rate is quoted
                                              in — per row, not per table */}
                                          <Box component="span" sx={bmCellUnit}>
                                            {row.rateCurrency || "—"}
                                          </Box>
                                        </Box>
                                      </TableCell>
                                      <TableCell
                                        align="right"
                                        sx={{
                                          px: "8px",
                                          py: "13px",
                                          color: BM.body,
                                          fontFamily: BM_MONO,
                                          fontWeight: 600,
                                        }}
                                      >
                                        <Box sx={bmCellStack}>
                                          <Box component="span">{bmNum(row.amount)}</Box>
                                          {/* the line total is quoted in the
                                              same currency as its rate */}
                                          <Box component="span" sx={bmCellUnit}>
                                            {row.rateCurrency || "—"}
                                          </Box>
                                        </Box>
                                      </TableCell>
                                      <TableCell
                                        align="right"
                                        sx={{
                                          px: "8px",
                                          py: "13px",
                                          color: BM.body,
                                          fontFamily: BM_MONO,
                                        }}
                                      >
                                        {row.usdAmount == null
                                          ? "—"
                                          : `$${bmNum(row.usdAmount)}`}
                                      </TableCell>
                                      <TableCell
                                        align="right"
                                        sx={{
                                          pl: "8px",
                                          pr: "16px",
                                          py: "13px",
                                          fontFamily: BM_MONO,
                                          fontWeight: 600,
                                          color: BM.ink,
                                          fontSize: "13.5px !important",
                                        }}
                                      >
                                        {row.inrAmount == null
                                          ? "—"
                                          : `₹${bmNum(row.inrAmount)}`}
                                      </TableCell>
                                    </TableRow>
                                  ))}
                                  {/* Column totals — every figure above it,
                                      summed. Rate and Amount are added up as
                                      quoted, so they only read as one number
                                      when every line shares a currency. */}
                                  {(() => {
                                    const sum = (k) =>
                                      block.rows.reduce(
                                        (s, r) => s + (Number(r[k]) || 0),
                                        0
                                      );
                                    const tdTotal = {
                                      px: "8px",
                                      py: "14px",
                                      fontSize: 13.5,
                                      fontFamily: BM_MONO,
                                      fontWeight: 800,
                                      color: BM.ink,
                                      background: BM.head,
                                      borderTop: `1px solid ${BM.rule}`,
                                      borderBottom: "none",
                                    };
                                    return (
                                      <TableRow>
                                        <TableCell
                                          sx={{
                                            ...tdTotal,
                                            pl: "16px",
                                            pr: "8px",
                                            fontFamily: "inherit",
                                            letterSpacing: ".02em",
                                          }}
                                        >
                                          Total
                                        </TableCell>
                                        <TableCell sx={tdTotal} />
                                        <TableCell align="right" sx={tdTotal}>
                                          {bmNum(sum("rate"))}
                                        </TableCell>
                                        <TableCell align="right" sx={tdTotal}>
                                          {bmNum(sum("amount"))}
                                        </TableCell>
                                        <TableCell align="right" sx={tdTotal}>
                                          ${bmNum(sum("usdAmount"))}
                                        </TableCell>
                                        <TableCell
                                          align="right"
                                          sx={{ ...tdTotal, pl: "8px", pr: "16px" }}
                                        >
                                          ₹{bmNum(sum("inrAmount"))}
                                        </TableCell>
                                      </TableRow>
                                    );
                                  })()}
                                </TableBody>
                              </Table>
                            </Box>
                          </BmCard>
                        ))}
                      </Box>

                      {/* payable amount — rides alongside the charge breakdown.
                          Scrolls with the page rather than pinning, so the tabs
                          below it are never covered. */}
                      <Box sx={{ minWidth: 0 }}>
                        <BmCard sx={{ overflow: "hidden", borderColor: BM.amberBd }}>
                          <Box
                            sx={{
                              px: 2.5,
                              py: "16px",
                              background: BM.head,
                              borderBottom: `1px solid ${BM.amberBd}`,
                            }}
                          >
                            <BmEyebrow size={12} color={BM.amber}>
                              Payable amount
                            </BmEyebrow>
                          </Box>

                          <Box
                            sx={{
                              px: 2.5,
                              py: "18px",
                              display: "flex",
                              flexDirection: "column",
                              gap: "16px",
                            }}
                          >
                            {bmPayableRows.map((row) => (
                              <React.Fragment key={row.label}>
                                {row.divider && (
                                  <Box sx={{ borderTop: `1px dashed ${BM.rule}` }} />
                                )}
                                <Stack
                                  direction="row"
                                  alignItems="baseline"
                                  justifyContent="space-between"
                                  gap={1.5}
                                >
                                  <Box sx={{ minWidth: 0 }}>
                                    <Typography sx={{ fontSize: 14, color: BM.body }}>
                                      {row.label}
                                    </Typography>
                                  </Box>
                                  <Box sx={{ textAlign: "right", flexShrink: 0 }}>
                                    <Typography
                                      sx={{
                                        fontSize: 17,
                                        fontWeight: 500,
                                        color: BM.ink,
                                        fontFamily: BM_MONO,
                                      }}
                                    >
                                      {row.minus ? "− " : ""}
                                      {/* Same ₹ treatment the net figure uses —
                                          the mono stack has no ₹ glyph, so drawn
                                          from the sans stack it matches the
                                          digits' height and weight instead of
                                          coming out smaller. */}
                                      <Box
                                        component="span"
                                        sx={{
                                          fontFamily: BM_SANS,
                                          fontSize: "1.08em",
                                          fontWeight: "inherit",
                                          mr: "1px",
                                        }}
                                      >
                                        ₹
                                      </Box>
                                      {bmNum(row.usd * usdInrRate)}
                                    </Typography>
                                    <Typography
                                      sx={{
                                        fontSize: 12.5,
                                        color: BM.faint,
                                        fontFamily: BM_MONO,
                                      }}
                                    >
                                      ${bmNum(row.usd)}
                                    </Typography>
                                  </Box>
                                </Stack>
                              </React.Fragment>
                            ))}
                          </Box>

                          <Box
                            sx={{
                              p: 2.5,
                              background: `linear-gradient(135deg,${BM.amberBg} 0%,#FFE6BC 100%)`,
                              borderTop: `1px solid ${BM.amberBd}`,
                              display: "flex",
                              flexDirection: "column",
                              gap: 1,
                            }}
                          >
                            <Stack
                              direction="row"
                              alignItems="center"
                              justifyContent="space-between"
                              gap={1}
                            >
                              <BmEyebrow size={12} ls=".09em" color={BM.amber}>
                                {isUpdateFlow
                                  ? "Amount to be paid"
                                  : "Net amount (payable)"}
                              </BmEyebrow>
                              <Box
                                component="span"
                                sx={{
                                  fontSize: 11,
                                  fontWeight: 700,
                                  color: AMBER_DARK,
                                  background: "#FFFFFF",
                                  border: `1px solid ${AMBER}`,
                                  px: "8px",
                                  py: "3px",
                                  borderRadius: "6px",
                                }}
                              >
                                INR
                              </Box>
                            </Stack>
                            <Typography
                              sx={{
                                fontSize: 24,
                                // Same weight the breakdown rows above use, so
                                // the net figure reads as one family with the
                                // subtotals it sums.
                                fontWeight: 500,
                                color: BM.ink,
                                letterSpacing: "-.03em",
                                lineHeight: 1.15,
                                fontFamily: BM_MONO,
                                wordBreak: "break-word",
                              }}
                            >
                              {/* No ₹ glyph here — the INR chip beside the
                                  eyebrow already names the currency. */}
                              {bmNum(bmPayableUsd * usdInrRate)}
                            </Typography>
                            <Typography
                              sx={{
                                fontSize: 13,
                                color: BM.muted,
                                fontFamily: BM_MONO,
                              }}
                            >
                              ${bmNum(bmPayableUsd)} @ {usdInrRate}
                            </Typography>
                            {isUpdateFlow && (
                              <Typography
                                sx={{ fontSize: 12.5, color: BM.body, lineHeight: 1.5 }}
                              >
                                {noExtraPayment
                                  ? "No additional payment required — this booking will be adjusted from your previously paid amount."
                                  : "Only the difference over your previously paid amount will be charged."}
                              </Typography>
                            )}
                          </Box>
                        </BmCard>
                      </Box>

                      {/* detail tabs and the agreement card — full width below
                          the charges / payable row */}
                      <Box
                        sx={{
                          gridColumn: { lg: "1 / -1" },
                          display: "flex",
                          flexDirection: "column",
                          gap: "22px",
                          minWidth: 0,
                        }}
                      >
                        {/* Detail tabs, same strip as the Freightify results
                            page. The quote's remarks also stay in their own
                            card below, since Confirm is gated on accepting
                            them and that must not sit behind a tab click. */}
                        {true && (
                          <Box
                            sx={{ display: "flex", flexDirection: "column", gap: 1.75 }}
                          >
                            {/* Tab strip — spans the full width of the modal,
                                each tab taking an equal share of it. */}
                            {true && (
                              <Box
                                role="tablist"
                                sx={{
                                  display: "flex",
                                  gap: "4px",
                                  p: "4px",
                                  background: BM.hair,
                                  borderRadius: "11px",
                                  width: "100%",
                                }}
                              >
                                {bmTabs.map((t) => (
                                  <BmSeg
                                    key={t.key}
                                    role="tab"
                                    aria-selected={bmActiveTab === t.key}
                                    active={bmActiveTab === t.key}
                                    onClick={() => setSummaryTab(t.key)}
                                    sx={{ flex: 1, minWidth: 0 }}
                                  >
                                    {t.label}
                                  </BmSeg>
                                ))}
                              </Box>
                            )}

                            {bmActiveTab === "schedule" &&
                              (() => {
                                const offer = currentBooking.offer;
                                const scheduleOptions =
                                  offer.productPrice.scheduleOptions?.length > 0
                                    ? offer.productPrice.scheduleOptions
                                    : [currentBooking.schedule || {}];
                                // A sailing is ALWAYS selected — the first one
                                // until the user picks another.
                                const activeIdx =
                                  selectedScheduleIdx !== null &&
                                  selectedScheduleIdx < scheduleOptions.length
                                    ? selectedScheduleIdx
                                    : 0;
                                const schedule = scheduleOptions[activeIdx] || {};
                                const legs = schedule?.scheduleDetails || [];
                                const arrival = effectiveArrival(schedule, offer);
                                const legRows =
                                  legs.length > 0
                                    ? legs
                                    : [
                                        {
                                          fromLocation: {
                                            unLocCode:
                                              schedule.fromLocation?.unLocCode ||
                                              offer.productOffer.originPort,
                                            portName:
                                              schedule.fromLocation?.portName || "",
                                            departure:
                                              schedule.fromLocation?.departure ||
                                              schedule.sailingDate,
                                          },
                                          toLocation: {
                                            unLocCode:
                                              schedule.toLocation?.unLocCode ||
                                              offer.productOffer.destinationPort,
                                            portName:
                                              schedule.toLocation?.portName || "",
                                            arrival,
                                          },
                                          transport: {
                                            vessel: { name: "" },
                                            voyageNumber: "",
                                          },
                                          serviceName: "",
                                        },
                                      ];

                                // Each leg flattens into a departure event and an
                                // arrival event, so multi-leg routing reads as one
                                // continuous timeline.
                                const events = [];
                                legRows.forEach((leg, idx) => {
                                  const isLast = idx === legRows.length - 1;
                                  events.push({
                                    date: leg.fromLocation?.departure,
                                    port:
                                      leg.fromLocation?.portName ||
                                      leg.fromLocation?.unLocCode,
                                    kind: "Departure",
                                    service: leg.serviceName,
                                    vessel: leg.transport?.vessel?.name,
                                    voyage: leg.transport?.voyageNumber,
                                    showDetails: true,
                                  });
                                  events.push({
                                    date:
                                      leg.toLocation?.arrival ||
                                      (isLast ? arrival : ""),
                                    port:
                                      leg.toLocation?.portName ||
                                      leg.toLocation?.unLocCode,
                                    kind: isLast ? "Arrival" : "Transshipment",
                                    showDetails: false,
                                  });
                                });

                                const kindColors = {
                                  Departure: [BM.indigoBg, BM.indigo],
                                  Transshipment: [BM.amberBg, BM.amber],
                                  Arrival: [BM.greenBg, BM.greenTx],
                                };
                                const fmtDay = (dt) =>
                                  dt.toLocaleDateString("en-GB", {
                                    day: "2-digit",
                                    month: "short",
                                    year: "numeric",
                                  });

                                return (
                                  <BmCard
                                    sx={{
                                      display: "grid",
                                      gridTemplateColumns: {
                                        xs: "1fr",
                                        md: "232px minmax(0,1fr)",
                                      },
                                    }}
                                  >
                                    <Box
                                      sx={{
                                        p: 2,
                                        borderRight: {
                                          xs: "none",
                                          md: `1px solid ${BM.hair}`,
                                        },
                                        borderBottom: {
                                          xs: `1px solid ${BM.hair}`,
                                          md: "none",
                                        },
                                        display: "flex",
                                        flexDirection: "column",
                                        gap: 1.25,
                                      }}
                                    >
                                      <BmEyebrow>Sailing option</BmEyebrow>
                                      {scheduleOptions.map((opt, idx) => {
                                        const dep =
                                          opt.fromLocation?.departure ||
                                          opt.sailingDate;
                                        const arr =
                                          opt.toLocation?.arrival ||
                                          opt.scheduleDetails?.[
                                            opt.scheduleDetails.length - 1
                                          ]?.toLocation?.arrival ||
                                          "";
                                        const depDate = dep ? new Date(dep) : null;
                                        const arrDate = arr ? new Date(arr) : null;
                                        const validDep =
                                          depDate && !isNaN(depDate.getTime());
                                        const validArr =
                                          arrDate && !isNaN(arrDate.getTime());
                                        const isActive = idx === activeIdx;
                                        const hops = opt.scheduleDetails?.length || 1;
                                        return (
                                          <Box
                                            key={idx}
                                            component="button"
                                            type="button"
                                            onClick={() => setSelectedScheduleIdx(idx)}
                                            sx={{
                                              textAlign: "left",
                                              cursor: "pointer",
                                              fontFamily: "inherit",
                                              border: isActive
                                                ? `1.5px solid ${BM.green}`
                                                : `1.5px solid ${BM.line}`,
                                              background: isActive
                                                ? "#F6FEF9"
                                                : "#FFFFFF",
                                              borderRadius: "11px",
                                              p: "13px",
                                              display: "flex",
                                              flexDirection: "column",
                                              gap: "7px",
                                              transition:
                                                "border-color .15s, background .15s",
                                              "&:hover": {
                                                background: isActive
                                                  ? "#F6FEF9"
                                                  : "#FAFBFC",
                                              },
                                            }}
                                          >
                                            <Stack
                                              direction="row"
                                              alignItems="center"
                                              justifyContent="space-between"
                                              gap={1}
                                            >
                                              <Typography
                                                sx={{
                                                  fontSize: 16,
                                                  fontWeight: 800,
                                                  color: BM.ink,
                                                }}
                                              >
                                                {opt.transitTime ||
                                                  bmTransitText ||
                                                  "—"}
                                              </Typography>
                                              {isActive && (
                                                <BmIconBadge
                                                  bg={BM.green}
                                                  size={18}
                                                  radius={5}
                                                >
                                                  <Check size={12} color="#fff" />
                                                </BmIconBadge>
                                              )}
                                            </Stack>
                                            <Typography
                                              sx={{
                                                fontSize: 13,
                                                fontWeight: 600,
                                                color: BM.text,
                                              }}
                                            >
                                              {opt.fromLocation?.unLocCode} →{" "}
                                              {opt.toLocation?.unLocCode}
                                            </Typography>
                                            <Typography
                                              sx={{ fontSize: 12, color: BM.muted }}
                                            >
                                              {validDep ? fmtDay(depDate) : "—"} —{" "}
                                              {validArr ? fmtDay(arrDate) : "—"}
                                            </Typography>
                                            <Box
                                              component="span"
                                              sx={{
                                                fontSize: 11,
                                                fontWeight: 600,
                                                color:
                                                  hops > 1 ? BM.greenTx : BM.muted,
                                                background:
                                                  hops > 1 ? "#DCFAE6" : BM.hair2,
                                                px: "7px",
                                                py: "3px",
                                                borderRadius: "5px",
                                                width: "fit-content",
                                              }}
                                            >
                                              {hops > 1
                                                ? `${hops - 1} transshipment · ${
                                                    opt.scheduleDetails?.[0]
                                                      ?.toLocation?.unLocCode || ""
                                                  }`
                                                : "Direct service"}
                                            </Box>
                                          </Box>
                                        );
                                      })}
                                    </Box>

                                    <Box sx={{ px: "20px", py: "18px" }}>
                                      {events.length === 0 && (
                                        <Box
                                          sx={{
                                            minHeight: 160,
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            textAlign: "center",
                                          }}
                                        >
                                          <Typography sx={bmEmptyText}>
                                            Schedule details are not available for
                                            this sailing.
                                          </Typography>
                                        </Box>
                                      )}

                                      {events.map((ev, i) => {
                                        const d = ev.date ? new Date(ev.date) : null;
                                        const valid = d && !isNaN(d.getTime());
                                        const isLast = i === events.length - 1;
                                        const [kindBg, kindFg] =
                                          kindColors[ev.kind] || kindColors.Departure;
                                        const dot = isLast
                                          ? BM.green
                                          : i === 0
                                          ? AMBER
                                          : "#FFFFFF";
                                        const ring = isLast
                                          ? BM.green
                                          : i === 0
                                          ? AMBER
                                          : "#D0D5DD";
                                        return (
                                          <Box
                                            key={i}
                                            sx={{
                                              display: "grid",
                                              gridTemplateColumns: "22px minmax(0,1fr)",
                                              gap: "14px",
                                            }}
                                          >
                                            <Box
                                              sx={{
                                                display: "flex",
                                                flexDirection: "column",
                                                alignItems: "center",
                                              }}
                                            >
                                              <Box
                                                sx={{
                                                  width: 11,
                                                  height: 11,
                                                  borderRadius: "50%",
                                                  flexShrink: 0,
                                                  background: dot,
                                                  border: `2.5px solid ${ring}`,
                                                }}
                                              />
                                              {!isLast && (
                                                <Box
                                                  sx={{
                                                    flex: 1,
                                                    width: "2px",
                                                    background: BM.rule,
                                                    my: "4px",
                                                  }}
                                                />
                                              )}
                                            </Box>

                                            <Box
                                              sx={{
                                                pb: "22px",
                                                display: "flex",
                                                flexDirection: "column",
                                                gap: 1,
                                              }}
                                            >
                                              <Typography
                                                sx={{
                                                  fontSize: 12,
                                                  fontWeight: 600,
                                                  color: BM.muted,
                                                }}
                                              >
                                                {valid
                                                  ? `${d.toLocaleDateString("en-GB", {
                                                      day: "2-digit",
                                                      month: "short",
                                                      year: "numeric",
                                                    })} · ${d.toLocaleTimeString([], {
                                                      hour: "2-digit",
                                                      minute: "2-digit",
                                                    })} · ${d.toLocaleDateString(
                                                      "en-US",
                                                      { weekday: "short" }
                                                    )}`
                                                  : "—"}
                                              </Typography>
                                              <Stack
                                                direction="row"
                                                alignItems="center"
                                                gap={1}
                                                flexWrap="wrap"
                                              >
                                                <Typography
                                                  sx={{
                                                    fontSize: 16,
                                                    fontWeight: 800,
                                                    color: BM.ink,
                                                    letterSpacing: "-.01em",
                                                  }}
                                                >
                                                  {ev.port}
                                                </Typography>
                                                <Box
                                                  component="span"
                                                  sx={{
                                                    fontSize: 10.5,
                                                    fontWeight: 700,
                                                    letterSpacing: ".06em",
                                                    textTransform: "uppercase",
                                                    px: "7px",
                                                    py: "3px",
                                                    borderRadius: "5px",
                                                    background: kindBg,
                                                    color: kindFg,
                                                  }}
                                                >
                                                  {ev.kind}
                                                </Box>
                                              </Stack>

                                              {ev.showDetails && (
                                                <Box
                                                  sx={{
                                                    display: "grid",
                                                    gridTemplateColumns: {
                                                      xs: "1fr",
                                                      sm: "repeat(3, minmax(0,1fr))",
                                                    },
                                                    gap: 1.25,
                                                    background: "#F9FAFB",
                                                    border: `1px solid ${BM.hair}`,
                                                    borderRadius: "9px",
                                                    px: "12px",
                                                    py: "10px",
                                                    maxWidth: 520,
                                                  }}
                                                >
                                                  {[
                                                    ["Service", ev.service, false],
                                                    ["Vessel", ev.vessel, false],
                                                    ["Voyage ref.", ev.voyage, true],
                                                  ].map(([k, v, mono]) => (
                                                    <Box
                                                      key={k}
                                                      sx={{
                                                        display: "flex",
                                                        flexDirection: "column",
                                                        gap: "2px",
                                                      }}
                                                    >
                                                      <BmEyebrow size={10} ls=".07em">
                                                        {k}
                                                      </BmEyebrow>
                                                      <Typography
                                                        sx={{
                                                          fontSize: 12.5,
                                                          fontWeight: 600,
                                                          color: BM.text,
                                                          fontFamily: mono
                                                            ? BM_MONO
                                                            : "inherit",
                                                        }}
                                                      >
                                                        {v || "N/A"}
                                                      </Typography>
                                                    </Box>
                                                  ))}
                                                </Box>
                                              )}
                                            </Box>
                                          </Box>
                                        );
                                      })}
                                    </Box>
                                  </BmCard>
                                );
                              })()}

                            {bmActiveTab === "freedays" &&
                              (() => {
                                if (bmDndConditions.length === 0) {
                                  return (
                                    <BmCard sx={{ px: "20px", py: "18px" }}>
                                      <Typography sx={bmEmptyText}>
                                        Free days information is not available
                                        for this quote.
                                      </Typography>
                                    </BmCard>
                                  );
                                }
                                const sideConditions = bmDndConditions.filter(
                                  (c) => (c.direction || "Export") === freeDaysDirection
                                );
                                const containerSizes = [
                                  ...new Set(
                                    sideConditions.map((c) => c.containerSizeType)
                                  ),
                                ];
                                const categories = [
                                  "Detention",
                                  "Demurrage",
                                  "Storage",
                                ];
                                const findCondition = (size, category) =>
                                  sideConditions.find(
                                    (c) =>
                                      c.containerSizeType === size &&
                                      c.chargeType === category
                                  );

                                return (
                                  <BmCard
                                    sx={{
                                      px: "20px",
                                      py: "18px",
                                      display: "flex",
                                      flexDirection: "column",
                                      gap: 2,
                                    }}
                                  >
                                    <Stack
                                      direction="row"
                                      alignItems="center"
                                      justifyContent="space-between"
                                      gap={2}
                                      flexWrap="wrap"
                                    >
                                      <Box
                                        sx={{
                                          display: "flex",
                                          gap: "4px",
                                          p: "3px",
                                          background: BM.hair2,
                                          borderRadius: "9px",
                                        }}
                                      >
                                        <BmSeg
                                          size="sm"
                                          active={freeDaysDirection === "Export"}
                                          onClick={() => setFreeDaysDirection("Export")}
                                        >
                                          Origin
                                        </BmSeg>
                                        <BmSeg
                                          size="sm"
                                          active={freeDaysDirection === "Import"}
                                          onClick={() => setFreeDaysDirection("Import")}
                                        >
                                          Destination
                                        </BmSeg>
                                      </Box>
                                      <Typography sx={{ fontSize: 12, color: BM.faint }}>
                                        Free days start from container gate-out
                                      </Typography>
                                    </Stack>

                                    {containerSizes.length === 0 ? (
                                      <Typography sx={bmEmptyText}>
                                        No free days configured for{" "}
                                        {freeDaysDirection === "Export"
                                          ? "Origin"
                                          : "Destination"}
                                        .
                                      </Typography>
                                    ) : (
                                      containerSizes.map((size) => (
                                        <Box
                                          key={size}
                                          sx={{
                                            display: "grid",
                                            gridTemplateColumns: {
                                              xs: "1fr",
                                              sm: "repeat(3, minmax(0,1fr))",
                                            },
                                            gap: 1.5,
                                          }}
                                        >
                                          {categories.map((cat) => {
                                            const cond = findCondition(size, cat);
                                            const tier =
                                              cond?.chargePerDiemAfterFreetime?.[0];
                                            const days = cond
                                              ? tier?.to ??
                                                cond.freetimeGrantInDays ??
                                                "—"
                                              : "—";
                                            const from = cond ? tier?.from ?? 0 : null;
                                            const rate = tier?.ratePerDay;
                                            return (
                                              <Box
                                                key={cat}
                                                sx={{
                                                  border: `1px solid ${BM.line}`,
                                                  borderRadius: "12px",
                                                  overflow: "hidden",
                                                }}
                                              >
                                                <Stack
                                                  direction="row"
                                                  alignItems="center"
                                                  justifyContent="space-between"
                                                  sx={{
                                                    px: "14px",
                                                    py: "10px",
                                                    background: BM.head,
                                                    borderBottom: `1px solid ${BM.hair}`,
                                                  }}
                                                >
                                                  <Typography
                                                    sx={{
                                                      fontSize: 12.5,
                                                      fontWeight: 700,
                                                      color: BM.text,
                                                    }}
                                                  >
                                                    {cat}
                                                  </Typography>
                                                  <Typography
                                                    sx={{
                                                      fontSize: 11,
                                                      fontWeight: 600,
                                                      color: BM.muted,
                                                    }}
                                                  >
                                                    {size}
                                                  </Typography>
                                                </Stack>

                                                <Stack
                                                  direction="row"
                                                  alignItems="baseline"
                                                  gap={1}
                                                  sx={{ p: "14px" }}
                                                >
                                                  <Typography
                                                    sx={{
                                                      fontSize: 30,
                                                      fontWeight: 800,
                                                      color: BM.ink,
                                                      letterSpacing: "-.03em",
                                                      lineHeight: 1,
                                                    }}
                                                  >
                                                    {cond ? days : "—"}
                                                  </Typography>
                                                  <Typography
                                                    sx={{
                                                      fontSize: 12.5,
                                                      fontWeight: 600,
                                                      color: BM.muted,
                                                    }}
                                                  >
                                                    {cond
                                                      ? "free days"
                                                      : "not applicable"}
                                                  </Typography>
                                                </Stack>

                                                <Stack
                                                  direction="row"
                                                  gap={2.25}
                                                  sx={{ px: "14px", pb: "14px" }}
                                                >
                                                  <Box
                                                    sx={{
                                                      display: "flex",
                                                      flexDirection: "column",
                                                      gap: "2px",
                                                    }}
                                                  >
                                                    <BmEyebrow size={10} ls=".07em">
                                                      Window
                                                    </BmEyebrow>
                                                    <Typography
                                                      sx={{
                                                        fontSize: 12.5,
                                                        fontWeight: 600,
                                                        color: BM.text,
                                                      }}
                                                    >
                                                      {cond
                                                        ? `Day ${from} – ${days}`
                                                        : "—"}
                                                    </Typography>
                                                  </Box>
                                                  <Box
                                                    sx={{
                                                      display: "flex",
                                                      flexDirection: "column",
                                                      gap: "2px",
                                                    }}
                                                  >
                                                    <BmEyebrow size={10} ls=".07em">
                                                      Rate / day
                                                    </BmEyebrow>
                                                    <Typography
                                                      sx={{
                                                        fontSize: 12.5,
                                                        fontWeight: 600,
                                                        color: BM.text,
                                                      }}
                                                    >
                                                      {rate ?? "—"}
                                                    </Typography>
                                                  </Box>
                                                </Stack>
                                              </Box>
                                            );
                                          })}
                                        </Box>
                                      ))
                                    )}
                                  </BmCard>
                                );
                              })()}

                            {bmActiveTab === "remarks" &&
                              (() => {
                                if (!bmTerms && !bmInclusions) {
                                  return (
                                    <BmCard sx={{ px: "20px", py: "18px" }}>
                                      <Typography sx={bmEmptyText}>
                                        No remarks or inclusions specified for this
                                        quote.
                                      </Typography>
                                    </BmCard>
                                  );
                                }
                                const htmlSx = {
                                  fontSize: 13,
                                  color: BM.body,
                                  lineHeight: 1.55,
                                  "& ul": { listStyleType: "disc", pl: 2.5, my: 0.5 },
                                  "& li": { mb: 0.5 },
                                  "& strong": { fontWeight: 700, color: BM.text },
                                };
                                // The source's markup, parsed into groups this
                                // panel lays out itself; an unparseable blob
                                // falls back to rendering the markup as before.
                                const remarkGroups = bmParseRemarkGroups(bmTerms);
                                const inclusionGroups =
                                  bmParseRemarkGroups(bmInclusions);
                                const remarkHeading =
                                  remarkGroups.length === 1
                                    ? remarkGroups[0].heading
                                    : "";
                                // Equipment the conditional fees are quoted
                                // against — the container types this quote's
                                // own charge rows carry.
                                const feeContainerLabel = [
                                  ...new Set(
                                    chargeBlocks
                                      .flatMap((b) => b.rows.map((r) => r.container))
                                      .filter((c) => c && c !== "General")
                                  ),
                                ].join(" · ");
                                return (
                                  <Box
                                    sx={{
                                      display: "grid",
                                      gridTemplateColumns: {
                                        xs: "1fr",
                                        md:
                                          bmTerms && bmInclusions
                                            ? "minmax(0,1.15fr) minmax(0,1fr)"
                                            : "1fr",
                                      },
                                      gap: 1.75,
                                    }}
                                  >
                                    {bmTerms && (
                                      <BmCard
                                        sx={{
                                          px: "20px",
                                          py: "18px",
                                          display: "flex",
                                          flexDirection: "column",
                                          gap: 1.75,
                                        }}
                                      >
                                        <Stack
                                          direction="row"
                                          alignItems="center"
                                          gap={1.125}
                                        >
                                          <BmIconBadge
                                            bg={BM.amberBg}
                                            size={24}
                                            radius={7}
                                          >
                                            <Info size={14} color={BM.amber} />
                                          </BmIconBadge>
                                          <Typography
                                            sx={{
                                              fontSize: 14,
                                              fontWeight: 700,
                                              color: BM.ink,
                                            }}
                                          >
                                            Remarks
                                            {remarkHeading
                                              ? ` — ${remarkHeading}`
                                              : ""}
                                          </Typography>
                                        </Stack>
                                        {remarkGroups.length > 0 ? (
                                          <BmRemarkGroups
                                            groups={remarkGroups}
                                            containerLabel={feeContainerLabel}
                                          />
                                        ) : (
                                          <Box sx={htmlSx}>
                                            <BmRichText html={bmTerms} />
                                          </Box>
                                        )}
                                      </BmCard>
                                    )}

                                    {bmInclusions && (
                                      <BmCard
                                        sx={{
                                          px: "20px",
                                          py: "18px",
                                          display: "flex",
                                          flexDirection: "column",
                                          gap: 1.75,
                                        }}
                                      >
                                        <Stack
                                          direction="row"
                                          alignItems="center"
                                          gap={1.125}
                                        >
                                          <BmIconBadge
                                            bg={BM.greenBg}
                                            size={24}
                                            radius={7}
                                          >
                                            <Check size={14} color={BM.greenTx} />
                                          </BmIconBadge>
                                          <Typography
                                            sx={{
                                              fontSize: 14,
                                              fontWeight: 700,
                                              color: BM.ink,
                                            }}
                                          >
                                            Included in this rate
                                          </Typography>
                                        </Stack>
                                        {inclusionGroups.length > 0 ? (
                                          <BmInclusionGroups
                                            groups={inclusionGroups}
                                          />
                                        ) : (
                                          <Box sx={htmlSx}>
                                            <BmRichText html={bmInclusions} />
                                          </Box>
                                        )}
                                      </BmCard>
                                    )}
                                  </Box>
                                );
                              })()}

                            {bmActiveTab === "terms" &&
                              (() => {
                                // A carrier's terms document / clauses when the
                                // offer carries them ({ url, data }); a manual
                                // quote states its conditions in the remarks
                                // instead, which the card below still shows.
                                const tc = bmPrice.termsAndCondition || {};
                                const tcUrl = tc.url || "";
                                const tcData = tc.data;
                                const clauses = Array.isArray(tcData)
                                  ? tcData
                                      .map((c) =>
                                        typeof c === "string"
                                          ? c
                                          : c?.description || c?.text || c?.name || ""
                                      )
                                      .filter(Boolean)
                                  : [];
                                const tcText =
                                  typeof tcData === "string" ? tcData.trim() : "";
                                const hasContent =
                                  !!tcUrl || !!tcText || clauses.length > 0;

                                if (!hasContent) {
                                  return (
                                    <BmCard sx={{ px: "20px", py: "18px" }}>
                                      <Typography sx={bmEmptyText}>
                                        {bmTerms
                                          ? "This quote states its conditions under Remarks — see the card below, which you need to accept before confirming."
                                          : "No terms and conditions specified for this quote."}
                                      </Typography>
                                    </BmCard>
                                  );
                                }

                                return (
                                  <BmCard
                                    sx={{
                                      px: "20px",
                                      py: "18px",
                                      display: "flex",
                                      flexDirection: "column",
                                      gap: 1.75,
                                    }}
                                  >
                                    <Stack
                                      direction="row"
                                      alignItems="center"
                                      gap={1.125}
                                    >
                                      <BmIconBadge bg={BM.hair2} size={24} radius={7}>
                                        <Info size={14} color={BM.muted} />
                                      </BmIconBadge>
                                      <Typography
                                        sx={{
                                          fontSize: 14,
                                          fontWeight: 700,
                                          color: BM.ink,
                                        }}
                                      >
                                        Terms &amp; conditions
                                      </Typography>
                                    </Stack>

                                    {tcText && (
                                      <Box
                                        sx={{
                                          fontSize: 13,
                                          color: BM.body,
                                          lineHeight: 1.55,
                                          "& ul": {
                                            listStyleType: "disc",
                                            pl: 2.5,
                                            my: 0.5,
                                          },
                                          "& li": { mb: 0.5 },
                                          "& strong": {
                                            fontWeight: 700,
                                            color: BM.text,
                                          },
                                        }}
                                      >
                                        <BmRichText html={tcText} />
                                      </Box>
                                    )}

                                    {clauses.length > 0 && (
                                      <Box
                                        sx={{
                                          display: "flex",
                                          flexDirection: "column",
                                          gap: 1.5,
                                        }}
                                      >
                                        {clauses.map((clause, i) => (
                                          <Box
                                            key={i}
                                            sx={{
                                              display: "grid",
                                              gridTemplateColumns: "26px minmax(0,1fr)",
                                              gap: 1.5,
                                              alignItems: "start",
                                              pb: 1.5,
                                              borderBottom:
                                                i === clauses.length - 1
                                                  ? "none"
                                                  : `1px solid ${BM.hair2}`,
                                            }}
                                          >
                                            <Box
                                              sx={{
                                                width: 26,
                                                height: 26,
                                                borderRadius: "8px",
                                                background: BM.hair2,
                                                color: BM.body,
                                                fontSize: 12,
                                                fontWeight: 700,
                                                fontFamily: BM_MONO,
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                              }}
                                            >
                                              {String(i + 1).padStart(2, "0")}
                                            </Box>
                                            <Typography
                                              sx={{
                                                fontSize: 12.5,
                                                color: BM.muted,
                                                lineHeight: 1.55,
                                              }}
                                            >
                                              {clause}
                                            </Typography>
                                          </Box>
                                        ))}
                                      </Box>
                                    )}

                                    {tcUrl && (
                                      <Box
                                        component="a"
                                        href={tcUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        sx={{
                                          fontSize: 13,
                                          fontWeight: 600,
                                          color: BM.indigo,
                                          textDecoration: "none",
                                          width: "fit-content",
                                          "&:hover": { textDecoration: "underline" },
                                        }}
                                      >
                                        Open the carrier's terms document ↗
                                      </Box>
                                    )}
                                  </BmCard>
                                );
                              })()}
                          </Box>
                        )}

                        {/* Terms & conditions — the quote's own remarks, with the
                            agreement tick this page has always required before
                            Confirm becomes clickable. */}
                        {bmTerms && (
                          <BmCard
                            sx={{
                              px: "20px",
                              py: "18px",
                              display: "flex",
                              flexDirection: "column",
                              gap: 1.75,
                            }}
                          >
                            <Stack direction="row" alignItems="center" gap={1.125}>
                              <BmIconBadge bg={BM.amberBg} size={24} radius={7}>
                                <Info size={14} color={BM.amber} />
                              </BmIconBadge>
                              <Typography
                                sx={{ fontSize: 14, fontWeight: 700, color: BM.ink }}
                              >
                                Terms &amp; conditions
                              </Typography>
                            </Stack>

                            <Box
                              sx={{
                                fontSize: 13,
                                color: BM.body,
                                lineHeight: 1.55,
                                "& ul": { listStyleType: "disc", pl: 2.5, my: 0.5 },
                                "& li": { mb: 0.5 },
                                "& strong": { fontWeight: 700, color: BM.text },
                              }}
                            >
                              <BmRichText html={bmTerms} />
                            </Box>

                            <Box
                              component="label"
                              htmlFor="agreeTerms"
                              sx={{
                                display: "flex",
                                alignItems: "flex-start",
                                gap: 1.25,
                                cursor: "pointer",
                                background: agreeTerms ? "#F6FEF9" : BM.head,
                                border: `1px solid ${agreeTerms ? BM.greenBd : BM.line}`,
                                borderRadius: "10px",
                                px: "13px",
                                py: "11px",
                                transition: "background .15s, border-color .15s",
                              }}
                            >
                              <Box
                                component="input"
                                type="checkbox"
                                id="agreeTerms"
                                checked={agreeTerms}
                                onChange={(e) => setAgreeTerms(e.target.checked)}
                                sx={{
                                  width: 16,
                                  height: 16,
                                  mt: "2px",
                                  cursor: "pointer",
                                  accentColor: BM.green,
                                  flexShrink: 0,
                                }}
                              />
                              <Typography
                                sx={{ fontSize: 13, fontWeight: 600, color: BM.text }}
                              >
                                I have read and agree to the terms and conditions above
                              </Typography>
                            </Box>
                          </BmCard>
                        )}
                      </Box>
                    </Box>
                  </Box>

                  {/* footer */}
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    gap={2.5}
                    flexWrap="wrap"
                    sx={{
                      px: { xs: 2.5, md: 4 },
                      py: 2,
                      borderTop: `1px solid ${BM.hair}`,
                      background: "#FFFFFF",
                      flexShrink: 0,
                    }}
                  >
                    <Stack direction="row" alignItems="center" gap={1.25}>
                      <ShieldCheck size={17} color={BM.green} />
                      <Typography sx={{ fontSize: 12.5, color: BM.muted }}>
                        {bmTerms && !agreeTerms
                          ? "Accept the terms and conditions to confirm this booking"
                          : "Review the charges and shipment details before confirming"}
                      </Typography>
                    </Stack>

                    <Stack direction="row" alignItems="center" gap={1.25}>
                      <Button
                        onClick={closeBookingModal}
                        disableElevation
                        sx={{
                          px: 2.5,
                          py: "11px",
                          borderRadius: "10px",
                          border: "1px solid #D5DAE1",
                          background: "#FFFFFF",
                          color: BM.text,
                          fontSize: 14,
                          fontWeight: 600,
                          textTransform: "none",
                          "&:hover": {
                            background: "#F4F6F8",
                            border: "1px solid #D5DAE1",
                          },
                        }}
                      >
                        Cancel
                      </Button>
                      <Button
                        onClick={confirmBooking}
                        disabled={bmConfirmBlocked}
                        disableElevation
                        endIcon={!isBookingInFlight ? <ArrowRight size={16} /> : null}
                        sx={{
                          px: 3,
                          py: "11px",
                          borderRadius: "10px",
                          border: "none",
                          background: `linear-gradient(180deg,${AMBER} 0%,${AMBER_DARK} 100%)`,
                          color: "#FFFFFF",
                          fontSize: 14,
                          fontWeight: 700,
                          textTransform: "none",
                          boxShadow: "0 6px 16px -6px rgba(224,142,11,.7)",
                          "&:hover": {
                            background: `linear-gradient(180deg,${AMBER} 0%,${AMBER_DARK} 100%)`,
                            filter: "brightness(1.06)",
                          },
                          "&.Mui-disabled": {
                            background: "#E4E7EC",
                            color: BM.faint,
                            boxShadow: "none",
                          },
                        }}
                      >
                        {isBookingInFlight ? "Processing..." : "Confirm booking"}
                      </Button>
                    </Stack>
                  </Stack>
                </Box>
              </Box>
            );
          })()}

        <BookingSuccessModal
          visible={showBookingSuccess}
          redirectTo={oceanBookingSuccessRedirect}
          {...(bookingSuccessKind === "confirmation_awaited"
            ? {
                title: "Booking Created!",
                subtitle: "Payment received — MBL assignment is still pending",
                message:
                  "Booking saved as \"Confirmation Awaited\" — assign the MBL number from Queries to confirm it.",
              }
            : {})}
        />

        <VerifyEmailModal
          open={showVerifyModal}
          // Without verifying there are no rates to show here, so go back to
          // the Queries list (where this quotation lives), as air does.
          onClose={() => {
            pendingSearchRef.current = null;
            setShowVerifyModal(false);
            navigate("/queries?cargoTab=2");
          }}
          // Verified: run the search that was held back.
          onVerified={() => {
            setShowVerifyModal(false);
            const run = pendingSearchRef.current;
            pendingSearchRef.current = null;
            if (run) run();
          }}
          defaultEmail={gateUser?.email || gateUser?.unverified_email}
          userId={gateUser?.id}
        />
      </div>
    </div>
  );
};

export default OceanMainRateCom;
